import type { TranslationLanguage } from '@common/ts/types';

/**
 * Strings rendered to the respondent across the chat app — error pages,
 * ErrorBoundary fallback, send-message / video-recording toasts, and a
 * few miscellaneous chat UI toasts. Localised across all 65 canonical
 * TranslationLanguage codes.
 *
 * Consumers look up via `getRespondentUiTranslation(languageCode)`,
 * which falls back to English for an unknown/legacy language code.
 *
 * The `satisfies Record<TranslationLanguage, RespondentUiTranslation>`
 * constraint means adding a new canonical language or renaming a key
 * fails tsc until every block is updated — this is our compile-time
 * guard against drift (TELV1-238).
 */
export interface RespondentUiTranslation {
  // Error pages (ChatRedirectedErrorPage)
  telletDoesNotExistTitle: string;
  projectDoesNotExistTitle: string;
  projectDoesNotExistMessage: string;
  maximumParticipantsTitle: string;
  maximumParticipantsMessage: string;
  somethingMissingTitle: string;
  somethingMissingMessage: string;
  projectCompletedTitle: string;
  projectCompletedMessage: string;
  projectInDraftOrReadyStatusTitle: string;
  projectInDraftOrReadyStatusMessage: string;
  // Quota-full redirect countdown
  redirectingIn: string;
  seconds: string;
  cancel: string;
  returnToPanel: string;
  // ErrorBoundary fallback
  errorBoundaryTitle: string;
  errorBoundaryMessage: string;
  errorBoundaryReloadButton: string;
  // useSendMessage hook toasts
  messageProcessingError: string;
  microphoneProblemTextChat: string;
  microphoneProblemVoiceChat: string;
  responseTimeoutError: string;
  noResponseError: string;
  connectionInterrupted: string;
  // useVideoRecording hook toasts
  videoStartError: string;
  videoCameraNotSupported: string;
  videoPauseError: string;
  videoResumeError: string;
  videoStopError: string;
  videoSwitchCameraError: string;
  videoSendError: string;
  // Miscellaneous chat toasts
  welcomeBack: string;
  unsupportedFileType: string;
  fileTooLarge: string;
  audioCloseError: string;
}

export const RESPONDENT_UI_TRANSLATION = {
  af: {
    telletDoesNotExistTitle: 'Hierdie skakel is nie beskikbaar nie',
    projectDoesNotExistTitle: 'Hierdie skakel is nie beskikbaar nie',
    projectDoesNotExistMessage:
      'Ons is jammer, maar ons kon nie hierdie studie vind nie. Kontroleer asseblief die skakel wat u ontvang het, of kontak die persoon wat u uitgenooi het.',
    maximumParticipantsTitle: 'Dankie',
    maximumParticipantsMessage:
      'Dankie vir u belangstelling om aan hierdie navorsing deel te neem.\nOns het nou die vereiste aantal antwoorde ontvang en kan ongelukkig nie meer nuwe deelnemers aanvaar nie.\nOns waardeer dat u die tyd geneem het om hierdie studie te besoek en u bereidwilligheid om tot hierdie belangrike projek by te dra.\nDankie vir u begrip.',
    somethingMissingTitle: 'Hierdie skakel is nie beskikbaar nie',
    somethingMissingMessage:
      'Ons is jammer, maar ons kon nie hierdie studie vind nie. Kontroleer asseblief die skakel wat u ontvang het, of kontak die persoon wat u uitgenooi het.',
    projectCompletedTitle: 'Dankie',
    projectCompletedMessage:
      'Dankie vir u belangstelling om aan hierdie navorsing deel te neem.\nHierdie studie is nou gesluit en ons kan ongelukkig nie meer nuwe deelnemers aanvaar nie.\nOns waardeer u bereidwilligheid om by te dra, en dankie vir u begrip.',
    projectInDraftOrReadyStatusTitle: 'Nog nie oop nie',
    projectInDraftOrReadyStatusMessage:
      'Dankie vir u belangstelling om aan hierdie navorsing deel te neem.\nHierdie studie is nog nie oop vir deelname nie. Probeer asseblief later weer, of kontak die persoon wat u uitgenooi het.',
    redirectingIn: 'Herlei oor',
    seconds: 'sekondes...',
    cancel: 'Kanselleer',
    returnToPanel: 'Keer terug na paneel',
    errorBoundaryTitle: 'Iets het verkeerd geloop',
    errorBoundaryMessage:
      "'n Onverwagte fout het voorgekom. Herlaai asseblief die bladsy om voort te gaan.",
    errorBoundaryReloadButton: 'Herlaai bladsy',
    messageProcessingError:
      'Oeps! Dit lyk of ons probleme het om jou boodskap te verwerk. Probeer dit asseblief weer stuur.',
    microphoneProblemTextChat:
      "Dit lyk of daar 'n probleem met jou mikrofoon is. Tik asseblief jou antwoorde met die sleutelbordknoppie in.",
    microphoneProblemVoiceChat:
      "Dit lyk of daar 'n probleem met jou mikrofoon is. Gaan asseblief jou blaaier-toestemmings na en probeer weer.",
    responseTimeoutError: 'Reaksie neem te lank. Probeer asseblief weer.',
    noResponseError: 'Geen reaksie opgespoor nie. Probeer asseblief weer.',
    connectionInterrupted: 'Verbinding onderbreek. Jy kan jou boodskap weer stuur.',
    videoStartError: 'Kon nie video-opname begin nie. Probeer asseblief weer.',
    videoCameraNotSupported:
      "Kamera word nie ondersteun nie. Probeer asseblief met 'n ander blaaier.",
    videoPauseError: 'Kon nie video-opname onderbreek nie.',
    videoResumeError: 'Kon nie video-opname hervat nie.',
    videoStopError: 'Kon nie video-opname stop nie. Probeer asseblief weer.',
    videoSwitchCameraError: 'Kon nie kamera wissel nie. Probeer asseblief weer.',
    videoSendError: 'Kon nie video-boodskap stuur nie.',
    welcomeBack: 'Welkom terug!',
    unsupportedFileType:
      "Lêertipe word nie ondersteun nie. Laai asseblief 'n JPG-, PNG-, GIF- of MP4-lêer op.",
    fileTooLarge: 'Hierdie lêer is te groot. Die maksimum grootte is 200 MB.',
    audioCloseError: 'Fout met sluit van oudio:',
  },
  am: {
    telletDoesNotExistTitle: 'ይህ አገናኝ አይገኝም',
    projectDoesNotExistTitle: 'ይህ አገናኝ አይገኝም',
    projectDoesNotExistMessage:
      'ይቅርታ፣ ይህን ጥናት ማግኘት አልቻልንም። የተሰጠዎትን አገናኝ እባክዎ ያረጋግጡ፣ ወይም የጠሩዎትን ሰው ያነጋግሩ።',
    maximumParticipantsTitle: 'እናመሰግናለን',
    maximumParticipantsMessage:
      'በዚህ ጥናት ላይ ለመሳተፍ ያሳያችሁትን ፍላጎት እናመሰግናለን።\nአሁን የአስፈላጊውን የምላሾች ብዛት ተቀብለናል፣ ስለዚህ በዚህ ጊዜ ተጨማሪ ተሳታፊዎችን ማቀበል አንችልም።\nይህን ጥናት ለመጎብኘት የወሰዳችሁትን ጊዜ እና ለዚህ አስፈላጊ ፕሮጀክት ለመዋጮ ያሳያችሁትን ፍቅር እናደናግራለን።\nለማስተዋልዎ እናመሰግናለን።',
    somethingMissingTitle: 'ይህ አገናኝ አይገኝም',
    somethingMissingMessage:
      'ይቅርታ፣ ይህን ጥናት ማግኘት አልቻልንም። የተሰጠዎትን አገናኝ እባክዎ ያረጋግጡ፣ ወይም የጠሩዎትን ሰው ያነጋግሩ።',
    projectCompletedTitle: 'እናመሰግናለን',
    projectCompletedMessage:
      'በዚህ ጥናት ላይ ለመሳተፍ ያሳያችሁትን ፍላጎት እናመሰግናለን።\nይህ ጥናት ተዘግቷል፣ ስለዚህ በዚህ ጊዜ ተጨማሪ ተሳታፊዎችን ማቀበል አንችልም።\nለመዋጮዎ ፍቅር እናመሰግናለን፣ ለማስተዋልዎም እናመሰግናለን።',
    projectInDraftOrReadyStatusTitle: 'አሁን አልተከፈተም',
    projectInDraftOrReadyStatusMessage:
      'በዚህ ጥናት ላይ ለመሳተፍ ያሳያችሁትን ፍላጎት እናመሰግናለን።\nይህ ጥናት ለመሳተፍ አሁን አልተከፈተም። እባክዎ በኋላ ደግመው ይሞክሩ፣ ወይም የጠሩዎትን ሰው ያነጋግሩ።',
    redirectingIn: 'እንደገና የሚዘዋወረው በ',
    seconds: 'ሰከንዶች...',
    cancel: 'ሰርዝ',
    returnToPanel: 'ወደ ፓነል ተመለስ',
    errorBoundaryTitle: 'አንድ ስህተት ተፈጠረ',
    errorBoundaryMessage: 'ያልተጠበቀ ስህተት ተፈጥሯል። እባክዎ ለመቀጠል ገጹን ደግመው ይጫኑ።',
    errorBoundaryReloadButton: 'ገጹን እንደገና ጫን',
    messageProcessingError: 'ይቅርታ! መልእክትዎን ለማስኬድ ችግር እያጋጠመን ይመስላል። እባክዎ እንደገና ለመላክ ይሞክሩ።',
    microphoneProblemTextChat: 'ከማይክሮፎንዎ ጋር ችግር ያለ ይመስላል። እባክዎ መልሶችዎን የቁልፍ ሰሌዳውን ቁልፍ በመጠቀም ይተይቡ።',
    microphoneProblemVoiceChat: 'ከማይክሮፎንዎ ጋር ችግር ያለ ይመስላል። እባክዎ የአሳሽዎን ፍቃዶች ያረጋግጡ እና እንደገና ይሞክሩ።',
    responseTimeoutError: 'ምላሹ በጣም ረጅም ጊዜ ወስዷል። እባክዎ እንደገና ይሞክሩ።',
    noResponseError: 'ምንም ምላሽ አልተገኘም። እባክዎ እንደገና ይሞክሩ።',
    connectionInterrupted: 'ግንኙነቱ ተቋርጧል። መልእክትዎን እንደገና መላክ ይችላሉ።',
    videoStartError: 'የቪዲዮ ቀረጻ መጀመር አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    videoCameraNotSupported: 'ካሜራ አይደገፍም። እባክዎ በተለየ አሳሽ ይሞክሩ።',
    videoPauseError: 'የቪዲዮ ቀረጻን ማቆም አልተሳካም።',
    videoResumeError: 'የቪዲዮ ቀረጻን መቀጠል አልተሳካም።',
    videoStopError: 'የቪዲዮ ቀረጻን ማቆም አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    videoSwitchCameraError: 'ካሜራ መቀየር አልተሳካም። እባክዎ እንደገና ይሞክሩ።',
    videoSendError: 'የቪዲዮ መልእክት መላክ አልተሳካም።',
    welcomeBack: 'እንኳን ደህና መጡ!',
    unsupportedFileType: 'የማይደገፍ የፋይል ዓይነት። እባክዎ JPG፣ PNG፣ GIF ወይም MP4 ፋይል ይጫኑ።',
    fileTooLarge: 'ይህ ፋይል በጣም ትልቅ ነው። ከፍተኛው መጠን 200 MB ነው።',
    audioCloseError: 'ድምፅን በመዝጋት ላይ ስህተት:',
  },
  ar: {
    telletDoesNotExistTitle: 'هذا الرابط غير متاح',
    projectDoesNotExistTitle: 'هذا الرابط غير متاح',
    projectDoesNotExistMessage:
      'نعتذر، لم نتمكن من العثور على هذه الدراسة. يرجى التحقق من الرابط الذي تم تزويدكم به أو التواصل مع الشخص الذي دعاكم.',
    maximumParticipantsTitle: 'شكرًا لكم',
    maximumParticipantsMessage:
      'شكرًا لاهتمامكم بالمشاركة في هذا البحث.\nلقد وصلنا الآن إلى العدد المطلوب من الردود، ولذلك نعتذر عن عدم قدرتنا على قبول مشاركين جدد في الوقت الحالي.\nنقدّر تخصيصكم الوقت لزيارة هذه الدراسة ورغبتكم في المساهمة في هذا المشروع الهام.\nشكرًا لتفهمكم.',
    somethingMissingTitle: 'هذا الرابط غير متاح',
    somethingMissingMessage:
      'نعتذر، لم نتمكن من العثور على هذه الدراسة. يرجى التحقق من الرابط الذي تم تزويدكم به أو التواصل مع الشخص الذي دعاكم.',
    projectCompletedTitle: 'شكرًا لكم',
    projectCompletedMessage:
      'شكرًا لاهتمامكم بالمشاركة في هذا البحث.\nلقد أُغلِقت هذه الدراسة الآن، ولذلك نعتذر عن عدم قدرتنا على قبول مشاركين جدد.\nنقدّر رغبتكم في المساهمة، ونشكركم على تفهمكم.',
    projectInDraftOrReadyStatusTitle: 'لم يبدأ بعد',
    projectInDraftOrReadyStatusMessage:
      'شكرًا لاهتمامكم بالمشاركة في هذا البحث.\nهذه الدراسة ليست متاحة للمشاركة بعد. يرجى المحاولة لاحقًا أو التواصل مع الشخص الذي دعاكم.',
    redirectingIn: 'إعادة التوجيه خلال',
    seconds: 'ثوانٍ...',
    cancel: 'إلغاء',
    returnToPanel: 'العودة إلى اللوحة',
    errorBoundaryTitle: 'حدث خطأ ما',
    errorBoundaryMessage: 'حدث خطأ غير متوقع. يرجى إعادة تحميل الصفحة للمتابعة.',
    errorBoundaryReloadButton: 'إعادة تحميل الصفحة',
    messageProcessingError:
      'عذرًا! يبدو أننا نواجه مشكلة في معالجة رسالتك. يرجى محاولة إعادة إرسالها.',
    microphoneProblemTextChat:
      'يبدو أن هناك مشكلة في الميكروفون. يرجى كتابة إجاباتك باستخدام زر لوحة المفاتيح.',
    microphoneProblemVoiceChat:
      'يبدو أن هناك مشكلة في الميكروفون. يرجى التحقق من أذونات المتصفح والمحاولة مرة أخرى.',
    responseTimeoutError: 'الاستجابة تستغرق وقتًا طويلاً. يرجى المحاولة مرة أخرى.',
    noResponseError: 'لم يتم اكتشاف أي استجابة. يرجى المحاولة مرة أخرى.',
    connectionInterrupted: 'انقطع الاتصال. يمكنك إعادة إرسال رسالتك.',
    videoStartError: 'فشل بدء تسجيل الفيديو. يرجى المحاولة مرة أخرى.',
    videoCameraNotSupported: 'الكاميرا غير مدعومة. يرجى المحاولة بمتصفح آخر.',
    videoPauseError: 'فشل إيقاف تسجيل الفيديو مؤقتًا.',
    videoResumeError: 'فشل استئناف تسجيل الفيديو.',
    videoStopError: 'فشل إيقاف تسجيل الفيديو. يرجى المحاولة مرة أخرى.',
    videoSwitchCameraError: 'فشل تبديل الكاميرا. يرجى المحاولة مرة أخرى.',
    videoSendError: 'فشل إرسال رسالة الفيديو.',
    welcomeBack: 'مرحبًا بعودتك!',
    unsupportedFileType: 'نوع الملف غير مدعوم. يرجى تحميل ملف JPG أو PNG أو GIF أو MP4.',
    fileTooLarge: 'هذا الملف كبير جدًا. الحد الأقصى للحجم هو 200 ميغابايت.',
    audioCloseError: 'خطأ في إغلاق الصوت:',
  },
  hy: {
    telletDoesNotExistTitle: 'Այս հղումը հասանելի չէ',
    projectDoesNotExistTitle: 'Այս հղումը հասանելի չէ',
    projectDoesNotExistMessage:
      'Ներողություն ենք խնդրում, սակայն չենք կարողանում գտնել այս ուսումնասիրությունը։ Խնդրում ենք ստուգել ձեզ տրամադրված հղումը կամ կապ հաստատել ձեզ հրավիրած անձի հետ։',
    maximumParticipantsTitle: 'Շնորհակալություն',
    maximumParticipantsMessage:
      'Շնորհակալություն այս ուսումնասիրությանը մասնակցելու ձեր հետաքրքրության համար։\nՄենք արդեն ստացել ենք անհրաժեշտ պատասխանների քանակը, ուստի այս պահին, ցավոք, չենք կարող ընդունել նոր մասնակիցների։\nՄենք գնահատում ենք, որ ժամանակ եք հատկացրել այցելելու այս ուսումնասիրությանը և պատրաստակամ եք եղել մասնակցել այս կարևոր նախագծին։\nՇնորհակալություն ըմբռնման համար։',
    somethingMissingTitle: 'Այս հղումը հասանելի չէ',
    somethingMissingMessage:
      'Ներողություն ենք խնդրում, սակայն չենք կարողանում գտնել այս ուսումնասիրությունը։ Խնդրում ենք ստուգել ձեզ տրամադրված հղումը կամ կապ հաստատել ձեզ հրավիրած անձի հետ։',
    projectCompletedTitle: 'Շնորհակալություն',
    projectCompletedMessage:
      'Շնորհակալություն այս ուսումնասիրությանը մասնակցելու ձեր հետաքրքրության համար։\nԱյս ուսումնասիրությունն արդեն փակ է, ուստի, ցավոք, չենք կարող ընդունել նոր մասնակիցների։\nՄենք գնահատում ենք ձեր պատրաստակամությունը մասնակցելու, և շնորհակալություն ենք հայտնում ըմբռնման համար։',
    projectInDraftOrReadyStatusTitle: 'Դեռ բաց չէ',
    projectInDraftOrReadyStatusMessage:
      'Շնորհակալություն այս ուսումնասիրությանը մասնակցելու ձեր հետաքրքրության համար։\nԱյս ուսումնասիրությունը դեռ բաց չէ մասնակցության համար։ Խնդրում ենք փորձել ավելի ուշ կամ կապ հաստատել ձեզ հրավիրած անձի հետ։',
    redirectingIn: 'Վերահղում',
    seconds: 'վայրկյանից...',
    cancel: 'Չեղարկել',
    returnToPanel: 'Վերադառնալ վահանակ',
    errorBoundaryTitle: 'Ինչ-որ բան սխալ ընթացավ',
    errorBoundaryMessage:
      'Անսպասելի սխալ տեղի ունեցավ։ Շարունակելու համար խնդրում ենք վերաբեռնել էջը։',
    errorBoundaryReloadButton: 'Վերաբեռնել էջը',
    messageProcessingError:
      'Ուփս։ Կարծես թե մենք խնդիրներ ունենք ձեր հաղորդագրությունը մշակելու հետ։ Խնդրում ենք փորձել այն կրկին ուղարկել։',
    microphoneProblemTextChat:
      'Կարծես ձեր խոսափողի հետ խնդիր կա։ Խնդրում ենք մուտքագրել ձեր պատասխանները՝ օգտագործելով ստեղնաշարի կոճակը։',
    microphoneProblemVoiceChat:
      'Կարծես ձեր խոսափողի հետ խնդիր կա։ Խնդրում ենք ստուգել ձեր բրաուզերի թույլտվությունները և փորձել կրկին։',
    responseTimeoutError: 'Պատասխանը չափազանց երկար է տևում։ Խնդրում ենք փորձել կրկին։',
    noResponseError: 'Պատասխան չի հայտնաբերվել։ Խնդրում ենք փորձել կրկին։',
    connectionInterrupted: 'Կապն ընդհատվել է։ Դուք կարող եք կրկին ուղարկել ձեր հաղորդագրությունը։',
    videoStartError: 'Չհաջողվեց սկսել տեսանյութի ձայնագրումը։ Խնդրում ենք փորձել կրկին։',
    videoCameraNotSupported: 'Տեսախցիկն աջակցվում չէ։ Խնդրում ենք փորձել այլ բրաուզերով։',
    videoPauseError: 'Չհաջողվեց դադարեցնել տեսանյութի ձայնագրումը։',
    videoResumeError: 'Չհաջողվեց վերսկսել տեսանյութի ձայնագրումը։',
    videoStopError: 'Չհաջողվեց դադարեցնել տեսանյութի ձայնագրումը։ Խնդրում ենք փորձել կրկին։',
    videoSwitchCameraError: 'Չհաջողվեց փոխարկել տեսախցիկը։ Խնդրում ենք փորձել կրկին։',
    videoSendError: 'Չհաջողվեց ուղարկել տեսահաղորդագրությունը։',
    welcomeBack: 'Բարի վերադարձ։',
    unsupportedFileType:
      'Ֆայլի տեսակն աջակցվում չէ։ Խնդրում ենք վերբեռնել JPG, PNG, GIF կամ MP4 ֆայլ։',
    fileTooLarge: 'Այս ֆայլը չափազանց մեծ է։ Առավելագույն չափը 200 ՄԲ է։',
    audioCloseError: 'Աուդիոն փակելու սխալ՝',
  },
  az: {
    telletDoesNotExistTitle: 'Bu keçid mövcud deyil',
    projectDoesNotExistTitle: 'Bu keçid mövcud deyil',
    projectDoesNotExistMessage:
      'Bağışlayın, bu araşdırmanı tapa bilmədik. Zəhmət olmasa, sizə göndərilən keçidi yoxlayın və ya sizi dəvət edən şəxslə əlaqə saxlayın.',
    maximumParticipantsTitle: 'Təşəkkür edirik',
    maximumParticipantsMessage:
      'Bu araşdırmada iştirak etmək istəyinizə görə təşəkkür edirik.\nLazımi sayda cavab artıq alınıb, buna görə də təəssüf ki, hazırda yeni iştirakçı qəbul edə bilmirik.\nBu layihəyə vaxt ayırdığınız və töhfə verməyə hazır olduğunuz üçün minnətdarıq.\nAnlayışınıza görə təşəkkür edirik.',
    somethingMissingTitle: 'Bu keçid mövcud deyil',
    somethingMissingMessage:
      'Bağışlayın, bu araşdırmanı tapa bilmədik. Zəhmət olmasa, sizə göndərilən keçidi yoxlayın və ya sizi dəvət edən şəxslə əlaqə saxlayın.',
    projectCompletedTitle: 'Təşəkkür edirik',
    projectCompletedMessage:
      'Bu araşdırmada iştirak etmək istəyinizə görə təşəkkür edirik.\nBu araşdırma artıq başa çatıb, buna görə də təəssüf ki, yeni iştirakçı qəbul edilmir.\nTöhfənizi verməyə hazır olduğunuz üçün minnətdarıq və anlayışınıza görə təşəkkür edirik.',
    projectInDraftOrReadyStatusTitle: 'Hələ açıq deyil',
    projectInDraftOrReadyStatusMessage:
      'Bu araşdırmada iştirak etmək istəyinizə görə təşəkkür edirik.\nBu araşdırma hələ iştirak üçün açıq deyil. Zəhmət olmasa, bir qədər sonra yenidən yoxlayın və ya sizi dəvət edən şəxslə əlaqə saxlayın.',
    redirectingIn: 'Yönləndirilir',
    seconds: 'saniyə...',
    cancel: 'Ləğv et',
    returnToPanel: 'Panelə qayıt',
    errorBoundaryTitle: 'Nəsə səhv getdi',
    errorBoundaryMessage:
      'Gözlənilməz xəta baş verdi. Davam etmək üçün zəhmət olmasa səhifəni yenidən yükləyin.',
    errorBoundaryReloadButton: 'Səhifəni yenidən yüklə',
    messageProcessingError:
      'Oops! Görünür, mesajınızı emal etməkdə problem yaşayırıq. Zəhmət olmasa yenidən göndərməyə cəhd edin.',
    microphoneProblemTextChat:
      'Mikrofonunuzda problem var kimi görünür. Zəhmət olmasa cavablarınızı klaviatura düyməsindən istifadə edərək yazın.',
    microphoneProblemVoiceChat:
      'Mikrofonunuzda problem var kimi görünür. Zəhmət olmasa brauzer icazələrinizi yoxlayın və yenidən cəhd edin.',
    responseTimeoutError: 'Cavab çox uzun çəkir. Zəhmət olmasa yenidən cəhd edin.',
    noResponseError: 'Cavab aşkar edilmədi. Zəhmət olmasa yenidən cəhd edin.',
    connectionInterrupted: 'Bağlantı kəsildi. Mesajınızı yenidən göndərə bilərsiniz.',
    videoStartError: 'Video yazılışını başlatmaq alınmadı. Zəhmət olmasa yenidən cəhd edin.',
    videoCameraNotSupported: 'Kamera dəstəklənmir. Zəhmət olmasa fərqli brauzerlə cəhd edin.',
    videoPauseError: 'Video yazılışını dayandırmaq alınmadı.',
    videoResumeError: 'Video yazılışını davam etdirmək alınmadı.',
    videoStopError: 'Video yazılışını dayandırmaq alınmadı. Zəhmət olmasa yenidən cəhd edin.',
    videoSwitchCameraError: 'Kameranı dəyişdirmək alınmadı. Zəhmət olmasa yenidən cəhd edin.',
    videoSendError: 'Video mesaj göndərmək alınmadı.',
    welcomeBack: 'Xoş gəlmisiniz!',
    unsupportedFileType:
      'Dəstəklənməyən fayl növü. Zəhmət olmasa JPG, PNG, GIF və ya MP4 faylı yükləyin.',
    fileTooLarge: 'Bu fayl çox böyükdür. Maksimum ölçü 200 MB-dır.',
    audioCloseError: 'Audio bağlanarkən xəta:',
  },
  be: {
    telletDoesNotExistTitle: 'Гэтая спасылка недаступная',
    projectDoesNotExistTitle: 'Гэтая спасылка недаступная',
    projectDoesNotExistMessage:
      'Прабачце, але нам не ўдалося знайсці гэта даследаванне. Калі ласка, праверце дасланую вам спасылку або звяжыцеся з асобай, якая вас запрасіла.',
    maximumParticipantsTitle: 'Дзякуй',
    maximumParticipantsMessage:
      'Дзякуй за вашую зацікаўленасць у ўдзеле ў гэтым даследаванні.\nМы ўжо атрымалі неабходную колькасць адказаў, таму, на жаль, не можам прыняць новых удзельнікаў у дадзены момант.\nМы цэнім, што вы знайшлі час наведаць гэта даследаванне і выказалі жаданне ўнесці свой уклад у гэты важны праект.\nДзякуй за разуменне.',
    somethingMissingTitle: 'Гэтая спасылка недаступная',
    somethingMissingMessage:
      'Прабачце, але нам не ўдалося знайсці гэта даследаванне. Калі ласка, праверце дасланую вам спасылку або звяжыцеся з асобай, якая вас запрасіла.',
    projectCompletedTitle: 'Дзякуй',
    projectCompletedMessage:
      'Дзякуй за вашую зацікаўленасць у ўдзеле ў гэтым даследаванні.\nГэта даследаванне ўжо завершана, таму, на жаль, мы не можам прыняць новых удзельнікаў.\nМы цэнім вашу гатоўнасць дапамагчы і дзякуем за разуменне.',
    projectInDraftOrReadyStatusTitle: 'Пакуль не адкрыта',
    projectInDraftOrReadyStatusMessage:
      'Дзякуй за вашую зацікаўленасць у ўдзеле ў гэтым даследаванні.\nГэта даследаванне пакуль не адкрыта для ўдзелу. Калі ласка, паспрабуйце пазней або звяжыцеся з асобай, якая вас запрасіла.',
    redirectingIn: 'Перанакіраванне праз',
    seconds: 'секунд...',
    cancel: 'Адмяніць',
    returnToPanel: 'Вярнуцца на панэль',
    errorBoundaryTitle: 'Нешта пайшло не так',
    errorBoundaryMessage:
      'Адбылася нечаканая памылка. Калі ласка, перазагрузіце старонку, каб працягнуць.',
    errorBoundaryReloadButton: 'Перазагрузіць старонку',
    messageProcessingError:
      'Ой! Падобна, у нас праблемы з апрацоўкай вашага паведамлення. Калі ласка, паспрабуйце адправіць яго зноў.',
    microphoneProblemTextChat:
      'Здаецца, з вашым мікрафонам ёсць праблема. Калі ласка, уводзьце свае адказы з дапамогай клавіятуры.',
    microphoneProblemVoiceChat:
      'Здаецца, з вашым мікрафонам ёсць праблема. Калі ласка, праверце дазволы браўзера і паспрабуйце зноў.',
    responseTimeoutError: 'Адказ займае занадта шмат часу. Калі ласка, паспрабуйце зноў.',
    noResponseError: 'Адказ не выяўлены. Калі ласка, паспрабуйце зноў.',
    connectionInterrupted: 'Злучэнне перарвана. Вы можаце паўторна адправіць сваё паведамленне.',
    videoStartError: 'Не ўдалося пачаць запіс відэа. Калі ласка, паспрабуйце зноў.',
    videoCameraNotSupported:
      'Камера не падтрымліваецца. Калі ласка, паспрабуйце з іншым браўзерам.',
    videoPauseError: 'Не ўдалося прыпыніць запіс відэа.',
    videoResumeError: 'Не ўдалося аднавіць запіс відэа.',
    videoStopError: 'Не ўдалося спыніць запіс відэа. Калі ласка, паспрабуйце зноў.',
    videoSwitchCameraError: 'Не ўдалося пераключыць камеру. Калі ласка, паспрабуйце зноў.',
    videoSendError: 'Не ўдалося адправіць відэапаведамленне.',
    welcomeBack: 'З вяртаннем!',
    unsupportedFileType:
      'Непадтрымоўваемы тып файла. Калі ласка, загрузіце файл JPG, PNG, GIF або MP4.',
    fileTooLarge: 'Гэты файл занадта вялікі. Максімальны памер — 200 МБ.',
    audioCloseError: 'Памылка закрыцця аўдыя:',
  },
  bn: {
    telletDoesNotExistTitle: 'এই লিঙ্কটি উপলব্ধ নয়',
    projectDoesNotExistTitle: 'এই লিঙ্কটি উপলব্ধ নয়',
    projectDoesNotExistMessage:
      'দুঃখিত, আমরা এই গবেষণাটি খুঁজে পাইনি। অনুগ্রহ করে আপনাকে দেওয়া লিঙ্কটি পরীক্ষা করুন অথবা যিনি আপনাকে আমন্ত্রণ জানিয়েছেন, তার সঙ্গে যোগাযোগ করুন।',
    maximumParticipantsTitle: 'ধন্যবাদ',
    maximumParticipantsMessage:
      'এই গবেষণায় অংশগ্রহণে আগ্রহ প্রকাশ করার জন্য আপনাকে ধন্যবাদ।\nআমরা ইতিমধ্যে প্রয়োজনীয় সংখ্যক প্রতিক্রিয়া পেয়েছি, তাই দুঃখিত, এই মুহূর্তে আর নতুন অংশগ্রহণকারী গ্রহণ করা সম্ভব নয়।\nআপনি সময় নিয়ে এই গবেষণা পরিদর্শন করেছেন এবং গুরুত্বপূর্ণ এই প্রকল্পে অবদান রাখার ইচ্ছা প্রকাশ করেছেন, তার জন্য আমরা কৃতজ্ঞ।\nআপনার সহযোগিতার জন্য ধন্যবাদ।',
    somethingMissingTitle: 'এই লিঙ্কটি উপলব্ধ নয়',
    somethingMissingMessage:
      'দুঃখিত, আমরা এই গবেষণাটি খুঁজে পাইনি। অনুগ্রহ করে আপনাকে দেওয়া লিঙ্কটি পরীক্ষা করুন অথবা যিনি আপনাকে আমন্ত্রণ জানিয়েছেন, তার সঙ্গে যোগাযোগ করুন।',
    projectCompletedTitle: 'ধন্যবাদ',
    projectCompletedMessage:
      'এই গবেষণায় অংশগ্রহণে আগ্রহ প্রকাশ করার জন্য আপনাকে ধন্যবাদ।\nএই গবেষণাটি এখন বন্ধ হয়ে গেছে, তাই দুঃখিত, আমরা আর নতুন অংশগ্রহণকারী গ্রহণ করতে পারছি না।\nআপনার অবদান রাখার ইচ্ছার জন্য আমরা কৃতজ্ঞ, এবং আপনার সহযোগিতার জন্য ধন্যবাদ।',
    projectInDraftOrReadyStatusTitle: 'এখনও খোলা হয়নি',
    projectInDraftOrReadyStatusMessage:
      'এই গবেষণায় অংশগ্রহণে আগ্রহ প্রকাশ করার জন্য আপনাকে ধন্যবাদ।\nএই গবেষণাটি এখনও অংশগ্রহণের জন্য খোলা হয়নি। অনুগ্রহ করে পরে আবার চেষ্টা করুন অথবা যিনি আপনাকে আমন্ত্রণ জানিয়েছেন, তার সঙ্গে যোগাযোগ করুন।',
    redirectingIn: 'পুনঃনির্দেশিত হচ্ছে',
    seconds: 'সেকেন্ডে...',
    cancel: 'বাতিল করুন',
    returnToPanel: 'প্যানেলে ফিরে যান',
    errorBoundaryTitle: 'কিছু ভুল হয়েছে',
    errorBoundaryMessage:
      'একটি অপ্রত্যাশিত ত্রুটি ঘটেছে। চালিয়ে যেতে অনুগ্রহ করে পৃষ্ঠাটি পুনরায় লোড করুন।',
    errorBoundaryReloadButton: 'পৃষ্ঠা পুনরায় লোড করুন',
    messageProcessingError:
      'ওহো! মনে হচ্ছে আপনার বার্তা প্রক্রিয়া করতে আমাদের সমস্যা হচ্ছে। অনুগ্রহ করে আবার পাঠানোর চেষ্টা করুন।',
    microphoneProblemTextChat:
      'মনে হচ্ছে আপনার মাইক্রোফোনে সমস্যা আছে। অনুগ্রহ করে কীবোর্ড বোতাম ব্যবহার করে আপনার উত্তর টাইপ করুন।',
    microphoneProblemVoiceChat:
      'মনে হচ্ছে আপনার মাইক্রোফোনে সমস্যা আছে। অনুগ্রহ করে আপনার ব্রাউজারের অনুমতি যাচাই করুন এবং আবার চেষ্টা করুন।',
    responseTimeoutError: 'প্রতিক্রিয়া অনেক সময় নিচ্ছে। অনুগ্রহ করে আবার চেষ্টা করুন।',
    noResponseError: 'কোন প্রতিক্রিয়া সনাক্ত করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।',
    connectionInterrupted: 'সংযোগ বিঘ্নিত হয়েছে। আপনি আপনার বার্তা পুনরায় পাঠাতে পারেন।',
    videoStartError: 'ভিডিও রেকর্ডিং শুরু করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    videoCameraNotSupported:
      'ক্যামেরা সমর্থিত নয়। অনুগ্রহ করে একটি ভিন্ন ব্রাউজার দিয়ে চেষ্টা করুন।',
    videoPauseError: 'ভিডিও রেকর্ডিং বিরতি দিতে ব্যর্থ।',
    videoResumeError: 'ভিডিও রেকর্ডিং পুনরায় শুরু করতে ব্যর্থ।',
    videoStopError: 'ভিডিও রেকর্ডিং বন্ধ করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    videoSwitchCameraError: 'ক্যামেরা পরিবর্তন করতে ব্যর্থ। অনুগ্রহ করে আবার চেষ্টা করুন।',
    videoSendError: 'ভিডিও বার্তা পাঠাতে ব্যর্থ।',
    welcomeBack: 'ফিরে আসার জন্য স্বাগতম!',
    unsupportedFileType:
      'অসমর্থিত ফাইল প্রকার। অনুগ্রহ করে একটি JPG, PNG, GIF, বা MP4 ফাইল আপলোড করুন।',
    fileTooLarge: 'এই ফাইলটি খুব বড়। সর্বোচ্চ আকার ২০০ MB।',
    audioCloseError: 'অডিও বন্ধ করতে ত্রুটি:',
  },
  bs: {
    telletDoesNotExistTitle: 'Ova poveznica nije dostupna',
    projectDoesNotExistTitle: 'Ova poveznica nije dostupna',
    projectDoesNotExistMessage:
      'Nažalost, nismo mogli pronaći ovu studiju. Molimo provjerite poveznicu koju ste dobili ili kontaktirajte osobu koja vas je pozvala.',
    maximumParticipantsTitle: 'Hvala vam',
    maximumParticipantsMessage:
      'Hvala vam na interesovanju za učešće u ovom istraživanju.\nPrimili smo dovoljan broj odgovora i trenutno, nažalost, ne možemo prihvatiti nove učesnike.\nCijenimo što ste odvojili vrijeme da posjetite ovu studiju i vašu spremnost da doprinesete ovom važnom projektu.\nZahvaljujemo na razumijevanju.',
    somethingMissingTitle: 'Ova poveznica nije dostupna',
    somethingMissingMessage:
      'Nažalost, nismo mogli pronaći ovu studiju. Molimo provjerite poveznicu koju ste dobili ili kontaktirajte osobu koja vas je pozvala.',
    projectCompletedTitle: 'Hvala vam',
    projectCompletedMessage:
      'Hvala vam na interesovanju za učešće u ovom istraživanju.\nOva studija je sada završena i, nažalost, više ne možemo prihvatiti nove učesnike.\nCijenimo vašu spremnost da doprinesete i zahvaljujemo na razumijevanju.',
    projectInDraftOrReadyStatusTitle: 'Još nije otvoreno',
    projectInDraftOrReadyStatusMessage:
      'Hvala vam na interesovanju za učešće u ovom istraživanju.\nOva studija još nije otvorena za učešće. Molimo pokušajte kasnije ili kontaktirajte osobu koja vas je pozvala.',
    redirectingIn: 'Preusmjeravanje za',
    seconds: 'sekundi...',
    cancel: 'Otkaži',
    returnToPanel: 'Povratak na panel',
    errorBoundaryTitle: 'Nešto je pošlo po zlu',
    errorBoundaryMessage:
      'Došlo je do neočekivane greške. Molimo vas da osvježite stranicu kako biste nastavili.',
    errorBoundaryReloadButton: 'Osvježi stranicu',
    messageProcessingError:
      'Ups! Izgleda da imamo problema s obradom vaše poruke. Molimo pokušajte je ponovo poslati.',
    microphoneProblemTextChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo ukucajte svoje odgovore koristeći dugme tastature.',
    microphoneProblemVoiceChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo provjerite dozvole preglednika i pokušajte ponovo.',
    responseTimeoutError: 'Odgovor predugo traje. Molimo pokušajte ponovo.',
    noResponseError: 'Nije otkriven odgovor. Molimo pokušajte ponovo.',
    connectionInterrupted: 'Veza je prekinuta. Možete ponovo poslati svoju poruku.',
    videoStartError: 'Pokretanje video snimanja nije uspjelo. Molimo pokušajte ponovo.',
    videoCameraNotSupported: 'Kamera nije podržana. Molimo pokušajte s drugim preglednikom.',
    videoPauseError: 'Pauziranje video snimanja nije uspjelo.',
    videoResumeError: 'Nastavak video snimanja nije uspio.',
    videoStopError: 'Zaustavljanje video snimanja nije uspjelo. Molimo pokušajte ponovo.',
    videoSwitchCameraError: 'Promjena kamere nije uspjela. Molimo pokušajte ponovo.',
    videoSendError: 'Slanje video poruke nije uspjelo.',
    welcomeBack: 'Dobrodošli nazad!',
    unsupportedFileType:
      'Nepodržana vrsta datoteke. Molimo prenesite JPG, PNG, GIF ili MP4 datoteku.',
    fileTooLarge: 'Ova datoteka je prevelika. Maksimalna veličina je 200 MB.',
    audioCloseError: 'Greška pri zatvaranju zvuka:',
  },
  bg: {
    telletDoesNotExistTitle: 'Този линк не е наличен',
    projectDoesNotExistTitle: 'Този линк не е наличен',
    projectDoesNotExistMessage:
      'Съжаляваме, но не успяхме да намерим това проучване. Моля, проверете линка, който сте получили, или се свържете с лицето, което ви е поканило.',
    maximumParticipantsTitle: 'Благодарим ви',
    maximumParticipantsMessage:
      'Благодарим ви за интереса да участвате в това изследване.\nВече получихме необходимия брой отговори, затова за съжаление не можем да приемем повече участници в момента.\nОценяваме, че отделихте време да посетите това проучване и сте готови да допринесете за този важен проект.\nБлагодарим ви за разбирането.',
    somethingMissingTitle: 'Този линк не е наличен',
    somethingMissingMessage:
      'Съжаляваме, но не успяхме да намерим това проучване. Моля, проверете линка, който сте получили, или се свържете с лицето, което ви е поканило.',
    projectCompletedTitle: 'Благодарим ви',
    projectCompletedMessage:
      'Благодарим ви за интереса да участвате в това изследване.\nТова проучване вече е приключило, затова за съжаление не можем да приемем повече участници.\nОценяваме вашата готовност да допринесете и ви благодарим за разбирането.',
    projectInDraftOrReadyStatusTitle: 'Все още не е отворено',
    projectInDraftOrReadyStatusMessage:
      'Благодарим ви за интереса да участвате в това изследване.\nТова проучване все още не е отворено за участие. Моля, опитайте отново по-късно или се свържете с лицето, което ви е поканило.',
    redirectingIn: 'Пренасочване след',
    seconds: 'секунди...',
    cancel: 'Отказ',
    returnToPanel: 'Връщане към панела',
    errorBoundaryTitle: 'Нещо се обърка',
    errorBoundaryMessage:
      'Възникна неочаквана грешка. Моля, презаредете страницата, за да продължите.',
    errorBoundaryReloadButton: 'Презареди страницата',
    messageProcessingError:
      'Опа! Изглежда имаме проблем с обработката на вашето съобщение. Моля, опитайте да го изпратите отново.',
    microphoneProblemTextChat:
      'Изглежда, че има проблем с вашия микрофон. Моля, въведете отговорите си, използвайки бутона за клавиатура.',
    microphoneProblemVoiceChat:
      'Изглежда, че има проблем с вашия микрофон. Моля, проверете разрешенията на браузъра си и опитайте отново.',
    responseTimeoutError: 'Отговорът отнема твърде дълго. Моля, опитайте отново.',
    noResponseError: 'Не е открит отговор. Моля, опитайте отново.',
    connectionInterrupted: 'Връзката е прекъсната. Можете да изпратите отново съобщението си.',
    videoStartError: 'Неуспешно стартиране на видеозапис. Моля, опитайте отново.',
    videoCameraNotSupported: 'Камерата не се поддържа. Моля, опитайте с друг браузър.',
    videoPauseError: 'Неуспешно поставяне на пауза на видеозаписа.',
    videoResumeError: 'Неуспешно възобновяване на видеозаписа.',
    videoStopError: 'Неуспешно спиране на видеозаписа. Моля, опитайте отново.',
    videoSwitchCameraError: 'Неуспешна смяна на камерата. Моля, опитайте отново.',
    videoSendError: 'Неуспешно изпращане на видеосъобщение.',
    welcomeBack: 'Добре дошли отново!',
    unsupportedFileType: 'Неподдържан тип файл. Моля, качете JPG, PNG, GIF или MP4 файл.',
    fileTooLarge: 'Този файл е твърде голям. Максималният размер е 200 MB.',
    audioCloseError: 'Грешка при затваряне на аудио:',
  },
  yue: {
    telletDoesNotExistTitle: '連結無效',
    projectDoesNotExistTitle: '連結無效',
    projectDoesNotExistMessage:
      '唔好意思，我哋搵唔到呢個研究。請檢查您收到嘅連結，或者聯絡邀請您嘅人士。',
    maximumParticipantsTitle: '多謝您',
    maximumParticipantsMessage:
      '多謝您有興趣參加呢個研究。\n我哋而家已經收到所需嘅回應數目，所以暫時唔再接受新參加者。\n多謝您抽時間瀏覽呢個研究，亦感謝您願意為呢個重要項目出一分力。\n多謝您體諒。',
    somethingMissingTitle: '連結無效',
    somethingMissingMessage:
      '唔好意思，我哋搵唔到呢個研究。請檢查您收到嘅連結，或者聯絡邀請您嘅人士。',
    projectCompletedTitle: '多謝您',
    projectCompletedMessage:
      '多謝您有興趣參加呢個研究。\n呢個研究而家已經結束，所以暫時唔再接受新參加者。\n多謝您願意參與，亦感謝您體諒。',
    projectInDraftOrReadyStatusTitle: '暫未開放',
    projectInDraftOrReadyStatusMessage:
      '多謝您有興趣參加呢個研究。\n呢個研究暫時未開放參與。請稍後再試，或者聯絡邀請您嘅人士。',
    redirectingIn: '重新導向仲有',
    seconds: '秒...',
    cancel: '取消',
    returnToPanel: '返回面板',
    errorBoundaryTitle: '出咗啲問題',
    errorBoundaryMessage: '發生咗預料之外嘅錯誤。請重新載入頁面以繼續。',
    errorBoundaryReloadButton: '重新載入頁面',
    messageProcessingError: '哎呀！好似處理你嘅訊息時出咗問題。請試下重新發送。',
    microphoneProblemTextChat: '你嘅咪高峰好似有問題。請用鍵盤掣打字輸入你嘅答案。',
    microphoneProblemVoiceChat: '你嘅咪高峰好似有問題。請檢查你嘅瀏覽器權限然後再試。',
    responseTimeoutError: '回應時間太長。請再試一次。',
    noResponseError: '偵測唔到回應。請再試一次。',
    connectionInterrupted: '連接中斷。你可以重新發送你嘅訊息。',
    videoStartError: '無法開始錄影。請再試一次。',
    videoCameraNotSupported: '唔支援相機。請用另一個瀏覽器試下。',
    videoPauseError: '無法暫停錄影。',
    videoResumeError: '無法繼續錄影。',
    videoStopError: '無法停止錄影。請再試一次。',
    videoSwitchCameraError: '無法切換相機。請再試一次。',
    videoSendError: '無法傳送影片訊息。',
    welcomeBack: '歡迎返嚟！',
    unsupportedFileType: '唔支援嘅檔案類型。請上傳 JPG、PNG、GIF 或 MP4 檔案。',
    fileTooLarge: '呢個檔案太大。最大限制係 200 MB。',
    audioCloseError: '關閉音訊時出錯：',
  },
  ca: {
    telletDoesNotExistTitle: 'Aquest enllaç no està disponible',
    projectDoesNotExistTitle: 'Aquest enllaç no està disponible',
    projectDoesNotExistMessage:
      "Ens sap greu, però no hem pogut trobar aquest estudi. Si us plau, comprovi l'enllaç que li han facilitat o posi's en contacte amb la persona que l'ha convidat.",
    maximumParticipantsTitle: 'Gràcies',
    maximumParticipantsMessage:
      "Gràcies per l'interès a participar en aquesta investigació.\nJa hem rebut el nombre de respostes necessàries, per la qual cosa, malauradament, no podem acceptar més participants en aquest moment.\nAgraïm que hagi dedicat temps a visitar aquest estudi i la seva voluntat de contribuir a aquest projecte tan important.\nGràcies per la seva comprensió.",
    somethingMissingTitle: 'Aquest enllaç no està disponible',
    somethingMissingMessage:
      "Ens sap greu, però no hem pogut trobar aquest estudi. Si us plau, comprovi l'enllaç que li han facilitat o posi's en contacte amb la persona que l'ha convidat.",
    projectCompletedTitle: 'Gràcies',
    projectCompletedMessage:
      "Gràcies per l'interès a participar en aquesta investigació.\nAquest estudi ja està tancat, així que, malauradament, no podem acceptar més participants.\nAgraïm la seva voluntat de contribuir i li donem les gràcies per la seva comprensió.",
    projectInDraftOrReadyStatusTitle: 'Encara no està obert',
    projectInDraftOrReadyStatusMessage:
      "Gràcies per l'interès a participar en aquesta investigació.\nAquest estudi encara no està obert a la participació. Torni-ho a intentar més endavant o posi's en contacte amb la persona que l'ha convidat.",
    redirectingIn: 'Redirigint en',
    seconds: 'segons...',
    cancel: 'Cancel·la',
    returnToPanel: 'Tornar al panell',
    errorBoundaryTitle: 'Alguna cosa ha anat malament',
    errorBoundaryMessage:
      "S'ha produït un error inesperat. Si us plau, torna a carregar la pàgina per continuar.",
    errorBoundaryReloadButton: 'Torna a carregar la pàgina',
    messageProcessingError:
      'Vaja! Sembla que tenim problemes per processar el teu missatge. Si us plau, prova de reenviar-lo.',
    microphoneProblemTextChat:
      'Sembla que hi ha un problema amb el teu micròfon. Si us plau, escriu les teves respostes amb el botó del teclat.',
    microphoneProblemVoiceChat:
      'Sembla que hi ha un problema amb el teu micròfon. Si us plau, comprova els permisos del navegador i torna-ho a provar.',
    responseTimeoutError: 'La resposta triga massa. Si us plau, torna-ho a provar.',
    noResponseError: "No s'ha detectat cap resposta. Si us plau, torna-ho a provar.",
    connectionInterrupted: 'Connexió interrompuda. Pots tornar a enviar el teu missatge.',
    videoStartError: "No s'ha pogut iniciar la gravació de vídeo. Si us plau, torna-ho a provar.",
    videoCameraNotSupported:
      'La càmera no és compatible. Si us plau, prova amb un altre navegador.',
    videoPauseError: "No s'ha pogut pausar la gravació de vídeo.",
    videoResumeError: "No s'ha pogut reprendre la gravació de vídeo.",
    videoStopError: "No s'ha pogut aturar la gravació de vídeo. Si us plau, torna-ho a provar.",
    videoSwitchCameraError: "No s'ha pogut canviar de càmera. Si us plau, torna-ho a provar.",
    videoSendError: "No s'ha pogut enviar el missatge de vídeo.",
    welcomeBack: 'Benvingut de nou!',
    unsupportedFileType:
      'Tipus de fitxer no compatible. Si us plau, puja un fitxer JPG, PNG, GIF o MP4.',
    fileTooLarge: 'Aquest fitxer és massa gran. La mida màxima és de 200 MB.',
    audioCloseError: "Error en tancar l'àudio:",
  },
  zh: {
    telletDoesNotExistTitle: '此链接不可用',
    projectDoesNotExistTitle: '此链接不可用',
    projectDoesNotExistMessage:
      '很抱歉，我们无法找到本研究。请检查您收到的链接，或联系邀请您的人员。',
    maximumParticipantsTitle: '感谢您',
    maximumParticipantsMessage:
      '感谢您有意参与本次研究。\n目前我们已收到所需的回复数量，因此暂时无法接受更多参与者。\n非常感谢您抽出宝贵时间访问本研究，并愿意为这一重要项目作出贡献。\n感谢您的理解。',
    somethingMissingTitle: '此链接不可用',
    somethingMissingMessage: '很抱歉，我们无法找到本研究。请检查您收到的链接，或联系邀请您的人员。',
    projectCompletedTitle: '感谢您',
    projectCompletedMessage:
      '感谢您有意参与本次研究。\n本研究现已结束，因此我们无法再接受新的参与者。\n感谢您愿意贡献力量，感谢您的理解。',
    projectInDraftOrReadyStatusTitle: '尚未开放',
    projectInDraftOrReadyStatusMessage:
      '感谢您有意参与本次研究。\n本研究尚未开放参与。请稍后再试，或联系邀请您的人员。',
    redirectingIn: '重定向倒计时',
    seconds: '秒...',
    cancel: '取消',
    returnToPanel: '返回面板',
    errorBoundaryTitle: '出现问题',
    errorBoundaryMessage: '发生意外错误。请重新加载页面以继续。',
    errorBoundaryReloadButton: '重新加载页面',
    messageProcessingError: '糟糕！我们在处理您的消息时遇到问题。请尝试重新发送。',
    microphoneProblemTextChat: '您的麦克风似乎有问题。请使用键盘按钮输入您的回答。',
    microphoneProblemVoiceChat: '您的麦克风似乎有问题。请检查您的浏览器权限并重试。',
    responseTimeoutError: '响应时间过长。请重试。',
    noResponseError: '未检测到响应。请重试。',
    connectionInterrupted: '连接中断。您可以重新发送您的消息。',
    videoStartError: '无法开始视频录制。请重试。',
    videoCameraNotSupported: '不支持摄像头。请尝试使用其他浏览器。',
    videoPauseError: '无法暂停视频录制。',
    videoResumeError: '无法恢复视频录制。',
    videoStopError: '无法停止视频录制。请重试。',
    videoSwitchCameraError: '无法切换摄像头。请重试。',
    videoSendError: '无法发送视频消息。',
    welcomeBack: '欢迎回来！',
    unsupportedFileType: '不支持的文件类型。请上传 JPG、PNG、GIF 或 MP4 文件。',
    fileTooLarge: '此文件过大。最大限制为 200 MB。',
    audioCloseError: '关闭音频时出错：',
  },
  'zh-TW': {
    telletDoesNotExistTitle: '此連結無法使用',
    projectDoesNotExistTitle: '此連結無法使用',
    projectDoesNotExistMessage:
      '很抱歉，我們無法找到此研究。請確認您收到的連結是否正確，或聯絡邀請您的相關人員。',
    maximumParticipantsTitle: '感謝您',
    maximumParticipantsMessage:
      '感謝您有意參與本研究。\n目前我們已收到所需的回覆數量，因此暫時無法再接受新的參與者。\n感謝您撥冗瀏覽本研究並願意貢獻寶貴意見。\n感謝您的理解。',
    somethingMissingTitle: '此連結無法使用',
    somethingMissingMessage:
      '很抱歉，我們無法找到此研究。請確認您收到的連結是否正確，或聯絡邀請您的相關人員。',
    projectCompletedTitle: '感謝您',
    projectCompletedMessage:
      '感謝您有意參與本研究。\n本研究現已結束，因此暫時無法再接受新的參與者。\n感謝您願意貢獻寶貴意見，並感謝您的理解。',
    projectInDraftOrReadyStatusTitle: '尚未開放',
    projectInDraftOrReadyStatusMessage:
      '感謝您有意參與本研究。\n本研究尚未開放參與。請稍後再試，或聯絡邀請您的相關人員。',
    redirectingIn: '重新導向倒數',
    seconds: '秒...',
    cancel: '取消',
    returnToPanel: '返回面板',
    errorBoundaryTitle: '出現問題',
    errorBoundaryMessage: '發生意外錯誤。請重新載入頁面以繼續。',
    errorBoundaryReloadButton: '重新載入頁面',
    messageProcessingError: '糟糕！我們在處理您的訊息時遇到問題。請嘗試重新傳送。',
    microphoneProblemTextChat: '您的麥克風似乎有問題。請使用鍵盤按鈕輸入您的回答。',
    microphoneProblemVoiceChat: '您的麥克風似乎有問題。請檢查您的瀏覽器權限並重試。',
    responseTimeoutError: '回應時間過長。請重試。',
    noResponseError: '未偵測到回應。請重試。',
    connectionInterrupted: '連線中斷。您可以重新傳送您的訊息。',
    videoStartError: '無法開始影片錄製。請重試。',
    videoCameraNotSupported: '不支援攝影機。請嘗試使用其他瀏覽器。',
    videoPauseError: '無法暫停影片錄製。',
    videoResumeError: '無法繼續影片錄製。',
    videoStopError: '無法停止影片錄製。請重試。',
    videoSwitchCameraError: '無法切換攝影機。請重試。',
    videoSendError: '無法傳送影片訊息。',
    welcomeBack: '歡迎回來！',
    unsupportedFileType: '不支援的檔案類型。請上傳 JPG、PNG、GIF 或 MP4 檔案。',
    fileTooLarge: '此檔案過大。大小上限為 200 MB。',
    audioCloseError: '關閉音訊時發生錯誤：',
  },
  hr: {
    telletDoesNotExistTitle: 'Ova poveznica nije dostupna',
    projectDoesNotExistTitle: 'Ova poveznica nije dostupna',
    projectDoesNotExistMessage:
      'Nažalost, nismo uspjeli pronaći ovu studiju. Molimo provjerite poveznicu koju ste dobili ili kontaktirajte osobu koja vas je pozvala.',
    maximumParticipantsTitle: 'Hvala vam',
    maximumParticipantsMessage:
      'Zahvaljujemo na vašem interesu za sudjelovanje u ovom istraživanju.\nPrimili smo dovoljan broj odgovora, pa nažalost trenutno ne možemo prihvatiti nove sudionike.\nCijenimo što ste odvojili vrijeme za posjet ovoj studiji i vašu spremnost da doprinesete ovom važnom projektu.\nHvala na razumijevanju.',
    somethingMissingTitle: 'Ova poveznica nije dostupna',
    somethingMissingMessage:
      'Nažalost, nismo uspjeli pronaći ovu studiju. Molimo provjerite poveznicu koju ste dobili ili kontaktirajte osobu koja vas je pozvala.',
    projectCompletedTitle: 'Hvala vam',
    projectCompletedMessage:
      'Zahvaljujemo na vašem interesu za sudjelovanje u ovom istraživanju.\nOva studija je sada završena, pa nažalost više ne možemo prihvatiti nove sudionike.\nCijenimo vašu spremnost na doprinos i zahvaljujemo na razumijevanju.',
    projectInDraftOrReadyStatusTitle: 'Još nije otvoreno',
    projectInDraftOrReadyStatusMessage:
      'Zahvaljujemo na vašem interesu za sudjelovanje u ovom istraživanju.\nOva studija još nije otvorena za sudjelovanje. Molimo pokušajte ponovno kasnije ili kontaktirajte osobu koja vas je pozvala.',
    redirectingIn: 'Preusmjeravanje za',
    seconds: 'sekundi...',
    cancel: 'Odustani',
    returnToPanel: 'Povratak na panel',
    errorBoundaryTitle: 'Nešto je pošlo po zlu',
    errorBoundaryMessage:
      'Došlo je do neočekivane pogreške. Molimo ponovno učitajte stranicu za nastavak.',
    errorBoundaryReloadButton: 'Ponovno učitaj stranicu',
    messageProcessingError:
      'Ups! Čini se da imamo problema s obradom vaše poruke. Molimo pokušajte je ponovno poslati.',
    microphoneProblemTextChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo upišite svoje odgovore pomoću gumba tipkovnice.',
    microphoneProblemVoiceChat:
      'Čini se da postoji problem s vašim mikrofonom. Molimo provjerite dopuštenja preglednika i pokušajte ponovno.',
    responseTimeoutError: 'Odgovor predugo traje. Molimo pokušajte ponovno.',
    noResponseError: 'Nije otkriven odgovor. Molimo pokušajte ponovno.',
    connectionInterrupted: 'Veza je prekinuta. Možete ponovno poslati svoju poruku.',
    videoStartError: 'Pokretanje video snimanja nije uspjelo. Molimo pokušajte ponovno.',
    videoCameraNotSupported: 'Kamera nije podržana. Molimo pokušajte s drugim preglednikom.',
    videoPauseError: 'Pauziranje video snimanja nije uspjelo.',
    videoResumeError: 'Nastavak video snimanja nije uspio.',
    videoStopError: 'Zaustavljanje video snimanja nije uspjelo. Molimo pokušajte ponovno.',
    videoSwitchCameraError: 'Promjena kamere nije uspjela. Molimo pokušajte ponovno.',
    videoSendError: 'Slanje videoporuke nije uspjelo.',
    welcomeBack: 'Dobrodošli natrag!',
    unsupportedFileType:
      'Nepodržana vrsta datoteke. Molimo prenesite JPG, PNG, GIF ili MP4 datoteku.',
    fileTooLarge: 'Ova datoteka je prevelika. Maksimalna veličina je 200 MB.',
    audioCloseError: 'Pogreška pri zatvaranju zvuka:',
  },
  cs: {
    telletDoesNotExistTitle: 'Tento odkaz není dostupný',
    projectDoesNotExistTitle: 'Tento odkaz není dostupný',
    projectDoesNotExistMessage:
      'Omlouváme se, ale tuto studii se nám nepodařilo najít. Zkontrolujte prosím odkaz, který jste obdrželi, nebo kontaktujte osobu, která Vás pozvala.',
    maximumParticipantsTitle: 'Děkujeme',
    maximumParticipantsMessage:
      'Děkujeme za Váš zájem o účast v tomto výzkumu.\nJiž jsme obdrželi potřebný počet odpovědí, a proto bohužel v tuto chvíli nemůžeme přijímat další účastníky.\nVelmi si vážíme toho, že jste si našli čas navštívit tuto studii a byli ochotni přispět k tomuto důležitému projektu.\nDěkujeme za pochopení.',
    somethingMissingTitle: 'Tento odkaz není dostupný',
    somethingMissingMessage:
      'Omlouváme se, ale tuto studii se nám nepodařilo najít. Zkontrolujte prosím odkaz, který jste obdrželi, nebo kontaktujte osobu, která Vás pozvala.',
    projectCompletedTitle: 'Děkujeme',
    projectCompletedMessage:
      'Děkujeme za Váš zájem o účast v tomto výzkumu.\nTato studie již byla uzavřena, a proto bohužel nemůžeme přijímat další účastníky.\nVážíme si Vaší ochoty přispět a děkujeme za pochopení.',
    projectInDraftOrReadyStatusTitle: 'Zatím není otevřeno',
    projectInDraftOrReadyStatusMessage:
      'Děkujeme za Váš zájem o účast v tomto výzkumu.\nTato studie zatím není otevřena pro účast. Zkuste to prosím později, nebo kontaktujte osobu, která Vás pozvala.',
    redirectingIn: 'Přesměrování za',
    seconds: 'sekund...',
    cancel: 'Zrušit',
    returnToPanel: 'Zpět na panel',
    errorBoundaryTitle: 'Něco se pokazilo',
    errorBoundaryMessage:
      'Došlo k neočekávané chybě. Pro pokračování prosím znovu načtěte stránku.',
    errorBoundaryReloadButton: 'Znovu načíst stránku',
    messageProcessingError:
      'Jejda! Zdá se, že máme potíže se zpracováním vaší zprávy. Zkuste ji prosím odeslat znovu.',
    microphoneProblemTextChat:
      'Zdá se, že je problém s vaším mikrofonem. Napište prosím své odpovědi pomocí tlačítka klávesnice.',
    microphoneProblemVoiceChat:
      'Zdá se, že je problém s vaším mikrofonem. Zkontrolujte prosím oprávnění prohlížeče a zkuste to znovu.',
    responseTimeoutError: 'Odpověď trvá příliš dlouho. Zkuste to prosím znovu.',
    noResponseError: 'Nebyla zjištěna žádná odpověď. Zkuste to prosím znovu.',
    connectionInterrupted: 'Spojení bylo přerušeno. Můžete znovu odeslat svou zprávu.',
    videoStartError: 'Spuštění nahrávání videa se nezdařilo. Zkuste to prosím znovu.',
    videoCameraNotSupported: 'Kamera není podporována. Zkuste to prosím s jiným prohlížečem.',
    videoPauseError: 'Pozastavení nahrávání videa se nezdařilo.',
    videoResumeError: 'Obnovení nahrávání videa se nezdařilo.',
    videoStopError: 'Zastavení nahrávání videa se nezdařilo. Zkuste to prosím znovu.',
    videoSwitchCameraError: 'Přepnutí kamery se nezdařilo. Zkuste to prosím znovu.',
    videoSendError: 'Odeslání videozprávy se nezdařilo.',
    welcomeBack: 'Vítejte zpět!',
    unsupportedFileType:
      'Nepodporovaný typ souboru. Nahrajte prosím soubor JPG, PNG, GIF nebo MP4.',
    fileTooLarge: 'Tento soubor je příliš velký. Maximální velikost je 200 MB.',
    audioCloseError: 'Chyba při zavírání zvuku:',
  },
  da: {
    telletDoesNotExistTitle: 'Dette link er ikke tilgængeligt',
    projectDoesNotExistTitle: 'Dette link er ikke tilgængeligt',
    projectDoesNotExistMessage:
      'Vi beklager, men vi kunne ikke finde denne undersøgelse. Kontroller venligst det link, De har modtaget, eller kontakt den person, der har inviteret Dem.',
    maximumParticipantsTitle: 'Tak',
    maximumParticipantsMessage:
      'Tak for Deres interesse i at deltage i denne undersøgelse.\nVi har nu modtaget det nødvendige antal svar, og kan desværre ikke acceptere flere deltagere på nuværende tidspunkt.\nVi sætter pris på, at De tog Dem tid til at besøge denne undersøgelse og Deres vilje til at bidrage til dette vigtige projekt.\nTak for Deres forståelse.',
    somethingMissingTitle: 'Dette link er ikke tilgængeligt',
    somethingMissingMessage:
      'Vi beklager, men vi kunne ikke finde denne undersøgelse. Kontroller venligst det link, De har modtaget, eller kontakt den person, der har inviteret Dem.',
    projectCompletedTitle: 'Tak',
    projectCompletedMessage:
      'Tak for Deres interesse i at deltage i denne undersøgelse.\nDenne undersøgelse er nu afsluttet, og vi kan desværre ikke acceptere flere deltagere.\nVi værdsætter Deres vilje til at bidrage, og takker for Deres forståelse.',
    projectInDraftOrReadyStatusTitle: 'Ikke åbnet endnu',
    projectInDraftOrReadyStatusMessage:
      'Tak for Deres interesse i at deltage i denne undersøgelse.\nDenne undersøgelse er endnu ikke åben for deltagelse. Prøv venligst igen senere, eller kontakt den person, der har inviteret Dem.',
    redirectingIn: 'Omdirigerer om',
    seconds: 'sekunder...',
    cancel: 'Annuller',
    returnToPanel: 'Tilbage til panelet',
    errorBoundaryTitle: 'Noget gik galt',
    errorBoundaryMessage: 'Der opstod en uventet fejl. Genindlæs venligst siden for at fortsætte.',
    errorBoundaryReloadButton: 'Genindlæs siden',
    messageProcessingError:
      'Ups! Det ser ud til, at vi har problemer med at behandle din besked. Prøv venligst at sende den igen.',
    microphoneProblemTextChat:
      'Det ser ud til, at der er et problem med din mikrofon. Skriv venligst dine svar ved hjælp af tastaturknappen.',
    microphoneProblemVoiceChat:
      'Det ser ud til, at der er et problem med din mikrofon. Tjek venligst dine browsertilladelser, og prøv igen.',
    responseTimeoutError: 'Svaret tager for lang tid. Prøv venligst igen.',
    noResponseError: 'Intet svar registreret. Prøv venligst igen.',
    connectionInterrupted: 'Forbindelsen blev afbrudt. Du kan sende din besked igen.',
    videoStartError: 'Kunne ikke starte videooptagelse. Prøv venligst igen.',
    videoCameraNotSupported: 'Kameraet understøttes ikke. Prøv venligst med en anden browser.',
    videoPauseError: 'Kunne ikke sætte videooptagelse på pause.',
    videoResumeError: 'Kunne ikke genoptage videooptagelse.',
    videoStopError: 'Kunne ikke stoppe videooptagelse. Prøv venligst igen.',
    videoSwitchCameraError: 'Kunne ikke skifte kamera. Prøv venligst igen.',
    videoSendError: 'Kunne ikke sende videobesked.',
    welcomeBack: 'Velkommen tilbage!',
    unsupportedFileType:
      'Ikke-understøttet filtype. Upload venligst en JPG-, PNG-, GIF- eller MP4-fil.',
    fileTooLarge: 'Denne fil er for stor. Den maksimale størrelse er 200 MB.',
    audioCloseError: 'Fejl ved lukning af lyd:',
  },
  nl: {
    telletDoesNotExistTitle: 'Deze link is niet beschikbaar',
    projectDoesNotExistTitle: 'Deze link is niet beschikbaar',
    projectDoesNotExistMessage:
      'Het spijt ons, maar wij kunnen dit onderzoek niet vinden. Controleert u alstublieft de link die u heeft ontvangen of neem contact op met de persoon die u heeft uitgenodigd.',
    maximumParticipantsTitle: 'Dank u wel',
    maximumParticipantsMessage:
      'Dank u wel voor uw interesse in dit onderzoek.\nWe hebben inmiddels het benodigde aantal reacties ontvangen en kunnen op dit moment helaas geen nieuwe deelnemers meer toelaten.\nWij waarderen het dat u de tijd heeft genomen om dit onderzoek te bezoeken en uw bereidheid om bij te dragen aan dit belangrijke project.\nDank u voor uw begrip.',
    somethingMissingTitle: 'Deze link is niet beschikbaar',
    somethingMissingMessage:
      'Het spijt ons, maar wij kunnen dit onderzoek niet vinden. Controleert u alstublieft de link die u heeft ontvangen of neem contact op met de persoon die u heeft uitgenodigd.',
    projectCompletedTitle: 'Dank u wel',
    projectCompletedMessage:
      'Dank u wel voor uw interesse in dit onderzoek.\nDit onderzoek is inmiddels gesloten, waardoor we helaas geen nieuwe deelnemers meer kunnen toelaten.\nWij stellen uw bereidheid om bij te dragen zeer op prijs en danken u voor uw begrip.',
    projectInDraftOrReadyStatusTitle: 'Nog niet geopend',
    projectInDraftOrReadyStatusMessage:
      'Dank u wel voor uw interesse in dit onderzoek.\nDit onderzoek is nog niet geopend voor deelname. Probeert u het later opnieuw of neem contact op met de persoon die u heeft uitgenodigd.',
    redirectingIn: 'Doorverwijzen over',
    seconds: 'seconden...',
    cancel: 'Annuleren',
    returnToPanel: 'Terug naar panel',
    errorBoundaryTitle: 'Er is iets misgegaan',
    errorBoundaryMessage:
      'Er is een onverwachte fout opgetreden. Laad de pagina opnieuw om door te gaan.',
    errorBoundaryReloadButton: 'Pagina opnieuw laden',
    messageProcessingError:
      'Oeps! Het lijkt erop dat we problemen hebben met het verwerken van je bericht. Probeer het opnieuw te versturen.',
    microphoneProblemTextChat:
      'Er lijkt een probleem te zijn met je microfoon. Typ je antwoorden met de toetsenbordknop.',
    microphoneProblemVoiceChat:
      'Er lijkt een probleem te zijn met je microfoon. Controleer je browserrechten en probeer het opnieuw.',
    responseTimeoutError: 'Reactie duurt te lang. Probeer het opnieuw.',
    noResponseError: 'Geen reactie ontvangen. Probeer het opnieuw.',
    connectionInterrupted: 'Verbinding onderbroken. Je kunt je bericht opnieuw versturen.',
    videoStartError: 'Kan video-opname niet starten. Probeer het opnieuw.',
    videoCameraNotSupported: 'Camera wordt niet ondersteund. Probeer het met een andere browser.',
    videoPauseError: 'Kan video-opname niet pauzeren.',
    videoResumeError: 'Kan video-opname niet hervatten.',
    videoStopError: 'Kan video-opname niet stoppen. Probeer het opnieuw.',
    videoSwitchCameraError: 'Kan camera niet wisselen. Probeer het opnieuw.',
    videoSendError: 'Kan videobericht niet versturen.',
    welcomeBack: 'Welkom terug!',
    unsupportedFileType:
      'Niet-ondersteund bestandstype. Upload een JPG-, PNG-, GIF- of MP4-bestand.',
    fileTooLarge: 'Dit bestand is te groot. De maximale grootte is 200 MB.',
    audioCloseError: 'Fout bij sluiten van audio:',
  },
  en: {
    telletDoesNotExistTitle: 'This link is not available',
    projectDoesNotExistTitle: 'This link is not available',
    projectDoesNotExistMessage:
      "We're sorry, but we could not find this study. Please check the link you were given, or contact the person who invited you.",
    maximumParticipantsTitle: 'Thank you',
    maximumParticipantsMessage:
      'Thank you for your interest in taking part in this research.\nWe have now received the number of responses needed, so we are unfortunately unable to accept any further participants at this time.\nWe appreciate you taking the time to visit this study and your willingness to contribute to this important project.\nThank you for your understanding.',
    somethingMissingTitle: 'This link is not available',
    somethingMissingMessage:
      "We're sorry, but we could not find this study. Please check the link you were given, or contact the person who invited you.",
    projectCompletedTitle: 'Thank you',
    projectCompletedMessage:
      'Thank you for your interest in taking part in this research.\nThis study has now closed, so we are unfortunately unable to accept any further participants.\nWe appreciate your willingness to contribute, and thank you for your understanding.',
    projectInDraftOrReadyStatusTitle: 'Not open yet',
    projectInDraftOrReadyStatusMessage:
      'Thank you for your interest in taking part in this research.\nThis study is not open for participation yet. Please try again later, or contact the person who invited you.',
    redirectingIn: 'Redirecting in',
    seconds: 'seconds...',
    cancel: 'Cancel',
    returnToPanel: 'Return to Panel',
    errorBoundaryTitle: 'Something went wrong',
    errorBoundaryMessage: 'An unexpected error occurred. Please reload the page to continue.',
    errorBoundaryReloadButton: 'Reload page',
    messageProcessingError:
      "Oops! Looks like we're having trouble processing your message. Please try resending it.",
    microphoneProblemTextChat:
      'It seems there is a problem with your microphone. Please type your answers using the keyboard button.',
    microphoneProblemVoiceChat:
      'It seems there is a problem with your microphone. Please check your browser permissions and try again.',
    responseTimeoutError: 'Response taking too long. Please try again.',
    noResponseError: 'No response detected. Please try again.',
    connectionInterrupted: 'Connection interrupted. You can resend your message.',
    videoStartError: 'Failed to start video recording. Please try again.',
    videoCameraNotSupported: 'Camera is not supported. Please try with a different browser.',
    videoPauseError: 'Failed to pause video recording.',
    videoResumeError: 'Failed to resume video recording.',
    videoStopError: 'Failed to stop video recording. Please try again.',
    videoSwitchCameraError: 'Failed to switch camera. Please try again.',
    videoSendError: 'Failed to send video message.',
    welcomeBack: 'Welcome back!',
    unsupportedFileType: 'Unsupported file type. Please upload a JPG, PNG, GIF, or MP4 file.',
    fileTooLarge: 'This file is too large. The maximum size is 200 MB.',
    audioCloseError: 'Error closing audio:',
  },
  et: {
    telletDoesNotExistTitle: 'See link ei ole saadaval',
    projectDoesNotExistTitle: 'See link ei ole saadaval',
    projectDoesNotExistMessage:
      'Vabandame, kuid me ei leidnud seda uuringut. Palun kontrollige teile saadetud linki või võtke ühendust teid kutsunud isikuga.',
    maximumParticipantsTitle: 'Täname teid',
    maximumParticipantsMessage:
      'Täname teid huvi eest selles uuringus osalemise vastu.\nOleme nüüd saanud vajaliku arvu vastuseid, mistõttu ei saa me hetkel enam uusi osalejaid vastu võtta.\nHindame väga teie aega ja valmisolekut sellesse olulisse projekti panustada.\nTäname mõistva suhtumise eest.',
    somethingMissingTitle: 'See link ei ole saadaval',
    somethingMissingMessage:
      'Vabandame, kuid me ei leidnud seda uuringut. Palun kontrollige teile saadetud linki või võtke ühendust teid kutsunud isikuga.',
    projectCompletedTitle: 'Täname teid',
    projectCompletedMessage:
      'Täname teid huvi eest selles uuringus osalemise vastu.\nSee uuring on nüüd lõppenud, mistõttu ei saa me enam uusi osalejaid vastu võtta.\nHindame teie valmisolekut panustada ning täname mõistva suhtumise eest.',
    projectInDraftOrReadyStatusTitle: 'Veel ei ole avatud',
    projectInDraftOrReadyStatusMessage:
      'Täname teid huvi eest selles uuringus osalemise vastu.\nSee uuring ei ole veel osalemiseks avatud. Palun proovige hiljem uuesti või võtke ühendust teid kutsunud isikuga.',
    redirectingIn: 'Ümbersuunamine',
    seconds: 'sekundi pärast...',
    cancel: 'Tühista',
    returnToPanel: 'Tagasi paneeli',
    errorBoundaryTitle: 'Midagi läks valesti',
    errorBoundaryMessage: 'Ilmnes ootamatu viga. Jätkamiseks laadige leht uuesti.',
    errorBoundaryReloadButton: 'Laadi leht uuesti',
    messageProcessingError:
      'Oih! Tundub, et meil on probleeme teie sõnumi töötlemisega. Palun proovige see uuesti saata.',
    microphoneProblemTextChat:
      'Tundub, et teie mikrofoniga on probleem. Palun kirjutage oma vastused klaviatuurinupu abil.',
    microphoneProblemVoiceChat:
      'Tundub, et teie mikrofoniga on probleem. Palun kontrollige oma brauseri lubasid ja proovige uuesti.',
    responseTimeoutError: 'Vastus võtab liiga kaua aega. Palun proovige uuesti.',
    noResponseError: 'Vastust ei tuvastatud. Palun proovige uuesti.',
    connectionInterrupted: 'Ühendus katkes. Saate oma sõnumi uuesti saata.',
    videoStartError: 'Videosalvestuse käivitamine ebaõnnestus. Palun proovige uuesti.',
    videoCameraNotSupported: 'Kaamera ei ole toetatud. Palun proovige teise brauseriga.',
    videoPauseError: 'Videosalvestuse peatamine ebaõnnestus.',
    videoResumeError: 'Videosalvestuse jätkamine ebaõnnestus.',
    videoStopError: 'Videosalvestuse peatamine ebaõnnestus. Palun proovige uuesti.',
    videoSwitchCameraError: 'Kaamera vahetamine ebaõnnestus. Palun proovige uuesti.',
    videoSendError: 'Videosõnumi saatmine ebaõnnestus.',
    welcomeBack: 'Tere tulemast tagasi!',
    unsupportedFileType:
      'Mittetoetatud failitüüp. Palun laadige üles JPG-, PNG-, GIF- või MP4-fail.',
    fileTooLarge: 'See fail on liiga suur. Maksimaalne suurus on 200 MB.',
    audioCloseError: 'Viga heli sulgemisel:',
  },
  fi: {
    telletDoesNotExistTitle: 'Tämä linkki ei ole käytettävissä',
    projectDoesNotExistTitle: 'Tämä linkki ei ole käytettävissä',
    projectDoesNotExistMessage:
      'Valitettavasti emme löytäneet tätä tutkimusta. Tarkistakaa saammanne linkki tai ottakaa yhteyttä henkilöön, joka kutsui Teidät.',
    maximumParticipantsTitle: 'Kiitos',
    maximumParticipantsMessage:
      'Kiitos mielenkiinnostanne osallistua tähän tutkimukseen.\nOlemme nyt saaneet tarvittavan määrän vastauksia, emmekä valitettavasti voi ottaa enää uusia osallistujia tällä kertaa.\nArvostamme, että käytitte aikaanne tutustuaksenne tähän tutkimukseen ja halukkuuttanne osallistua tähän tärkeään projektiin.\nKiitos ymmärryksestänne.',
    somethingMissingTitle: 'Tämä linkki ei ole käytettävissä',
    somethingMissingMessage:
      'Valitettavasti emme löytäneet tätä tutkimusta. Tarkistakaa saammanne linkki tai ottakaa yhteyttä henkilöön, joka kutsui Teidät.',
    projectCompletedTitle: 'Kiitos',
    projectCompletedMessage:
      'Kiitos mielenkiinnostanne osallistua tähän tutkimukseen.\nTämä tutkimus on nyt päättynyt, emmekä valitettavasti voi ottaa enää uusia osallistujia.\nArvostamme halukkuuttanne osallistua ja kiitämme ymmärryksestänne.',
    projectInDraftOrReadyStatusTitle: 'Ei vielä avoinna',
    projectInDraftOrReadyStatusMessage:
      'Kiitos mielenkiinnostanne osallistua tähän tutkimukseen.\nTämä tutkimus ei ole vielä avoinna osallistumiselle. Yrittäkää myöhemmin uudelleen tai ottakaa yhteyttä henkilöön, joka kutsui Teidät.',
    redirectingIn: 'Uudelleenohjaus',
    seconds: 'sekunnin kuluttua...',
    cancel: 'Peruuta',
    returnToPanel: 'Palaa paneeliin',
    errorBoundaryTitle: 'Jotain meni pieleen',
    errorBoundaryMessage: 'Tapahtui odottamaton virhe. Lataa sivu uudelleen jatkaaksesi.',
    errorBoundaryReloadButton: 'Lataa sivu uudelleen',
    messageProcessingError:
      'Hups! Näyttää siltä, että viestisi käsittelyssä on ongelmia. Yritä lähettää se uudelleen.',
    microphoneProblemTextChat:
      'Mikrofonissasi näyttää olevan ongelma. Kirjoita vastauksesi näppäimistöpainikkeella.',
    microphoneProblemVoiceChat:
      'Mikrofonissasi näyttää olevan ongelma. Tarkista selaimen käyttöoikeudet ja yritä uudelleen.',
    responseTimeoutError: 'Vastaus kestää liian kauan. Yritä uudelleen.',
    noResponseError: 'Vastausta ei havaittu. Yritä uudelleen.',
    connectionInterrupted: 'Yhteys katkesi. Voit lähettää viestisi uudelleen.',
    videoStartError: 'Videotallennuksen aloittaminen epäonnistui. Yritä uudelleen.',
    videoCameraNotSupported: 'Kameraa ei tueta. Yritä toisella selaimella.',
    videoPauseError: 'Videotallennuksen keskeyttäminen epäonnistui.',
    videoResumeError: 'Videotallennuksen jatkaminen epäonnistui.',
    videoStopError: 'Videotallennuksen lopettaminen epäonnistui. Yritä uudelleen.',
    videoSwitchCameraError: 'Kameran vaihto epäonnistui. Yritä uudelleen.',
    videoSendError: 'Videoviestin lähettäminen epäonnistui.',
    welcomeBack: 'Tervetuloa takaisin!',
    unsupportedFileType: 'Tiedostotyyppiä ei tueta. Lataa JPG-, PNG-, GIF- tai MP4-tiedosto.',
    fileTooLarge: 'Tämä tiedosto on liian suuri. Enimmäiskoko on 200 Mt.',
    audioCloseError: 'Virhe äänen sulkemisessa:',
  },
  fr: {
    telletDoesNotExistTitle: 'Ce lien n’est pas disponible',
    projectDoesNotExistTitle: 'Ce lien n’est pas disponible',
    projectDoesNotExistMessage:
      'Nous sommes désolés, mais nous n’avons pas pu trouver cette étude. Veuillez vérifier le lien qui vous a été transmis ou contacter la personne qui vous a invité.',
    maximumParticipantsTitle: 'Merci',
    maximumParticipantsMessage:
      'Merci de votre intérêt pour cette recherche.\nNous avons désormais reçu le nombre de réponses nécessaires et ne pouvons malheureusement plus accepter de nouveaux participants pour le moment.\nNous vous remercions d’avoir pris le temps de visiter cette étude et de votre volonté de contribuer à ce projet important.\nMerci de votre compréhension.',
    somethingMissingTitle: 'Ce lien n’est pas disponible',
    somethingMissingMessage:
      'Nous sommes désolés, mais nous n’avons pas pu trouver cette étude. Veuillez vérifier le lien qui vous a été transmis ou contacter la personne qui vous a invité.',
    projectCompletedTitle: 'Merci',
    projectCompletedMessage:
      'Merci de votre intérêt pour cette recherche.\nCette étude est à présent terminée et nous ne pouvons malheureusement plus accepter de nouveaux participants.\nNous vous remercions de votre volonté de contribuer et vous remercions de votre compréhension.',
    projectInDraftOrReadyStatusTitle: 'Pas encore ouvert',
    projectInDraftOrReadyStatusMessage:
      'Merci de votre intérêt pour cette recherche.\nCette étude n’est pas encore ouverte à la participation. Veuillez réessayer ultérieurement ou contacter la personne qui vous a invité.',
    redirectingIn: 'Redirection dans',
    seconds: 'secondes...',
    cancel: 'Annuler',
    returnToPanel: 'Retour au panel',
    errorBoundaryTitle: 'Une erreur est survenue',
    errorBoundaryMessage:
      "Une erreur inattendue s'est produite. Veuillez recharger la page pour continuer.",
    errorBoundaryReloadButton: 'Recharger la page',
    messageProcessingError:
      'Oups ! Il semble que nous ayons des difficultés à traiter votre message. Veuillez essayer de le renvoyer.',
    microphoneProblemTextChat:
      "Il semble y avoir un problème avec votre microphone. Veuillez taper vos réponses à l'aide du bouton clavier.",
    microphoneProblemVoiceChat:
      'Il semble y avoir un problème avec votre microphone. Veuillez vérifier les autorisations de votre navigateur et réessayer.',
    responseTimeoutError: 'La réponse prend trop de temps. Veuillez réessayer.',
    noResponseError: 'Aucune réponse détectée. Veuillez réessayer.',
    connectionInterrupted: 'Connexion interrompue. Vous pouvez renvoyer votre message.',
    videoStartError: "Impossible de démarrer l'enregistrement vidéo. Veuillez réessayer.",
    videoCameraNotSupported:
      "La caméra n'est pas prise en charge. Veuillez essayer avec un autre navigateur.",
    videoPauseError: "Impossible de mettre en pause l'enregistrement vidéo.",
    videoResumeError: "Impossible de reprendre l'enregistrement vidéo.",
    videoStopError: "Impossible d'arrêter l'enregistrement vidéo. Veuillez réessayer.",
    videoSwitchCameraError: 'Impossible de changer de caméra. Veuillez réessayer.',
    videoSendError: "Impossible d'envoyer le message vidéo.",
    welcomeBack: 'Bon retour !',
    unsupportedFileType:
      'Type de fichier non pris en charge. Veuillez télécharger un fichier JPG, PNG, GIF ou MP4.',
    fileTooLarge: 'Ce fichier est trop volumineux. La taille maximale est de 200 Mo.',
    audioCloseError: "Erreur lors de la fermeture de l'audio :",
  },
  gl: {
    telletDoesNotExistTitle: 'Esta ligazón non está dispoñible',
    projectDoesNotExistTitle: 'Esta ligazón non está dispoñible',
    projectDoesNotExistMessage:
      'Sentímolo, pero non puidemos atopar este estudo. Por favor, comprobe a ligazón que lle facilitaron ou contacte coa persoa que lle convidou.',
    maximumParticipantsTitle: 'Grazas',
    maximumParticipantsMessage:
      'Grazas polo seu interese en participar nesta investigación.\nXa recibimos o número necesario de respostas, polo que lamentablemente non podemos aceptar máis participantes neste momento.\nAgradecemos que dedicase o seu tempo a visitar este estudo e a súa disposición para colaborar neste importante proxecto.\nGrazas pola súa comprensión.',
    somethingMissingTitle: 'Esta ligazón non está dispoñible',
    somethingMissingMessage:
      'Sentímolo, pero non puidemos atopar este estudo. Por favor, comprobe a ligazón que lle facilitaron ou contacte coa persoa que lle convidou.',
    projectCompletedTitle: 'Grazas',
    projectCompletedMessage:
      'Grazas polo seu interese en participar nesta investigación.\nEste estudo xa foi pechado, polo que lamentablemente non podemos aceptar máis participantes.\nAgradecemos a súa disposición para colaborar e grazas pola súa comprensión.',
    projectInDraftOrReadyStatusTitle: 'Aínda non está aberto',
    projectInDraftOrReadyStatusMessage:
      'Grazas polo seu interese en participar nesta investigación.\nEste estudo aínda non está aberto á participación. Por favor, inténteo de novo máis tarde ou contacte coa persoa que lle convidou.',
    redirectingIn: 'Redirixindo en',
    seconds: 'segundos...',
    cancel: 'Cancelar',
    returnToPanel: 'Volver ao panel',
    errorBoundaryTitle: 'Algo saíu mal',
    errorBoundaryMessage: 'Produciuse un erro inesperado. Recarga a páxina para continuar.',
    errorBoundaryReloadButton: 'Recargar páxina',
    messageProcessingError:
      'Vaia! Parece que temos problemas para procesar a túa mensaxe. Por favor, tenta enviala de novo.',
    microphoneProblemTextChat:
      'Parece que hai un problema co teu micrófono. Por favor, escribe as túas respostas usando o botón do teclado.',
    microphoneProblemVoiceChat:
      'Parece que hai un problema co teu micrófono. Por favor, comproba os permisos do teu navegador e téntao de novo.',
    responseTimeoutError: 'A resposta está tardando demasiado. Téntao de novo.',
    noResponseError: 'Non se detectou ningunha resposta. Téntao de novo.',
    connectionInterrupted: 'Conexión interrompida. Podes reenviar a túa mensaxe.',
    videoStartError: 'Non se puido iniciar a gravación de vídeo. Téntao de novo.',
    videoCameraNotSupported: 'A cámara non é compatible. Téntao cun navegador diferente.',
    videoPauseError: 'Non se puido pausar a gravación de vídeo.',
    videoResumeError: 'Non se puido retomar a gravación de vídeo.',
    videoStopError: 'Non se puido deter a gravación de vídeo. Téntao de novo.',
    videoSwitchCameraError: 'Non se puido cambiar de cámara. Téntao de novo.',
    videoSendError: 'Non se puido enviar a mensaxe de vídeo.',
    welcomeBack: 'Benvido de novo!',
    unsupportedFileType:
      'Tipo de ficheiro non compatible. Por favor, sobe un ficheiro JPG, PNG, GIF ou MP4.',
    fileTooLarge: 'Este ficheiro é demasiado grande. O tamaño máximo é de 200 MB.',
    audioCloseError: 'Erro ao pechar o audio:',
  },
  de: {
    telletDoesNotExistTitle: 'Dieser Link ist nicht verfügbar',
    projectDoesNotExistTitle: 'Dieser Link ist nicht verfügbar',
    projectDoesNotExistMessage:
      'Es tut uns leid, aber wir konnten diese Studie nicht finden. Bitte überprüfen Sie den erhaltenen Link oder wenden Sie sich an die Person, die Sie eingeladen hat.',
    maximumParticipantsTitle: 'Vielen Dank',
    maximumParticipantsMessage:
      'Vielen Dank für Ihr Interesse an dieser Forschung.\nWir haben inzwischen die benötigte Anzahl an Antworten erhalten und können daher leider keine weiteren Teilnehmenden mehr aufnehmen.\nWir schätzen es sehr, dass Sie sich die Zeit genommen haben, diese Studie zu besuchen und bereit waren, zu diesem wichtigen Projekt beizutragen.\nVielen Dank für Ihr Verständnis.',
    somethingMissingTitle: 'Dieser Link ist nicht verfügbar',
    somethingMissingMessage:
      'Es tut uns leid, aber wir konnten diese Studie nicht finden. Bitte überprüfen Sie den erhaltenen Link oder wenden Sie sich an die Person, die Sie eingeladen hat.',
    projectCompletedTitle: 'Vielen Dank',
    projectCompletedMessage:
      'Vielen Dank für Ihr Interesse an dieser Forschung.\nDiese Studie ist nun abgeschlossen, daher können wir leider keine weiteren Teilnehmenden mehr aufnehmen.\nWir danken Ihnen für Ihre Bereitschaft, mitzuwirken, und bitten um Ihr Verständnis.',
    projectInDraftOrReadyStatusTitle: 'Noch nicht geöffnet',
    projectInDraftOrReadyStatusMessage:
      'Vielen Dank für Ihr Interesse an dieser Forschung.\nDiese Studie ist derzeit noch nicht für die Teilnahme geöffnet. Bitte versuchen Sie es später erneut oder wenden Sie sich an die Person, die Sie eingeladen hat.',
    redirectingIn: 'Weiterleitung in',
    seconds: 'Sekunden...',
    cancel: 'Abbrechen',
    returnToPanel: 'Zurück zum Panel',
    errorBoundaryTitle: 'Etwas ist schiefgelaufen',
    errorBoundaryMessage:
      'Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu, um fortzufahren.',
    errorBoundaryReloadButton: 'Seite neu laden',
    messageProcessingError:
      'Hoppla! Anscheinend haben wir Probleme, Ihre Nachricht zu verarbeiten. Bitte versuchen Sie, sie erneut zu senden.',
    microphoneProblemTextChat:
      'Es scheint ein Problem mit Ihrem Mikrofon zu geben. Bitte tippen Sie Ihre Antworten über die Tastaturschaltfläche ein.',
    microphoneProblemVoiceChat:
      'Es scheint ein Problem mit Ihrem Mikrofon zu geben. Bitte überprüfen Sie Ihre Browser-Berechtigungen und versuchen Sie es erneut.',
    responseTimeoutError: 'Die Antwort dauert zu lange. Bitte versuchen Sie es erneut.',
    noResponseError: 'Keine Antwort erkannt. Bitte versuchen Sie es erneut.',
    connectionInterrupted: 'Verbindung unterbrochen. Sie können Ihre Nachricht erneut senden.',
    videoStartError:
      'Videoaufzeichnung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
    videoCameraNotSupported:
      'Kamera wird nicht unterstützt. Bitte versuchen Sie es mit einem anderen Browser.',
    videoPauseError: 'Videoaufzeichnung konnte nicht pausiert werden.',
    videoResumeError: 'Videoaufzeichnung konnte nicht fortgesetzt werden.',
    videoStopError:
      'Videoaufzeichnung konnte nicht gestoppt werden. Bitte versuchen Sie es erneut.',
    videoSwitchCameraError: 'Kamera konnte nicht gewechselt werden. Bitte versuchen Sie es erneut.',
    videoSendError: 'Videonachricht konnte nicht gesendet werden.',
    welcomeBack: 'Willkommen zurück!',
    unsupportedFileType:
      'Nicht unterstützter Dateityp. Bitte laden Sie eine JPG-, PNG-, GIF- oder MP4-Datei hoch.',
    fileTooLarge: 'Diese Datei ist zu groß. Die maximale Größe beträgt 200 MB.',
    audioCloseError: 'Fehler beim Schließen des Audios:',
  },
  el: {
    telletDoesNotExistTitle: 'Αυτός ο σύνδεσμος δεν είναι διαθέσιμος',
    projectDoesNotExistTitle: 'Αυτός ο σύνδεσμος δεν είναι διαθέσιμος',
    projectDoesNotExistMessage:
      'Λυπούμαστε, αλλά δεν μπορέσαμε να βρούμε αυτή τη μελέτη. Παρακαλούμε ελέγξτε τον σύνδεσμο που σας δόθηκε ή επικοινωνήστε με το άτομο που σας προσκάλεσε.',
    maximumParticipantsTitle: 'Σας ευχαριστούμε',
    maximumParticipantsMessage:
      'Σας ευχαριστούμε για το ενδιαφέρον σας να συμμετάσχετε σε αυτήν την έρευνα.\nΈχουμε πλέον λάβει τον απαιτούμενο αριθμό απαντήσεων, επομένως δυστυχώς δεν μπορούμε να δεχτούμε άλλους συμμετέχοντες αυτή τη στιγμή.\nΕκτιμούμε τον χρόνο που αφιερώσατε για να επισκεφθείτε αυτή τη μελέτη και τη διάθεσή σας να συμβάλετε σε αυτό το σημαντικό έργο.\nΣας ευχαριστούμε για την κατανόησή σας.',
    somethingMissingTitle: 'Αυτός ο σύνδεσμος δεν είναι διαθέσιμος',
    somethingMissingMessage:
      'Λυπούμαστε, αλλά δεν μπορέσαμε να βρούμε αυτή τη μελέτη. Παρακαλούμε ελέγξτε τον σύνδεσμο που σας δόθηκε ή επικοινωνήστε με το άτομο που σας προσκάλεσε.',
    projectCompletedTitle: 'Σας ευχαριστούμε',
    projectCompletedMessage:
      'Σας ευχαριστούμε για το ενδιαφέρον σας να συμμετάσχετε σε αυτήν την έρευνα.\nΑυτή η μελέτη έχει πλέον ολοκληρωθεί, επομένως δυστυχώς δεν μπορούμε να δεχτούμε άλλους συμμετέχοντες.\nΕκτιμούμε τη διάθεσή σας να συμβάλετε και σας ευχαριστούμε για την κατανόησή σας.',
    projectInDraftOrReadyStatusTitle: 'Δεν είναι ακόμη ανοιχτό',
    projectInDraftOrReadyStatusMessage:
      'Σας ευχαριστούμε για το ενδιαφέρον σας να συμμετάσχετε σε αυτήν την έρευνα.\nΑυτή η μελέτη δεν είναι ακόμη ανοιχτή για συμμετοχή. Παρακαλούμε δοκιμάστε ξανά αργότερα ή επικοινωνήστε με το άτομο που σας προσκάλεσε.',
    redirectingIn: 'Ανακατεύθυνση σε',
    seconds: 'δευτερόλεπτα...',
    cancel: 'Ακύρωση',
    returnToPanel: 'Επιστροφή στο Panel',
    errorBoundaryTitle: 'Κάτι πήγε στραβά',
    errorBoundaryMessage:
      'Παρουσιάστηκε ένα απρόσμενο σφάλμα. Παρακαλώ επαναφορτώστε τη σελίδα για να συνεχίσετε.',
    errorBoundaryReloadButton: 'Επαναφόρτωση σελίδας',
    messageProcessingError:
      'Ουπς! Φαίνεται ότι αντιμετωπίζουμε πρόβλημα με την επεξεργασία του μηνύματός σας. Παρακαλώ προσπαθήστε να το στείλετε ξανά.',
    microphoneProblemTextChat:
      'Φαίνεται ότι υπάρχει πρόβλημα με το μικρόφωνό σας. Παρακαλώ πληκτρολογήστε τις απαντήσεις σας χρησιμοποιώντας το κουμπί του πληκτρολογίου.',
    microphoneProblemVoiceChat:
      'Φαίνεται ότι υπάρχει πρόβλημα με το μικρόφωνό σας. Παρακαλώ ελέγξτε τα δικαιώματα του προγράμματος περιήγησής σας και δοκιμάστε ξανά.',
    responseTimeoutError: 'Η απόκριση καθυστερεί πολύ. Παρακαλώ δοκιμάστε ξανά.',
    noResponseError: 'Δεν εντοπίστηκε απόκριση. Παρακαλώ δοκιμάστε ξανά.',
    connectionInterrupted: 'Η σύνδεση διακόπηκε. Μπορείτε να στείλετε ξανά το μήνυμά σας.',
    videoStartError: 'Η εκκίνηση της εγγραφής βίντεο απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    videoCameraNotSupported:
      'Η κάμερα δεν υποστηρίζεται. Παρακαλώ δοκιμάστε με διαφορετικό πρόγραμμα περιήγησης.',
    videoPauseError: 'Η παύση της εγγραφής βίντεο απέτυχε.',
    videoResumeError: 'Η συνέχιση της εγγραφής βίντεο απέτυχε.',
    videoStopError: 'Η διακοπή της εγγραφής βίντεο απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    videoSwitchCameraError: 'Η αλλαγή κάμερας απέτυχε. Παρακαλώ δοκιμάστε ξανά.',
    videoSendError: 'Η αποστολή του μηνύματος βίντεο απέτυχε.',
    welcomeBack: 'Καλώς ήρθατε ξανά!',
    unsupportedFileType:
      'Μη υποστηριζόμενος τύπος αρχείου. Παρακαλώ ανεβάστε ένα αρχείο JPG, PNG, GIF ή MP4.',
    fileTooLarge: 'Αυτό το αρχείο είναι πολύ μεγάλο. Το μέγιστο μέγεθος είναι 200 MB.',
    audioCloseError: 'Σφάλμα κατά το κλείσιμο του ήχου:',
  },
  iw: {
    telletDoesNotExistTitle: 'קישור זה אינו זמין',
    projectDoesNotExistTitle: 'קישור זה אינו זמין',
    projectDoesNotExistMessage:
      'אנו מצטערים, אך לא הצלחנו למצוא את המחקר. נא לבדוק את הקישור שקיבלת או לפנות לאדם שהזמין אותך.',
    maximumParticipantsTitle: 'תודה',
    maximumParticipantsMessage:
      'תודה על התעניינותך בהשתתפות במחקר זה.\nהגענו למספר התשובות הנדרש, ולכן לצערנו איננו יכולים לקבל משתתפים נוספים בשלב זה.\nאנו מעריכים את הזמן שהקדשת ואת נכונותך לתרום לפרויקט חשוב זה.\nתודה על ההבנה.',
    somethingMissingTitle: 'קישור זה אינו זמין',
    somethingMissingMessage:
      'אנו מצטערים, אך לא הצלחנו למצוא את המחקר. נא לבדוק את הקישור שקיבלת או לפנות לאדם שהזמין אותך.',
    projectCompletedTitle: 'תודה',
    projectCompletedMessage:
      'תודה על התעניינותך בהשתתפות במחקר זה.\nהמחקר נסגר כעת, ולכן לצערנו איננו יכולים לקבל משתתפים נוספים.\nאנו מעריכים את נכונותך לתרום, ותודה על ההבנה.',
    projectInDraftOrReadyStatusTitle: 'עדיין לא פתוח',
    projectInDraftOrReadyStatusMessage:
      'תודה על התעניינותך בהשתתפות במחקר זה.\nהמחקר טרם נפתח להרשמה. נא לנסות שוב מאוחר יותר, או לפנות לאדם שהזמין אותך.',
    redirectingIn: 'הפניה בעוד',
    seconds: 'שניות...',
    cancel: 'ביטול',
    returnToPanel: 'חזרה לפאנל',
    errorBoundaryTitle: 'משהו השתבש',
    errorBoundaryMessage: 'אירעה שגיאה לא צפויה. אנא טען מחדש את הדף כדי להמשיך.',
    errorBoundaryReloadButton: 'טען דף מחדש',
    messageProcessingError: 'אופס! נראה שאנחנו מתקשים לעבד את ההודעה שלך. אנא נסה לשלוח אותה שוב.',
    microphoneProblemTextChat:
      'נראה שיש בעיה עם המיקרופון שלך. אנא הקלד את תשובותיך באמצעות לחצן המקלדת.',
    microphoneProblemVoiceChat:
      'נראה שיש בעיה עם המיקרופון שלך. אנא בדוק את הרשאות הדפדפן שלך ונסה שוב.',
    responseTimeoutError: 'התגובה אורכת זמן רב מדי. אנא נסה שוב.',
    noResponseError: 'לא זוהתה תגובה. אנא נסה שוב.',
    connectionInterrupted: 'החיבור נקטע. תוכל לשלוח את ההודעה שלך שוב.',
    videoStartError: 'הפעלת הקלטת הווידאו נכשלה. אנא נסה שוב.',
    videoCameraNotSupported: 'המצלמה אינה נתמכת. אנא נסה עם דפדפן אחר.',
    videoPauseError: 'השהיית הקלטת הווידאו נכשלה.',
    videoResumeError: 'חידוש הקלטת הווידאו נכשל.',
    videoStopError: 'עצירת הקלטת הווידאו נכשלה. אנא נסה שוב.',
    videoSwitchCameraError: 'החלפת המצלמה נכשלה. אנא נסה שוב.',
    videoSendError: 'שליחת הודעת הווידאו נכשלה.',
    welcomeBack: 'ברוך שובך!',
    unsupportedFileType: 'סוג קובץ לא נתמך. אנא העלה קובץ JPG, PNG, GIF או MP4.',
    fileTooLarge: 'הקובץ גדול מדי. הגודל המרבי הוא 200 MB.',
    audioCloseError: 'שגיאה בסגירת האודיו:',
  },
  hi: {
    telletDoesNotExistTitle: 'यह लिंक उपलब्ध नहीं है',
    projectDoesNotExistTitle: 'यह लिंक उपलब्ध नहीं है',
    projectDoesNotExistMessage:
      'हमें खेद है, लेकिन हम इस अध्ययन को नहीं ढूंढ सके। कृपया आपको दिया गया लिंक जांचें, या आमंत्रण भेजने वाले व्यक्ति से संपर्क करें।',
    maximumParticipantsTitle: 'धन्यवाद',
    maximumParticipantsMessage:
      'इस शोध में भाग लेने में आपकी रुचि के लिए धन्यवाद।\nहम आवश्यक प्रतिक्रियाएँ प्राप्त कर चुके हैं, इसलिए दुर्भाग्यवश इस समय हम और प्रतिभागियों को स्वीकार नहीं कर सकते।\nइस अध्ययन पर आने और इस महत्वपूर्ण परियोजना में योगदान देने की आपकी इच्छा के लिए हम आपके आभारी हैं।\nआपकी समझदारी के लिए धन्यवाद।',
    somethingMissingTitle: 'यह लिंक उपलब्ध नहीं है',
    somethingMissingMessage:
      'हमें खेद है, लेकिन हम इस अध्ययन को नहीं ढूंढ सके। कृपया आपको दिया गया लिंक जांचें, या आमंत्रण भेजने वाले व्यक्ति से संपर्क करें।',
    projectCompletedTitle: 'धन्यवाद',
    projectCompletedMessage:
      'इस शोध में भाग लेने में आपकी रुचि के लिए धन्यवाद।\nयह अध्ययन अब बंद हो चुका है, इसलिए दुर्भाग्यवश हम और प्रतिभागियों को स्वीकार नहीं कर सकते।\nआपकी योगदान देने की इच्छा के लिए हम आपके आभारी हैं, और आपकी समझदारी के लिए धन्यवाद।',
    projectInDraftOrReadyStatusTitle: 'अभी खुला नहीं है',
    projectInDraftOrReadyStatusMessage:
      'इस शोध में भाग लेने में आपकी रुचि के लिए धन्यवाद।\nयह अध्ययन अभी प्रतिभागियों के लिए खुला नहीं है। कृपया बाद में पुनः प्रयास करें, या आमंत्रण भेजने वाले व्यक्ति से संपर्क करें।',
    redirectingIn: 'रीडायरेक्ट हो रहा है',
    seconds: 'सेकंड में...',
    cancel: 'रद्द करें',
    returnToPanel: 'पैनल पर वापस जाएँ',
    errorBoundaryTitle: 'कुछ गलत हो गया',
    errorBoundaryMessage: 'एक अप्रत्याशित त्रुटि हुई। जारी रखने के लिए कृपया पेज को पुनः लोड करें।',
    errorBoundaryReloadButton: 'पेज पुनः लोड करें',
    messageProcessingError:
      'अरे! लगता है हमें आपका संदेश प्रोसेस करने में समस्या हो रही है। कृपया इसे फिर से भेजने का प्रयास करें।',
    microphoneProblemTextChat:
      'ऐसा लगता है कि आपके माइक्रोफ़ोन में समस्या है। कृपया कीबोर्ड बटन का उपयोग करके अपने उत्तर टाइप करें।',
    microphoneProblemVoiceChat:
      'ऐसा लगता है कि आपके माइक्रोफ़ोन में समस्या है। कृपया अपने ब्राउज़र की अनुमतियाँ जाँचें और पुनः प्रयास करें।',
    responseTimeoutError: 'प्रतिक्रिया में बहुत अधिक समय लग रहा है। कृपया पुनः प्रयास करें।',
    noResponseError: 'कोई प्रतिक्रिया नहीं मिली। कृपया पुनः प्रयास करें।',
    connectionInterrupted: 'कनेक्शन बाधित हुआ। आप अपना संदेश फिर से भेज सकते हैं।',
    videoStartError: 'वीडियो रिकॉर्डिंग शुरू करने में विफल। कृपया पुनः प्रयास करें।',
    videoCameraNotSupported: 'कैमरा समर्थित नहीं है। कृपया किसी अन्य ब्राउज़र से प्रयास करें।',
    videoPauseError: 'वीडियो रिकॉर्डिंग रोकने में विफल।',
    videoResumeError: 'वीडियो रिकॉर्डिंग फिर से शुरू करने में विफल।',
    videoStopError: 'वीडियो रिकॉर्डिंग बंद करने में विफल। कृपया पुनः प्रयास करें।',
    videoSwitchCameraError: 'कैमरा बदलने में विफल। कृपया पुनः प्रयास करें।',
    videoSendError: 'वीडियो संदेश भेजने में विफल।',
    welcomeBack: 'वापसी पर स्वागत है!',
    unsupportedFileType: 'असमर्थित फ़ाइल प्रकार। कृपया JPG, PNG, GIF, या MP4 फ़ाइल अपलोड करें।',
    fileTooLarge: 'यह फ़ाइल बहुत बड़ी है। अधिकतम आकार 200 MB है।',
    audioCloseError: 'ऑडियो बंद करने में त्रुटि:',
  },
  hu: {
    telletDoesNotExistTitle: 'Ez a hivatkozás nem elérhető',
    projectDoesNotExistTitle: 'Ez a hivatkozás nem elérhető',
    projectDoesNotExistMessage:
      'Sajnáljuk, de ezt a tanulmányt nem találtuk. Kérjük, ellenőrizze a kapott hivatkozást, vagy vegye fel a kapcsolatot a meghívó személlyel.',
    maximumParticipantsTitle: 'Köszönjük',
    maximumParticipantsMessage:
      'Köszönjük, hogy érdeklődik a kutatásban való részvétel iránt.\nA szükséges válaszok száma már beérkezett, ezért sajnos jelenleg nem tudunk további résztvevőket fogadni.\nNagyra értékeljük, hogy időt szánt a tanulmány meglátogatására, és hogy hozzájárulásával segíteni kívánta ezt a fontos projektet.\nKöszönjük megértését.',
    somethingMissingTitle: 'Ez a hivatkozás nem elérhető',
    somethingMissingMessage:
      'Sajnáljuk, de ezt a tanulmányt nem találtuk. Kérjük, ellenőrizze a kapott hivatkozást, vagy vegye fel a kapcsolatot a meghívó személlyel.',
    projectCompletedTitle: 'Köszönjük',
    projectCompletedMessage:
      'Köszönjük, hogy érdeklődik a kutatásban való részvétel iránt.\nEz a tanulmány már lezárult, ezért sajnos nem tudunk további résztvevőket fogadni.\nNagyra értékeljük hozzájárulási szándékát, és köszönjük megértését.',
    projectInDraftOrReadyStatusTitle: 'Még nem elérhető',
    projectInDraftOrReadyStatusMessage:
      'Köszönjük, hogy érdeklődik a kutatásban való részvétel iránt.\nEz a tanulmány még nem nyílt meg a részvételre. Kérjük, próbálja meg később, vagy vegye fel a kapcsolatot a meghívó személlyel.',
    redirectingIn: 'Átirányítás',
    seconds: 'másodperc múlva...',
    cancel: 'Mégse',
    returnToPanel: 'Vissza a panelhez',
    errorBoundaryTitle: 'Valami hiba történt',
    errorBoundaryMessage: 'Váratlan hiba történt. A folytatáshoz töltse be újra az oldalt.',
    errorBoundaryReloadButton: 'Oldal újratöltése',
    messageProcessingError:
      'Hoppá! Úgy tűnik, problémába ütköztünk az üzenete feldolgozása közben. Kérjük, próbálja meg újraküldeni.',
    microphoneProblemTextChat:
      'Úgy tűnik, probléma van a mikrofonjával. Kérjük, gépelje be a válaszait a billentyűzet gomb segítségével.',
    microphoneProblemVoiceChat:
      'Úgy tűnik, probléma van a mikrofonjával. Kérjük, ellenőrizze a böngésző engedélyeit, és próbálja újra.',
    responseTimeoutError: 'A válasz túl sokáig tart. Kérjük, próbálja újra.',
    noResponseError: 'Nem érkezett válasz. Kérjük, próbálja újra.',
    connectionInterrupted: 'A kapcsolat megszakadt. Újraküldheti az üzenetét.',
    videoStartError: 'A videofelvétel indítása nem sikerült. Kérjük, próbálja újra.',
    videoCameraNotSupported: 'A kamera nem támogatott. Kérjük, próbálja egy másik böngészővel.',
    videoPauseError: 'A videofelvétel szüneteltetése nem sikerült.',
    videoResumeError: 'A videofelvétel folytatása nem sikerült.',
    videoStopError: 'A videofelvétel leállítása nem sikerült. Kérjük, próbálja újra.',
    videoSwitchCameraError: 'A kameraváltás nem sikerült. Kérjük, próbálja újra.',
    videoSendError: 'A videoüzenet küldése nem sikerült.',
    welcomeBack: 'Üdvözöljük újra!',
    unsupportedFileType:
      'Nem támogatott fájltípus. Kérjük, töltsön fel JPG, PNG, GIF vagy MP4 fájlt.',
    fileTooLarge: 'Ez a fájl túl nagy. A maximális méret 200 MB.',
    audioCloseError: 'Hiba a hang bezárásakor:',
  },
  is: {
    telletDoesNotExistTitle: 'Þessi tengill er ekki tiltækur',
    projectDoesNotExistTitle: 'Þessi tengill er ekki tiltækur',
    projectDoesNotExistMessage:
      'Því miður fundum við ekki þessa rannsókn. Vinsamlegast athugaðu tengilinn sem þú fékkst eða hafðu samband við þann sem bauð þér að taka þátt.',
    maximumParticipantsTitle: 'Takk fyrir',
    maximumParticipantsMessage:
      'Takk fyrir áhugann á að taka þátt í þessari rannsókn.\nVið höfum nú fengið þann fjölda svara sem við þurfum og getum því miður ekki tekið við fleiri þátttakendum að svo stöddu.\nVið kunnum að meta að þér hafi gefist tími til að heimsækja þessa rannsókn og sýna vilja til að leggja verkefninu lið.\nTakk fyrir skilninginn.',
    somethingMissingTitle: 'Þessi tengill er ekki tiltækur',
    somethingMissingMessage:
      'Því miður fundum við ekki þessa rannsókn. Vinsamlegast athugaðu tengilinn sem þú fékkst eða hafðu samband við þann sem bauð þér að taka þátt.',
    projectCompletedTitle: 'Takk fyrir',
    projectCompletedMessage:
      'Takk fyrir áhugann á að taka þátt í þessari rannsókn.\nÞessari rannsókn er nú lokið og því getum við því miður ekki tekið við fleiri þátttakendum.\nVið kunnum að meta vilja þinn til að leggja verkefninu lið og þökkum skilninginn.',
    projectInDraftOrReadyStatusTitle: 'Ekki opið enn',
    projectInDraftOrReadyStatusMessage:
      'Takk fyrir áhugann á að taka þátt í þessari rannsókn.\nÞessi rannsókn er ekki opin fyrir þátttöku ennþá. Vinsamlegast reyndu síðar eða hafðu samband við þann sem bauð þér að taka þátt.',
    redirectingIn: 'Áframsent eftir',
    seconds: 'sekúndur...',
    cancel: 'Hætta við',
    returnToPanel: 'Fara aftur á Panel',
    errorBoundaryTitle: 'Eitthvað fór úrskeiðis',
    errorBoundaryMessage:
      'Óvænt villa kom upp. Vinsamlegast endurhladið síðuna til að halda áfram.',
    errorBoundaryReloadButton: 'Endurhlaða síðu',
    messageProcessingError:
      'Úbbs! Það virðist sem við eigum í vandræðum með að vinna úr skilaboðunum þínum. Vinsamlegast reyndu að senda þau aftur.',
    microphoneProblemTextChat:
      'Það virðist vera vandamál með hljóðnemann þinn. Vinsamlegast sláðu inn svörin þín með lyklaborðshnappnum.',
    microphoneProblemVoiceChat:
      'Það virðist vera vandamál með hljóðnemann þinn. Vinsamlegast athugaðu heimildir vafrans og reyndu aftur.',
    responseTimeoutError: 'Svarið tekur of langan tíma. Vinsamlegast reyndu aftur.',
    noResponseError: 'Ekkert svar greint. Vinsamlegast reyndu aftur.',
    connectionInterrupted: 'Tenging rofin. Þú getur sent skilaboðin þín aftur.',
    videoStartError: 'Ekki tókst að hefja myndbandsupptöku. Vinsamlegast reyndu aftur.',
    videoCameraNotSupported: 'Myndavélin er ekki studd. Vinsamlegast reyndu með öðrum vafra.',
    videoPauseError: 'Ekki tókst að gera hlé á myndbandsupptöku.',
    videoResumeError: 'Ekki tókst að halda áfram myndbandsupptöku.',
    videoStopError: 'Ekki tókst að stöðva myndbandsupptöku. Vinsamlegast reyndu aftur.',
    videoSwitchCameraError: 'Ekki tókst að skipta um myndavél. Vinsamlegast reyndu aftur.',
    videoSendError: 'Ekki tókst að senda myndbandsskilaboð.',
    welcomeBack: 'Velkomin aftur!',
    unsupportedFileType:
      'Óstudd skráartegund. Vinsamlegast hladdu upp JPG-, PNG-, GIF- eða MP4-skrá.',
    fileTooLarge: 'Þessi skrá er of stór. Hámarksstærð er 200 MB.',
    audioCloseError: 'Villa við að loka hljóði:',
  },
  it: {
    telletDoesNotExistTitle: 'Questo link non è disponibile',
    projectDoesNotExistTitle: 'Questo link non è disponibile',
    projectDoesNotExistMessage:
      'Ci dispiace, ma non siamo riusciti a trovare questo studio. La preghiamo di verificare il link che Le è stato fornito o di contattare la persona che La ha invitata.',
    maximumParticipantsTitle: 'Grazie',
    maximumParticipantsMessage:
      'Grazie per il Suo interesse a partecipare a questa ricerca.\nAbbiamo già ricevuto il numero necessario di risposte, quindi al momento non possiamo accettare ulteriori partecipanti.\nApprezziamo il tempo che ha dedicato a visitare questo studio e la Sua disponibilità a contribuire a questo importante progetto.\nLa ringraziamo per la comprensione.',
    somethingMissingTitle: 'Questo link non è disponibile',
    somethingMissingMessage:
      'Ci dispiace, ma non siamo riusciti a trovare questo studio. La preghiamo di verificare il link che Le è stato fornito o di contattare la persona che La ha invitata.',
    projectCompletedTitle: 'Grazie',
    projectCompletedMessage:
      'Grazie per il Suo interesse a partecipare a questa ricerca.\nQuesto studio è stato chiuso, quindi al momento non possiamo accettare ulteriori partecipanti.\nApprezziamo la Sua disponibilità a contribuire e La ringraziamo per la comprensione.',
    projectInDraftOrReadyStatusTitle: 'Non ancora aperto',
    projectInDraftOrReadyStatusMessage:
      'Grazie per il Suo interesse a partecipare a questa ricerca.\nQuesto studio non è ancora aperto alla partecipazione. La invitiamo a riprovare più tardi o a contattare la persona che La ha invitata.',
    redirectingIn: 'Reindirizzamento tra',
    seconds: 'secondi...',
    cancel: 'Annulla',
    returnToPanel: 'Torna al Panel',
    errorBoundaryTitle: 'Qualcosa è andato storto',
    errorBoundaryMessage:
      'Si è verificato un errore imprevisto. Ricarica la pagina per continuare.',
    errorBoundaryReloadButton: 'Ricarica pagina',
    messageProcessingError:
      'Ops! Sembra che stiamo avendo problemi a elaborare il tuo messaggio. Prova a inviarlo di nuovo.',
    microphoneProblemTextChat:
      'Sembra esserci un problema con il tuo microfono. Digita le tue risposte utilizzando il pulsante della tastiera.',
    microphoneProblemVoiceChat:
      'Sembra esserci un problema con il tuo microfono. Controlla i permessi del browser e riprova.',
    responseTimeoutError: 'La risposta sta impiegando troppo tempo. Riprova.',
    noResponseError: 'Nessuna risposta rilevata. Riprova.',
    connectionInterrupted: 'Connessione interrotta. Puoi inviare di nuovo il tuo messaggio.',
    videoStartError: 'Impossibile avviare la registrazione video. Riprova.',
    videoCameraNotSupported: 'La fotocamera non è supportata. Prova con un browser diverso.',
    videoPauseError: 'Impossibile mettere in pausa la registrazione video.',
    videoResumeError: 'Impossibile riprendere la registrazione video.',
    videoStopError: 'Impossibile interrompere la registrazione video. Riprova.',
    videoSwitchCameraError: 'Impossibile cambiare fotocamera. Riprova.',
    videoSendError: 'Impossibile inviare il messaggio video.',
    welcomeBack: 'Bentornato!',
    unsupportedFileType: 'Tipo di file non supportato. Carica un file JPG, PNG, GIF o MP4.',
    fileTooLarge: 'Questo file è troppo grande. La dimensione massima è di 200 MB.',
    audioCloseError: "Errore nella chiusura dell'audio:",
  },
  ja: {
    telletDoesNotExistTitle: 'このリンクはご利用いただけません',
    projectDoesNotExistTitle: 'このリンクはご利用いただけません',
    projectDoesNotExistMessage:
      '申し訳ございませんが、該当する調査が見つかりませんでした。ご案内されたリンクをご確認いただくか、ご招待者様までご連絡ください。',
    maximumParticipantsTitle: 'ありがとうございます',
    maximumParticipantsMessage:
      '本調査へのご関心をお寄せいただき、誠にありがとうございます。\n必要な回答数に達したため、これ以上のご参加は受け付けておりません。\nご訪問いただき、ご協力の意思をお示しいただきましたことに心より感謝申し上げます。\nご理解のほど、よろしくお願いいたします。',
    somethingMissingTitle: 'このリンクはご利用いただけません',
    somethingMissingMessage:
      '申し訳ございませんが、該当する調査が見つかりませんでした。ご案内されたリンクをご確認いただくか、ご招待者様までご連絡ください。',
    projectCompletedTitle: 'ありがとうございます',
    projectCompletedMessage:
      '本調査へのご関心をお寄せいただき、誠にありがとうございます。\nこの調査はすでに終了しており、これ以上のご参加は受け付けておりません。\nご協力の意思をお示しいただき、誠にありがとうございました。ご理解のほど、よろしくお願いいたします。',
    projectInDraftOrReadyStatusTitle: 'まだ受付を開始しておりません',
    projectInDraftOrReadyStatusMessage:
      '本調査へのご関心をお寄せいただき、誠にありがとうございます。\nこの調査はまだご参加いただけません。お手数ですが、後ほど再度お試しいただくか、ご招待者様までご連絡ください。',
    redirectingIn: 'リダイレクトまで',
    seconds: '秒...',
    cancel: 'キャンセル',
    returnToPanel: 'パネルに戻る',
    errorBoundaryTitle: '問題が発生しました',
    errorBoundaryMessage:
      '予期しないエラーが発生しました。続行するにはページを再読み込みしてください。',
    errorBoundaryReloadButton: 'ページを再読み込み',
    messageProcessingError:
      'おっと！メッセージの処理に問題が発生しているようです。もう一度送信してみてください。',
    microphoneProblemTextChat:
      'マイクに問題があるようです。キーボードボタンを使って回答を入力してください。',
    microphoneProblemVoiceChat:
      'マイクに問題があるようです。ブラウザの権限を確認して、もう一度お試しください。',
    responseTimeoutError: '応答に時間がかかりすぎています。もう一度お試しください。',
    noResponseError: '応答が検出されませんでした。もう一度お試しください。',
    connectionInterrupted: '接続が中断されました。メッセージを再送信できます。',
    videoStartError: 'ビデオ録画の開始に失敗しました。もう一度お試しください。',
    videoCameraNotSupported: 'カメラはサポートされていません。別のブラウザでお試しください。',
    videoPauseError: 'ビデオ録画の一時停止に失敗しました。',
    videoResumeError: 'ビデオ録画の再開に失敗しました。',
    videoStopError: 'ビデオ録画の停止に失敗しました。もう一度お試しください。',
    videoSwitchCameraError: 'カメラの切り替えに失敗しました。もう一度お試しください。',
    videoSendError: 'ビデオメッセージの送信に失敗しました。',
    welcomeBack: 'おかえりなさい！',
    unsupportedFileType:
      'サポートされていないファイル形式です。JPG、PNG、GIF、またはMP4ファイルをアップロードしてください。',
    fileTooLarge: 'このファイルは大きすぎます。最大サイズは200 MBです。',
    audioCloseError: 'オーディオを閉じる際のエラー：',
  },
  ka: {
    telletDoesNotExistTitle: 'ბმული მიუწვდომელია',
    projectDoesNotExistTitle: 'ბმული მიუწვდომელია',
    projectDoesNotExistMessage:
      'ბოდიშს გიხდით, ვერ ვპოულობთ ამ კვლევას. გთხოვთ, გადაამოწმეთ მოცემული ბმული ან დაუკავშირდით იმ პირს, ვინც მოგიწვიათ.',
    maximumParticipantsTitle: 'გმადლობთ',
    maximumParticipantsMessage:
      'გმადლობთ, რომ დაინტერესდით ამ კვლევაში მონაწილეობით.\nჩვენ უკვე მივიღეთ საჭირო რაოდენობის პასუხები, ამიტომ ამ ეტაპზე, სამწუხაროდ, ვეღარ ვიღებთ ახალ მონაწილეებს.\nვაფასებთ, რომ დრო დაუთმეთ ამ კვლევას და მზად იყავით მნიშვნელოვანი პროექტის მხარდაჭერისთვის.\nგმადლობთ გაგებისთვის.',
    somethingMissingTitle: 'ბმული მიუწვდომელია',
    somethingMissingMessage:
      'ბოდიშს გიხდით, ვერ ვპოულობთ ამ კვლევას. გთხოვთ, გადაამოწმეთ მოცემული ბმული ან დაუკავშირდით იმ პირს, ვინც მოგიწვიათ.',
    projectCompletedTitle: 'გმადლობთ',
    projectCompletedMessage:
      'გმადლობთ, რომ დაინტერესდით ამ კვლევაში მონაწილეობით.\nეს კვლევა უკვე დასრულებულია, ამიტომ, სამწუხაროდ, ვეღარ ვიღებთ ახალ მონაწილეებს.\nვაფასებთ თქვენს მზადყოფნას მონაწილეობისათვის და გმადლობთ გაგებისთვის.',
    projectInDraftOrReadyStatusTitle: 'ჯერ არ გახსნილა',
    projectInDraftOrReadyStatusMessage:
      'გმადლობთ, რომ დაინტერესდით ამ კვლევაში მონაწილეობით.\nეს კვლევა ჯერ არ არის გახსნილი მონაწილეობისათვის. გთხოვთ, სცადეთ მოგვიანებით ან დაუკავშირდით იმ პირს, ვინც მოგიწვიათ.',
    redirectingIn: 'გადამისამართება',
    seconds: 'წამში...',
    cancel: 'გაუქმება',
    returnToPanel: 'დაბრუნება პანელზე',
    errorBoundaryTitle: 'რაღაც არასწორად წავიდა',
    errorBoundaryMessage: 'მოხდა მოულოდნელი შეცდომა. გასაგრძელებლად გთხოვთ განაახლოთ გვერდი.',
    errorBoundaryReloadButton: 'გვერდის განახლება',
    messageProcessingError:
      'ოპა! როგორც ჩანს, თქვენი შეტყობინების დამუშავებასთან დაკავშირებით პრობლემა გვაქვს. გთხოვთ, სცადოთ ხელახლა გაგზავნა.',
    microphoneProblemTextChat:
      'როგორც ჩანს, თქვენს მიკროფონთან პრობლემაა. გთხოვთ, აკრიფოთ თქვენი პასუხები კლავიატურის ღილაკის გამოყენებით.',
    microphoneProblemVoiceChat:
      'როგორც ჩანს, თქვენს მიკროფონთან პრობლემაა. გთხოვთ, შეამოწმოთ თქვენი ბრაუზერის ნებართვები და სცადოთ ხელახლა.',
    responseTimeoutError: 'პასუხი ძალიან დიდხანს გრძელდება. გთხოვთ, სცადოთ ხელახლა.',
    noResponseError: 'პასუხი ვერ მოიძებნა. გთხოვთ, სცადოთ ხელახლა.',
    connectionInterrupted: 'კავშირი შეწყდა. შეგიძლიათ ხელახლა გაგზავნოთ თქვენი შეტყობინება.',
    videoStartError: 'ვიდეო ჩაწერის დაწყება ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა.',
    videoCameraNotSupported: 'კამერა არ არის მხარდაჭერილი. გთხოვთ, სცადოთ სხვა ბრაუზერით.',
    videoPauseError: 'ვიდეო ჩაწერის შეჩერება ვერ მოხერხდა.',
    videoResumeError: 'ვიდეო ჩაწერის გაგრძელება ვერ მოხერხდა.',
    videoStopError: 'ვიდეო ჩაწერის შეწყვეტა ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა.',
    videoSwitchCameraError: 'კამერის გადართვა ვერ მოხერხდა. გთხოვთ, სცადოთ ხელახლა.',
    videoSendError: 'ვიდეო შეტყობინების გაგზავნა ვერ მოხერხდა.',
    welcomeBack: 'მოგესალმებით ისევ!',
    unsupportedFileType: 'მხარდაუჭერელი ფაილის ტიპი. გთხოვთ, ატვირთოთ JPG, PNG, GIF ან MP4 ფაილი.',
    fileTooLarge: 'ეს ფაილი ძალიან დიდია. მაქსიმალური ზომაა 200 MB.',
    audioCloseError: 'შეცდომა აუდიოს დახურვისას:',
  },
  kn: {
    telletDoesNotExistTitle: 'ಈ ಲಿಂಕ್ ಲಭ್ಯವಿಲ್ಲ',
    projectDoesNotExistTitle: 'ಈ ಲಿಂಕ್ ಲಭ್ಯವಿಲ್ಲ',
    projectDoesNotExistMessage:
      'ಕ್ಷಮಿಸಿ, ನಾವು ಈ ಅಧ್ಯಯನವನ್ನು ಕಂಡುಹಿಡಿಯಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ನಿಮಗೆ ನೀಡಲಾದ ಲಿಂಕ್ ಪರಿಶೀಲಿಸಿ ಅಥವಾ ನಿಮ್ಮನ್ನು ಆಹ್ವಾನಿಸಿದ ವ್ಯಕ್ತಿಯನ್ನು ಸಂಪರ್ಕಿಸಿ.',
    maximumParticipantsTitle: 'ಧನ್ಯವಾದಗಳು',
    maximumParticipantsMessage:
      'ಈ ಸಂಶೋಧನೆಯಲ್ಲಿ ಭಾಗವಹಿಸಲು ತೋರಿಸಿದ ಆಸಕ್ತಿಗೆ ಧನ್ಯವಾದಗಳು.\nಈಗಾಗಲೇ ಅಗತ್ಯವಿರುವ ಪ್ರತಿಕ್ರಿಯೆಗಳ ಸಂಖ್ಯೆಯನ್ನು ನಾವು ಸ್ವೀಕರಿಸಿದ್ದೇವೆ, ಆದ್ದರಿಂದ ದುರದೃಷ್ಟವಶಾತ್, ಈ ಸಮಯದಲ್ಲಿ ಇನ್ನಷ್ಟು ಭಾಗವಹಿಸುವವರನ್ನು ಸ್ವೀಕರಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ.\nಈ ಮಹತ್ವದ ಯೋಜನೆಗೆ ನಿಮ್ಮ ಸಮಯವನ್ನು ಮೀಸಲಿಟ್ಟಿದ್ದಕ್ಕಾಗಿ ಮತ್ತು ಸಹಕರಿಸುವ ಇಚ್ಛೆಗೆ ನಾವು ಕೃತಜ್ಞರಾಗಿದ್ದೇವೆ.\nನಿಮ್ಮ ಸಹಕಾರಕ್ಕೆ ಧನ್ಯವಾದಗಳು.',
    somethingMissingTitle: 'ಈ ಲಿಂಕ್ ಲಭ್ಯವಿಲ್ಲ',
    somethingMissingMessage:
      'ಕ್ಷಮಿಸಿ, ನಾವು ಈ ಅಧ್ಯಯನವನ್ನು ಕಂಡುಹಿಡಿಯಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ನಿಮಗೆ ನೀಡಲಾದ ಲಿಂಕ್ ಪರಿಶೀಲಿಸಿ ಅಥವಾ ನಿಮ್ಮನ್ನು ಆಹ್ವಾನಿಸಿದ ವ್ಯಕ್ತಿಯನ್ನು ಸಂಪರ್ಕಿಸಿ.',
    projectCompletedTitle: 'ಧನ್ಯವಾದಗಳು',
    projectCompletedMessage:
      'ಈ ಸಂಶೋಧನೆಯಲ್ಲಿ ಭಾಗವಹಿಸಲು ತೋರಿಸಿದ ಆಸಕ್ತಿಗೆ ಧನ್ಯವಾದಗಳು.\nಈ ಅಧ್ಯಯನವು ಈಗ ಮುಕ್ತಾಯಗೊಂಡಿದೆ, ಆದ್ದರಿಂದ ದುರದೃಷ್ಟವಶಾತ್, ಇನ್ನಷ್ಟು ಭಾಗವಹಿಸುವವರನ್ನು ಸ್ವೀಕರಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ.\nನಿಮ್ಮ ಸಹಕಾರದ ಮನೋಭಾವಕ್ಕೆ ನಾವು ಕೃತಜ್ಞರಾಗಿದ್ದೇವೆ ಮತ್ತು ನಿಮ್ಮ ಸಹಕಾರಕ್ಕೆ ಧನ್ಯವಾದಗಳು.',
    projectInDraftOrReadyStatusTitle: 'ಇನ್ನೂ ತೆರೆಯಲಾಗಿಲ್ಲ',
    projectInDraftOrReadyStatusMessage:
      'ಈ ಸಂಶೋಧನೆಯಲ್ಲಿ ಭಾಗವಹಿಸಲು ತೋರಿಸಿದ ಆಸಕ್ತಿಗೆ ಧನ್ಯವಾದಗಳು.\nಈ ಅಧ್ಯಯನವು ಇನ್ನೂ ಭಾಗವಹಿಸುವವರಿಗೆ ತೆರೆಯಲಾಗಿಲ್ಲ. ದಯವಿಟ್ಟು ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ ಅಥವಾ ನಿಮ್ಮನ್ನು ಆಹ್ವಾನಿಸಿದ ವ್ಯಕ್ತಿಯನ್ನು ಸಂಪರ್ಕಿಸಿ.',
    redirectingIn: 'ಮರುನಿರ್ದೇಶಿಸಲಾಗುತ್ತಿದೆ',
    seconds: 'ಸೆಕೆಂಡುಗಳಲ್ಲಿ...',
    cancel: 'ರದ್ದುಮಾಡಿ',
    returnToPanel: 'ಪ್ಯಾನೆಲ್‌ಗೆ ಹಿಂತಿರುಗಿ',
    errorBoundaryTitle: 'ಏನೋ ತಪ್ಪಾಗಿದೆ',
    errorBoundaryMessage: 'ಅನಿರೀಕ್ಷಿತ ದೋಷ ಸಂಭವಿಸಿದೆ. ಮುಂದುವರಿಸಲು ದಯವಿಟ್ಟು ಪುಟವನ್ನು ಮರುಲೋಡ್ ಮಾಡಿ.',
    errorBoundaryReloadButton: 'ಪುಟವನ್ನು ಮರುಲೋಡ್ ಮಾಡಿ',
    messageProcessingError:
      'ಅಯ್ಯೋ! ನಿಮ್ಮ ಸಂದೇಶವನ್ನು ಪ್ರಕ್ರಿಯೆಗೊಳಿಸುವಲ್ಲಿ ನಮಗೆ ತೊಂದರೆಯಾಗುತ್ತಿದೆ. ದಯವಿಟ್ಟು ಅದನ್ನು ಮತ್ತೆ ಕಳುಹಿಸಲು ಪ್ರಯತ್ನಿಸಿ.',
    microphoneProblemTextChat:
      'ನಿಮ್ಮ ಮೈಕ್ರೊಫೋನ್‌ನಲ್ಲಿ ಸಮಸ್ಯೆ ಇರುವಂತೆ ತೋರುತ್ತಿದೆ. ದಯವಿಟ್ಟು ಕೀಬೋರ್ಡ್ ಬಟನ್ ಬಳಸಿ ನಿಮ್ಮ ಉತ್ತರಗಳನ್ನು ಟೈಪ್ ಮಾಡಿ.',
    microphoneProblemVoiceChat:
      'ನಿಮ್ಮ ಮೈಕ್ರೊಫೋನ್‌ನಲ್ಲಿ ಸಮಸ್ಯೆ ಇರುವಂತೆ ತೋರುತ್ತಿದೆ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಬ್ರೌಸರ್ ಅನುಮತಿಗಳನ್ನು ಪರಿಶೀಲಿಸಿ ಮತ್ತು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    responseTimeoutError: 'ಪ್ರತಿಕ್ರಿಯೆಗೆ ಹೆಚ್ಚು ಸಮಯ ತೆಗೆದುಕೊಳ್ಳುತ್ತಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    noResponseError: 'ಯಾವುದೇ ಪ್ರತಿಕ್ರಿಯೆ ಪತ್ತೆಯಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    connectionInterrupted: 'ಸಂಪರ್ಕ ಅಡಚಣೆಯಾಗಿದೆ. ನೀವು ನಿಮ್ಮ ಸಂದೇಶವನ್ನು ಮರುಕಳುಹಿಸಬಹುದು.',
    videoStartError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ಪ್ರಾರಂಭಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoCameraNotSupported: 'ಕ್ಯಾಮೆರಾ ಬೆಂಬಲಿತವಾಗಿಲ್ಲ. ದಯವಿಟ್ಟು ಬೇರೆ ಬ್ರೌಸರ್‌ನೊಂದಿಗೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoPauseError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ವಿರಾಮಗೊಳಿಸಲು ವಿಫಲವಾಗಿದೆ.',
    videoResumeError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ಪುನರಾರಂಭಿಸಲು ವಿಫಲವಾಗಿದೆ.',
    videoStopError: 'ವೀಡಿಯೊ ರೆಕಾರ್ಡಿಂಗ್ ನಿಲ್ಲಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoSwitchCameraError: 'ಕ್ಯಾಮೆರಾ ಬದಲಾಯಿಸಲು ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.',
    videoSendError: 'ವೀಡಿಯೊ ಸಂದೇಶ ಕಳುಹಿಸಲು ವಿಫಲವಾಗಿದೆ.',
    welcomeBack: 'ಮರಳಿ ಸ್ವಾಗತ!',
    unsupportedFileType:
      'ಬೆಂಬಲಿತವಲ್ಲದ ಫೈಲ್ ಪ್ರಕಾರ. ದಯವಿಟ್ಟು JPG, PNG, GIF ಅಥವಾ MP4 ಫೈಲ್ ಅನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ.',
    fileTooLarge: 'ಈ ಫೈಲ್ ತುಂಬಾ ದೊಡ್ಡದಾಗಿದೆ. ಗರಿಷ್ಠ ಗಾತ್ರ 200 MB.',
    audioCloseError: 'ಆಡಿಯೊ ಮುಚ್ಚುವಲ್ಲಿ ದೋಷ:',
  },
  kk: {
    telletDoesNotExistTitle: 'Бұл сілтеме қолжетімді емес',
    projectDoesNotExistTitle: 'Бұл сілтеме қолжетімді емес',
    projectDoesNotExistMessage:
      'Кешіріңіз, бұл зерттеуді таба алмадық. Сізге берілген сілтеменің дұрыстығын тексеріңіз немесе Сізді шақырған адамға хабарласыңыз.',
    maximumParticipantsTitle: 'Рахмет',
    maximumParticipantsMessage:
      'Бұл зерттеуге қатысуға қызығушылық танытқаныңыз үшін алғысымызды білдіреміз.\nҚажетті жауаптар саны жиналды, сондықтан, өкінішке орай, қазір жаңа қатысушылар қабылданбайды.\nБұл маңызды жобаға үлес қосуға дайын болғаныңыз үшін және уақытыңызды бөлгеніңіз үшін ризамыз.\nТүсіністік танытқаныңызға рахмет.',
    somethingMissingTitle: 'Бұл сілтеме қолжетімді емес',
    somethingMissingMessage:
      'Кешіріңіз, бұл зерттеуді таба алмадық. Сізге берілген сілтеменің дұрыстығын тексеріңіз немесе Сізді шақырған адамға хабарласыңыз.',
    projectCompletedTitle: 'Рахмет',
    projectCompletedMessage:
      'Бұл зерттеуге қатысуға қызығушылық танытқаныңыз үшін алғысымызды білдіреміз.\nБұл зерттеу аяқталды, сондықтан, өкінішке орай, жаңа қатысушылар қабылданбайды.\nҮлес қосуға дайын болғаныңыз үшін ризамыз және түсіністік танытқаныңызға рахмет.',
    projectInDraftOrReadyStatusTitle: 'Әлі ашылған жоқ',
    projectInDraftOrReadyStatusMessage:
      'Бұл зерттеуге қатысуға қызығушылық танытқаныңыз үшін рахмет.\nБұл зерттеу әлі қатысуға ашылған жоқ. Кейінірек қайта көріп көріңіз немесе Сізді шақырған адамға хабарласыңыз.',
    redirectingIn: 'Бағыттау',
    seconds: 'секундтан кейін...',
    cancel: 'Болдырмау',
    returnToPanel: 'Панельге оралу',
    errorBoundaryTitle: 'Бірдеңе дұрыс болмады',
    errorBoundaryMessage: 'Күтпеген қате пайда болды. Жалғастыру үшін бетті қайта жүктеңіз.',
    errorBoundaryReloadButton: 'Бетті қайта жүктеу',
    messageProcessingError:
      'Қап! Хабарламаңызды өңдеу кезінде мәселе туындады. Оны қайта жіберіп көріңіз.',
    microphoneProblemTextChat:
      'Микрофоныңызда ақау бар сияқты. Жауаптарыңызды пернетақта арқылы тере беріңіз.',
    microphoneProblemVoiceChat:
      'Микрофоныңызда ақау бар сияқты. Браузер рұқсаттарын тексеріп, қайталап көріңіз.',
    responseTimeoutError: 'Жауап тым ұзаққа созылуда. Қайталап көріңіз.',
    noResponseError: 'Жауап анықталмады. Қайталап көріңіз.',
    connectionInterrupted: 'Байланыс үзілді. Хабарламаңызды қайта жібере аласыз.',
    videoStartError: 'Бейне жазбаны бастау мүмкін болмады. Қайталап көріңіз.',
    videoCameraNotSupported: 'Камера қолдауға алынбайды. Басқа браузерді қолданып көріңіз.',
    videoPauseError: 'Бейне жазбаны кідірту мүмкін болмады.',
    videoResumeError: 'Бейне жазбаны жалғастыру мүмкін болмады.',
    videoStopError: 'Бейне жазбаны тоқтату мүмкін болмады. Қайталап көріңіз.',
    videoSwitchCameraError: 'Камераны ауыстыру мүмкін болмады. Қайталап көріңіз.',
    videoSendError: 'Бейне хабарламаны жіберу мүмкін болмады.',
    welcomeBack: 'Қайта келуіңізбен!',
    unsupportedFileType:
      'Қолдау көрсетілмейтін файл түрі. JPG, PNG, GIF немесе MP4 файлын жүктеңіз.',
    fileTooLarge: 'Бұл файл тым үлкен. Ең үлкен өлшемі — 200 МБ.',
    audioCloseError: 'Аудионы жабу қатесі:',
  },
  ko: {
    telletDoesNotExistTitle: '이 링크는 사용할 수 없습니다',
    projectDoesNotExistTitle: '이 링크는 사용할 수 없습니다',
    projectDoesNotExistMessage:
      '죄송합니다. 해당 연구를 찾을 수 없습니다. 전달받으신 링크를 다시 확인해 주시거나, 초대해 주신 분께 문의해 주시기 바랍니다.',
    maximumParticipantsTitle: '감사합니다',
    maximumParticipantsMessage:
      '본 연구에 관심을 가져주셔서 감사합니다.\n현재 필요한 응답 수를 모두 받아 더 이상 새로운 참여자를 모실 수 없게 되었습니다.\n연구에 방문해 주시고 소중한 참여 의사를 밝혀주셔서 진심으로 감사드립니다.\n이해해 주셔서 감사합니다.',
    somethingMissingTitle: '이 링크는 사용할 수 없습니다',
    somethingMissingMessage:
      '죄송합니다. 해당 연구를 찾을 수 없습니다. 전달받으신 링크를 다시 확인해 주시거나, 초대해 주신 분께 문의해 주시기 바랍니다.',
    projectCompletedTitle: '감사합니다',
    projectCompletedMessage:
      '본 연구에 관심을 가져주셔서 감사합니다.\n이 연구는 현재 종료되어 더 이상 새로운 참여자를 모실 수 없습니다.\n참여 의사에 감사드리며, 너그러운 양해 부탁드립니다.',
    projectInDraftOrReadyStatusTitle: '아직 오픈되지 않았습니다',
    projectInDraftOrReadyStatusMessage:
      '본 연구에 관심을 가져주셔서 감사합니다.\n이 연구는 아직 참여가 불가능합니다. 잠시 후 다시 시도해 주시거나, 초대해 주신 분께 문의해 주시기 바랍니다.',
    redirectingIn: '리디렉션까지',
    seconds: '초 남음...',
    cancel: '취소',
    returnToPanel: '패널로 돌아가기',
    errorBoundaryTitle: '문제가 발생했습니다',
    errorBoundaryMessage: '예기치 않은 오류가 발생했습니다. 계속하려면 페이지를 새로 고침하세요.',
    errorBoundaryReloadButton: '페이지 새로 고침',
    messageProcessingError: '앗! 메시지를 처리하는 데 문제가 있는 것 같습니다. 다시 보내주세요.',
    microphoneProblemTextChat:
      '마이크에 문제가 있는 것 같습니다. 키보드 버튼을 사용하여 답변을 입력해주세요.',
    microphoneProblemVoiceChat:
      '마이크에 문제가 있는 것 같습니다. 브라우저 권한을 확인하고 다시 시도해주세요.',
    responseTimeoutError: '응답이 너무 오래 걸립니다. 다시 시도해주세요.',
    noResponseError: '응답이 감지되지 않았습니다. 다시 시도해주세요.',
    connectionInterrupted: '연결이 중단되었습니다. 메시지를 다시 보낼 수 있습니다.',
    videoStartError: '비디오 녹화를 시작하지 못했습니다. 다시 시도해주세요.',
    videoCameraNotSupported: '카메라가 지원되지 않습니다. 다른 브라우저로 시도해주세요.',
    videoPauseError: '비디오 녹화를 일시 중지하지 못했습니다.',
    videoResumeError: '비디오 녹화를 재개하지 못했습니다.',
    videoStopError: '비디오 녹화를 중지하지 못했습니다. 다시 시도해주세요.',
    videoSwitchCameraError: '카메라 전환에 실패했습니다. 다시 시도해주세요.',
    videoSendError: '비디오 메시지를 보내지 못했습니다.',
    welcomeBack: '다시 오신 것을 환영합니다!',
    unsupportedFileType:
      '지원되지 않는 파일 형식입니다. JPG, PNG, GIF 또는 MP4 파일을 업로드해주세요.',
    fileTooLarge: '파일이 너무 큽니다. 최대 크기는 200 MB입니다.',
    audioCloseError: '오디오 닫기 오류:',
  },
  lv: {
    telletDoesNotExistTitle: 'Šī saite nav pieejama',
    projectDoesNotExistTitle: 'Šī saite nav pieejama',
    projectDoesNotExistMessage:
      'Atvainojiet, mums diemžēl neizdevās atrast šo pētījumu. Lūdzu, pārbaudiet Jums nosūtīto saiti vai sazinieties ar personu, kas Jūs uzaicināja.',
    maximumParticipantsTitle: 'Paldies',
    maximumParticipantsMessage:
      'Paldies par Jūsu interesi piedalīties šajā pētījumā.\nMēs jau esam saņēmuši nepieciešamo atbilžu skaitu, tāpēc šobrīd diemžēl nevaram pieņemt jaunus dalībniekus.\nAugstu vērtējam Jūsu vēlmi piedalīties un ieguldīt šajā svarīgajā projektā.\nPaldies par sapratni.',
    somethingMissingTitle: 'Šī saite nav pieejama',
    somethingMissingMessage:
      'Atvainojiet, mums diemžēl neizdevās atrast šo pētījumu. Lūdzu, pārbaudiet Jums nosūtīto saiti vai sazinieties ar personu, kas Jūs uzaicināja.',
    projectCompletedTitle: 'Paldies',
    projectCompletedMessage:
      'Paldies par Jūsu interesi piedalīties šajā pētījumā.\nŠis pētījums ir noslēdzies, tāpēc šobrīd diemžēl nevaram pieņemt jaunus dalībniekus.\nAugstu vērtējam Jūsu vēlmi piedalīties un pateicamies par sapratni.',
    projectInDraftOrReadyStatusTitle: 'Vēl nav atvērts',
    projectInDraftOrReadyStatusMessage:
      'Paldies par Jūsu interesi piedalīties šajā pētījumā.\nŠis pētījums vēl nav atvērts dalībai. Lūdzu, mēģiniet vēlāk vēlreiz vai sazinieties ar personu, kas Jūs uzaicināja.',
    redirectingIn: 'Pāradresācija pēc',
    seconds: 'sekundēm...',
    cancel: 'Atcelt',
    returnToPanel: 'Atgriezties panelī',
    errorBoundaryTitle: 'Kaut kas nogāja greizi',
    errorBoundaryMessage: 'Radās neparedzēta kļūda. Lūdzu, pārlādējiet lapu, lai turpinātu.',
    errorBoundaryReloadButton: 'Pārlādēt lapu',
    messageProcessingError:
      'Ak! Šķiet, ka mums ir problēmas ar jūsu ziņojuma apstrādi. Lūdzu, mēģiniet to nosūtīt vēlreiz.',
    microphoneProblemTextChat:
      'Šķiet, ka ir problēma ar jūsu mikrofonu. Lūdzu, ierakstiet savas atbildes, izmantojot tastatūras pogu.',
    microphoneProblemVoiceChat:
      'Šķiet, ka ir problēma ar jūsu mikrofonu. Lūdzu, pārbaudiet pārlūkprogrammas atļaujas un mēģiniet vēlreiz.',
    responseTimeoutError: 'Atbilde aizņem pārāk ilgu laiku. Lūdzu, mēģiniet vēlreiz.',
    noResponseError: 'Atbilde netika atklāta. Lūdzu, mēģiniet vēlreiz.',
    connectionInterrupted:
      'Savienojums tika pārtraukts. Jūs varat atkārtoti nosūtīt savu ziņojumu.',
    videoStartError: 'Neizdevās sākt video ierakstu. Lūdzu, mēģiniet vēlreiz.',
    videoCameraNotSupported: 'Kamera netiek atbalstīta. Lūdzu, mēģiniet citā pārlūkprogrammā.',
    videoPauseError: 'Neizdevās apturēt video ierakstu.',
    videoResumeError: 'Neizdevās atsākt video ierakstu.',
    videoStopError: 'Neizdevās apturēt video ierakstu. Lūdzu, mēģiniet vēlreiz.',
    videoSwitchCameraError: 'Neizdevās pārslēgt kameru. Lūdzu, mēģiniet vēlreiz.',
    videoSendError: 'Neizdevās nosūtīt video ziņojumu.',
    welcomeBack: 'Laipni lūdzam atpakaļ!',
    unsupportedFileType:
      'Neatbalstīts faila tips. Lūdzu, augšupielādējiet JPG, PNG, GIF vai MP4 failu.',
    fileTooLarge: 'Šis fails ir pārāk liels. Maksimālais izmērs ir 200 MB.',
    audioCloseError: 'Kļūda, aizverot audio:',
  },
  lt: {
    telletDoesNotExistTitle: 'Ši nuoroda nepasiekiama',
    projectDoesNotExistTitle: 'Ši nuoroda nepasiekiama',
    projectDoesNotExistMessage:
      'Atsiprašome, tačiau nepavyko rasti šio tyrimo. Patikrinkite jums pateiktą nuorodą arba susisiekite su jus pakvietusiu asmeniu.',
    maximumParticipantsTitle: 'Dėkojame',
    maximumParticipantsMessage:
      'Dėkojame, kad domitės galimybe dalyvauti šiame tyrime.\nŠiuo metu jau gavome reikiamą atsakymų skaičių, todėl, deja, nebegalime priimti daugiau dalyvių.\nVertiname, kad skyrėte laiko apsilankyti šiame tyrime ir norėjote prisidėti prie šio svarbaus projekto.\nDėkojame už supratingumą.',
    somethingMissingTitle: 'Ši nuoroda nepasiekiama',
    somethingMissingMessage:
      'Atsiprašome, tačiau nepavyko rasti šio tyrimo. Patikrinkite jums pateiktą nuorodą arba susisiekite su jus pakvietusiu asmeniu.',
    projectCompletedTitle: 'Dėkojame',
    projectCompletedMessage:
      'Dėkojame, kad domitės galimybe dalyvauti šiame tyrime.\nŠis tyrimas jau yra uždarytas, todėl, deja, nebegalime priimti daugiau dalyvių.\nVertiname jūsų norą prisidėti ir dėkojame už supratingumą.',
    projectInDraftOrReadyStatusTitle: 'Dar neprasidėjo',
    projectInDraftOrReadyStatusMessage:
      'Dėkojame, kad domitės galimybe dalyvauti šiame tyrime.\nŠis tyrimas dar nėra atviras dalyvavimui. Prašome pabandyti vėliau arba susisiekite su jus pakvietusiu asmeniu.',
    redirectingIn: 'Nukreipiama po',
    seconds: 'sekundžių...',
    cancel: 'Atšaukti',
    returnToPanel: 'Grįžti į skydelį',
    errorBoundaryTitle: 'Kažkas nepavyko',
    errorBoundaryMessage: 'Įvyko netikėta klaida. Norėdami tęsti, perkraukite puslapį.',
    errorBoundaryReloadButton: 'Perkrauti puslapį',
    messageProcessingError:
      'Oi! Atrodo, turime problemų apdorodami jūsų žinutę. Pabandykite ją išsiųsti dar kartą.',
    microphoneProblemTextChat:
      'Atrodo, kad yra problema su jūsų mikrofonu. Įveskite atsakymus naudodami klaviatūros mygtuką.',
    microphoneProblemVoiceChat:
      'Atrodo, kad yra problema su jūsų mikrofonu. Patikrinkite naršyklės leidimus ir bandykite dar kartą.',
    responseTimeoutError: 'Atsakymas užtrunka per ilgai. Bandykite dar kartą.',
    noResponseError: 'Atsakymas neaptiktas. Bandykite dar kartą.',
    connectionInterrupted: 'Ryšys nutrūko. Galite iš naujo išsiųsti savo žinutę.',
    videoStartError: 'Nepavyko pradėti vaizdo įrašymo. Bandykite dar kartą.',
    videoCameraNotSupported: 'Kamera nepalaikoma. Pabandykite su kita naršykle.',
    videoPauseError: 'Nepavyko pristabdyti vaizdo įrašymo.',
    videoResumeError: 'Nepavyko atnaujinti vaizdo įrašymo.',
    videoStopError: 'Nepavyko sustabdyti vaizdo įrašymo. Bandykite dar kartą.',
    videoSwitchCameraError: 'Nepavyko perjungti kameros. Bandykite dar kartą.',
    videoSendError: 'Nepavyko išsiųsti vaizdo žinutės.',
    welcomeBack: 'Sveiki sugrįžę!',
    unsupportedFileType: 'Nepalaikomas failo tipas. Įkelkite JPG, PNG, GIF arba MP4 failą.',
    fileTooLarge: 'Šis failas per didelis. Didžiausias dydis — 200 MB.',
    audioCloseError: 'Klaida uždarant garso įrašą:',
  },
  mk: {
    telletDoesNotExistTitle: 'Оваа врска не е достапна',
    projectDoesNotExistTitle: 'Оваа врска не е достапна',
    projectDoesNotExistMessage:
      'Се извинуваме, но не можевме да ја најдеме оваа студија. Ве молиме проверете ја врската што ви беше дадена или контактирајте ја лицето кое ве покани.',
    maximumParticipantsTitle: 'Ви благодариме',
    maximumParticipantsMessage:
      'Ви благодариме за вашиот интерес да учествувате во ова истражување.\nГо достигнавме потребниот број на одговори, па за жал во моментов не можеме да прифатиме нови учесници.\nВи благодариме што одвоивте време да ја посетите оваа студија и за вашата подготвеност да придонесете кон овој важен проект.\nВи благодариме за разбирањето.',
    somethingMissingTitle: 'Оваа врска не е достапна',
    somethingMissingMessage:
      'Се извинуваме, но не можевме да ја најдеме оваа студија. Ве молиме проверете ја врската што ви беше дадена или контактирајте ја лицето кое ве покани.',
    projectCompletedTitle: 'Ви благодариме',
    projectCompletedMessage:
      'Ви благодариме за вашиот интерес да учествувате во ова истражување.\nОваа студија е затворена, па за жал не можеме да прифатиме нови учесници.\nВи благодариме за вашата подготвеност да придонесете и за разбирањето.',
    projectInDraftOrReadyStatusTitle: 'Сè уште не е отворено',
    projectInDraftOrReadyStatusMessage:
      'Ви благодариме за вашиот интерес да учествувате во ова истражување.\nОваа студија сè уште не е отворена за учество. Ве молиме обидете се повторно подоцна или контактирајте ја лицето кое ве покани.',
    redirectingIn: 'Пренасочување за',
    seconds: 'секунди...',
    cancel: 'Откажи',
    returnToPanel: 'Врати се на панелот',
    errorBoundaryTitle: 'Нешто тргна наопаку',
    errorBoundaryMessage:
      'Се појави неочекувана грешка. Ве молиме, освежете ја страницата за да продолжите.',
    errorBoundaryReloadButton: 'Освежи ја страницата',
    messageProcessingError:
      'Упс! Изгледа дека имаме проблем со обработката на вашата порака. Обидете се повторно да ја испратите.',
    microphoneProblemTextChat:
      'Се чини дека има проблем со вашиот микрофон. Ве молиме, внесете ги вашите одговори користејќи го копчето на тастатурата.',
    microphoneProblemVoiceChat:
      'Се чини дека има проблем со вашиот микрофон. Проверете ги дозволите на прелистувачот и обидете се повторно.',
    responseTimeoutError: 'Одговорот трае премногу долго. Обидете се повторно.',
    noResponseError: 'Не е откриен одговор. Обидете се повторно.',
    connectionInterrupted: 'Врската е прекината. Можете повторно да ја испратите вашата порака.',
    videoStartError: 'Неуспешно стартување на видео снимањето. Обидете се повторно.',
    videoCameraNotSupported: 'Камерата не е поддржана. Обидете се со друг прелистувач.',
    videoPauseError: 'Неуспешно паузирање на видео снимањето.',
    videoResumeError: 'Неуспешно продолжување на видео снимањето.',
    videoStopError: 'Неуспешно запирање на видео снимањето. Обидете се повторно.',
    videoSwitchCameraError: 'Неуспешно менување на камерата. Обидете се повторно.',
    videoSendError: 'Неуспешно испраќање на видео пораката.',
    welcomeBack: 'Добредојдовте назад!',
    unsupportedFileType:
      'Неподдржан тип на датотека. Ве молиме, прикачете JPG, PNG, GIF или MP4 датотека.',
    fileTooLarge: 'Оваа датотека е преголема. Максималната големина е 200 MB.',
    audioCloseError: 'Грешка при затворање на аудио:',
  },
  ms: {
    telletDoesNotExistTitle: 'Pautan ini tidak tersedia',
    projectDoesNotExistTitle: 'Pautan ini tidak tersedia',
    projectDoesNotExistMessage:
      'Maaf, kami tidak dapat menemui kajian ini. Sila semak pautan yang diberikan kepada anda, atau hubungi individu yang menjemput anda.',
    maximumParticipantsTitle: 'Terima kasih',
    maximumParticipantsMessage:
      'Terima kasih atas minat anda untuk menyertai kajian ini.\nKami telah menerima jumlah respons yang diperlukan, jadi kami tidak dapat menerima peserta baharu pada masa ini.\nKami menghargai masa yang anda luangkan untuk melawat kajian ini serta kesediaan anda untuk menyumbang kepada projek penting ini.\nTerima kasih atas pemahaman anda.',
    somethingMissingTitle: 'Pautan ini tidak tersedia',
    somethingMissingMessage:
      'Maaf, kami tidak dapat menemui kajian ini. Sila semak pautan yang diberikan kepada anda, atau hubungi individu yang menjemput anda.',
    projectCompletedTitle: 'Terima kasih',
    projectCompletedMessage:
      'Terima kasih atas minat anda untuk menyertai kajian ini.\nKajian ini telah ditutup dan kami tidak dapat menerima peserta baharu.\nKami menghargai kesediaan anda untuk menyumbang, dan terima kasih atas pemahaman anda.',
    projectInDraftOrReadyStatusTitle: 'Belum dibuka',
    projectInDraftOrReadyStatusMessage:
      'Terima kasih atas minat anda untuk menyertai kajian ini.\nKajian ini belum dibuka untuk penyertaan. Sila cuba lagi kemudian, atau hubungi individu yang menjemput anda.',
    redirectingIn: 'Mengalih semula dalam',
    seconds: 'saat...',
    cancel: 'Batal',
    returnToPanel: 'Kembali ke Panel',
    errorBoundaryTitle: 'Sesuatu telah berlaku',
    errorBoundaryMessage:
      'Ralat tidak dijangka berlaku. Sila muat semula halaman untuk meneruskan.',
    errorBoundaryReloadButton: 'Muat semula halaman',
    messageProcessingError:
      'Alamak! Nampaknya kami menghadapi masalah memproses mesej anda. Sila cuba hantar semula.',
    microphoneProblemTextChat:
      'Nampaknya ada masalah dengan mikrofon anda. Sila taip jawapan anda menggunakan butang papan kekunci.',
    microphoneProblemVoiceChat:
      'Nampaknya ada masalah dengan mikrofon anda. Sila semak kebenaran pelayar anda dan cuba lagi.',
    responseTimeoutError: 'Respons mengambil masa terlalu lama. Sila cuba lagi.',
    noResponseError: 'Tiada respons dikesan. Sila cuba lagi.',
    connectionInterrupted: 'Sambungan terganggu. Anda boleh menghantar semula mesej anda.',
    videoStartError: 'Gagal memulakan rakaman video. Sila cuba lagi.',
    videoCameraNotSupported: 'Kamera tidak disokong. Sila cuba dengan pelayar yang berbeza.',
    videoPauseError: 'Gagal menjeda rakaman video.',
    videoResumeError: 'Gagal menyambung semula rakaman video.',
    videoStopError: 'Gagal menghentikan rakaman video. Sila cuba lagi.',
    videoSwitchCameraError: 'Gagal menukar kamera. Sila cuba lagi.',
    videoSendError: 'Gagal menghantar mesej video.',
    welcomeBack: 'Selamat kembali!',
    unsupportedFileType: 'Jenis fail tidak disokong. Sila muat naik fail JPG, PNG, GIF, atau MP4.',
    fileTooLarge: 'Fail ini terlalu besar. Saiz maksimum ialah 200 MB.',
    audioCloseError: 'Ralat menutup audio:',
  },
  mi: {
    telletDoesNotExistTitle: 'Kāore tēnei hononga e wātea ana',
    projectDoesNotExistTitle: 'Kāore tēnei hononga e wātea ana',
    projectDoesNotExistMessage:
      'Aroha mai, kāore i taea te kimi i tēnei rangahau. Tēnā, tirohia te hononga i tukuna ki a koe, ā, whakapā atu ki te tangata nāna koe i tono.',
    maximumParticipantsTitle: 'Ngā mihi',
    maximumParticipantsMessage:
      'Ngā mihi mō tō hiahia ki te whai wāhi mai ki tēnei rangahau.\nKua tae mātou ki te nui o ngā whakautu e hiahiatia ana, nō reira kāore e taea te whakaae atu ki ngā kaiuru anō i tēnei wā.\nE mihi ana mātou mō tō whakapau wā ki te toro mai ki tēnei rangahau me tō hiahia ki te āwhina i tēnei kaupapa hira.\nNgā mihi mō tō mārama.',
    somethingMissingTitle: 'Kāore tēnei hononga e wātea ana',
    somethingMissingMessage:
      'Aroha mai, kāore i taea te kimi i tēnei rangahau. Tēnā, tirohia te hononga i tukuna ki a koe, ā, whakapā atu ki te tangata nāna koe i tono.',
    projectCompletedTitle: 'Ngā mihi',
    projectCompletedMessage:
      'Ngā mihi mō tō hiahia ki te whai wāhi mai ki tēnei rangahau.\nKua kati tēnei rangahau ināianei, nō reira kāore e taea te whakaae atu ki ngā kaiuru anō.\nE mihi ana mātou mō tō hiahia ki te āwhina, ā, ngā mihi mō tō mārama.',
    projectInDraftOrReadyStatusTitle: 'Kāore anō kia tuwhera',
    projectInDraftOrReadyStatusMessage:
      'Ngā mihi mō tō hiahia ki te whai wāhi mai ki tēnei rangahau.\nKāore anō kia tuwhera tēnei rangahau mō te whai wāhi mai. Tēnā, whakamātau anō ā muri ake nei, ā, whakapā atu ki te tangata nāna koe i tono.',
    redirectingIn: 'Ka whakaaro anō i roto i',
    seconds: 'hēkona...',
    cancel: 'Whakakore',
    returnToPanel: 'Hoki ki te Papa',
    errorBoundaryTitle: 'I hē tētahi mea',
    errorBoundaryMessage:
      'I puta he hapa ohorere. Tēnā koa, utaina anō te whārangi ki te haere tonu.',
    errorBoundaryReloadButton: 'Utaina anō te whārangi',
    messageProcessingError:
      'Auē! Te āhua nei kei te raru mātou ki te tukatuka i tō karere. Tēnā koa, tukuna anō.',
    microphoneProblemTextChat:
      'Te āhua nei he raru kei tō hopuoro. Tēnā koa, patohia ō whakautu mā te pātuhi.',
    microphoneProblemVoiceChat:
      'Te āhua nei he raru kei tō hopuoro. Tēnā koa, tirohia ngā whakaaetanga a tō tirotiro, ka ngana anō.',
    responseTimeoutError: 'Kei te roa rawa te whakautu. Tēnā koa, ngana anō.',
    noResponseError: 'Kāore he whakautu i kitea. Tēnā koa, ngana anō.',
    connectionInterrupted: 'Kua whakatārewahia te hononga. Ka taea e koe te tuku anō i tō karere.',
    videoStartError: 'I rahua te tīmata i te hopu ataata. Tēnā koa, ngana anō.',
    videoCameraNotSupported:
      'Kāore te kāmera e tautokohia ana. Tēnā koa, ngana ki tētahi tirotiro rerekē.',
    videoPauseError: 'I rahua te whakatārewa i te hopu ataata.',
    videoResumeError: 'I rahua te haere tonu i te hopu ataata.',
    videoStopError: 'I rahua te whakamutu i te hopu ataata. Tēnā koa, ngana anō.',
    videoSwitchCameraError: 'I rahua te whakawhiti kāmera. Tēnā koa, ngana anō.',
    videoSendError: 'I rahua te tuku karere ataata.',
    welcomeBack: 'Nau mai anō!',
    unsupportedFileType:
      'Momo kōnae kāore e tautokohia. Tēnā koa, tukuna he kōnae JPG, PNG, GIF, MP4 rānei.',
    fileTooLarge: 'He rahi rawa tēnei kōnae. Ko te rahi mōrahi he 200 MB.',
    audioCloseError: 'He hapa i te katinga o te hopuoro:',
  },
  mr: {
    telletDoesNotExistTitle: 'ही लिंक उपलब्ध नाही',
    projectDoesNotExistTitle: 'ही लिंक उपलब्ध नाही',
    projectDoesNotExistMessage:
      'क्षमस्व, आम्हाला हा अभ्यास सापडला नाही. कृपया आपल्याला दिलेली लिंक तपासा किंवा आपल्याला आमंत्रित करणाऱ्या व्यक्तीशी संपर्क साधा.',
    maximumParticipantsTitle: 'धन्यवाद',
    maximumParticipantsMessage:
      'या संशोधनात सहभागी होण्याच्या आपल्या इच्छेबद्दल धन्यवाद.\nआवश्यक प्रतिसादांची संख्या आता मिळाली आहे, त्यामुळे सध्या आम्ही आणखी सहभागी स्वीकारू शकत नाही.\nया महत्त्वाच्या प्रकल्पात योगदान देण्याची आपली तयारी आणि वेळ दिल्याबद्दल आम्ही आपले आभारी आहोत.\nआपल्या समजूतदारपणाबद्दल धन्यवाद.',
    somethingMissingTitle: 'ही लिंक उपलब्ध नाही',
    somethingMissingMessage:
      'क्षमस्व, आम्हाला हा अभ्यास सापडला नाही. कृपया आपल्याला दिलेली लिंक तपासा किंवा आपल्याला आमंत्रित करणाऱ्या व्यक्तीशी संपर्क साधा.',
    projectCompletedTitle: 'धन्यवाद',
    projectCompletedMessage:
      'या संशोधनात सहभागी होण्याच्या आपल्या इच्छेबद्दल धन्यवाद.\nहा अभ्यास आता पूर्ण झाला आहे, त्यामुळे सध्या आम्ही आणखी सहभागी स्वीकारू शकत नाही.\nआपली तयारी आणि योगदानाबद्दल आम्ही आपले आभारी आहोत. आपल्या समजूतदारपणाबद्दल धन्यवाद.',
    projectInDraftOrReadyStatusTitle: 'अद्याप खुले नाही',
    projectInDraftOrReadyStatusMessage:
      'या संशोधनात सहभागी होण्याच्या आपल्या इच्छेबद्दल धन्यवाद.\nहा अभ्यास अद्याप सहभागींसाठी खुला झालेला नाही. कृपया नंतर पुन्हा प्रयत्न करा किंवा आपल्याला आमंत्रित करणाऱ्या व्यक्तीशी संपर्क साधा.',
    redirectingIn: 'यामध्ये पुनर्निर्देशित करत आहे',
    seconds: 'सेकंद...',
    cancel: 'रद्द करा',
    returnToPanel: 'पॅनेलवर परत जा',
    errorBoundaryTitle: 'काहीतरी चूक झाली',
    errorBoundaryMessage: 'एक अनपेक्षित त्रुटी आली. पुढे जाण्यासाठी कृपया पृष्ठ पुन्हा लोड करा.',
    errorBoundaryReloadButton: 'पृष्ठ पुन्हा लोड करा',
    messageProcessingError:
      'अरेरे! आपला संदेश प्रक्रिया करण्यात आम्हाला अडचण येत आहे असे दिसते. कृपया तो पुन्हा पाठवण्याचा प्रयत्न करा.',
    microphoneProblemTextChat:
      'आपल्या मायक्रोफोनमध्ये समस्या असल्याचे दिसते. कृपया कीबोर्ड बटण वापरून आपली उत्तरे टाइप करा.',
    microphoneProblemVoiceChat:
      'आपल्या मायक्रोफोनमध्ये समस्या असल्याचे दिसते. कृपया आपल्या ब्राउझर परवानग्या तपासा आणि पुन्हा प्रयत्न करा.',
    responseTimeoutError: 'प्रतिसादाला खूप वेळ लागत आहे. कृपया पुन्हा प्रयत्न करा.',
    noResponseError: 'कोणताही प्रतिसाद आढळला नाही. कृपया पुन्हा प्रयत्न करा.',
    connectionInterrupted: 'कनेक्शन खंडित झाले. आपण आपला संदेश पुन्हा पाठवू शकता.',
    videoStartError: 'व्हिडिओ रेकॉर्डिंग सुरू करण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
    videoCameraNotSupported: 'कॅमेरा समर्थित नाही. कृपया वेगळ्या ब्राउझरसह प्रयत्न करा.',
    videoPauseError: 'व्हिडिओ रेकॉर्डिंग थांबवण्यात अयशस्वी.',
    videoResumeError: 'व्हिडिओ रेकॉर्डिंग पुन्हा सुरू करण्यात अयशस्वी.',
    videoStopError: 'व्हिडिओ रेकॉर्डिंग थांबवण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
    videoSwitchCameraError: 'कॅमेरा बदलण्यात अयशस्वी. कृपया पुन्हा प्रयत्न करा.',
    videoSendError: 'व्हिडिओ संदेश पाठवण्यात अयशस्वी.',
    welcomeBack: 'पुन्हा स्वागत आहे!',
    unsupportedFileType: 'असमर्थित फाइल प्रकार. कृपया JPG, PNG, GIF किंवा MP4 फाइल अपलोड करा.',
    fileTooLarge: 'ही फाइल खूप मोठी आहे. कमाल आकार 200 MB आहे.',
    audioCloseError: 'ऑडिओ बंद करताना त्रुटी:',
  },
  ne: {
    telletDoesNotExistTitle: 'यो लिङ्क उपलब्ध छैन',
    projectDoesNotExistTitle: 'यो लिङ्क उपलब्ध छैन',
    projectDoesNotExistMessage:
      'माफ गर्नुहोस्, हामीले यो अध्ययन फेला पार्न सकेनौं। कृपया तपाईंलाई दिइएको लिङ्क जाँच गर्नुहोस्, वा तपाईंलाई निमन्त्रणा गर्ने व्यक्तिसँग सम्पर्क गर्नुहोस्।',
    maximumParticipantsTitle: 'धन्यवाद',
    maximumParticipantsMessage:
      'यस अनुसन्धानमा सहभागी हुनुभएको रुचिका लागि धन्यवाद।\nहामीले आवश्यक प्रतिक्रिया संख्या प्राप्त गरिसकेका छौं, त्यसैले अहिलेका लागि नयाँ सहभागीहरू स्वीकार गर्न सक्दैनौं।\nयस अध्ययनमा समय दिनुभएको र महत्त्वपूर्ण परियोजनामा योगदान गर्न इच्छुक हुनु भएकोमा हामी आभारी छौं।\nतपाईंको बुझाइका लागि धन्यवाद।',
    somethingMissingTitle: 'यो लिङ्क उपलब्ध छैन',
    somethingMissingMessage:
      'माफ गर्नुहोस्, हामीले यो अध्ययन फेला पार्न सकेनौं। कृपया तपाईंलाई दिइएको लिङ्क जाँच गर्नुहोस्, वा तपाईंलाई निमन्त्रणा गर्ने व्यक्तिसँग सम्पर्क गर्नुहोस्।',
    projectCompletedTitle: 'धन्यवाद',
    projectCompletedMessage:
      'यस अनुसन्धानमा सहभागी हुनुभएको रुचिका लागि धन्यवाद।\nयो अध्ययन अब बन्द भइसकेको छ, त्यसैले अहिलेका लागि नयाँ सहभागीहरू स्वीकार गर्न सक्दैनौं।\nतपाईंको योगदानको इच्छाका लागि आभार प्रकट गर्दछौं, र तपाईंको बुझाइका लागि धन्यवाद।',
    projectInDraftOrReadyStatusTitle: 'अहिलेसम्म खुला छैन',
    projectInDraftOrReadyStatusMessage:
      'यस अनुसन्धानमा सहभागी हुनुभएको रुचिका लागि धन्यवाद।\nयो अध्ययनमा सहभागी हुन अझ खुला भएको छैन। कृपया केही समयपछि पुन: प्रयास गर्नुहोस्, वा तपाईंलाई निमन्त्रणा गर्ने व्यक्तिसँग सम्पर्क गर्नुहोस्।',
    redirectingIn: 'यसमा पुनर्निर्देशित गर्दै',
    seconds: 'सेकेन्ड...',
    cancel: 'रद्द गर्नुहोस्',
    returnToPanel: 'प्यानलमा फर्कनुहोस्',
    errorBoundaryTitle: 'केही गल्ती भयो',
    errorBoundaryMessage: 'अनपेक्षित त्रुटि भयो। जारी राख्न कृपया पृष्ठ पुनः लोड गर्नुहोस्।',
    errorBoundaryReloadButton: 'पृष्ठ पुनः लोड गर्नुहोस्',
    messageProcessingError:
      'ओहो! तपाईंको सन्देश प्रक्रिया गर्न हामीलाई समस्या भइरहेको जस्तो देखिन्छ। कृपया यसलाई फेरि पठाउने प्रयास गर्नुहोस्।',
    microphoneProblemTextChat:
      'तपाईंको माइक्रोफोनमा समस्या भएको जस्तो देखिन्छ। कृपया किबोर्ड बटन प्रयोग गरेर आफ्ना जवाफहरू टाइप गर्नुहोस्।',
    microphoneProblemVoiceChat:
      'तपाईंको माइक्रोफोनमा समस्या भएको जस्तो देखिन्छ। कृपया आफ्नो ब्राउजर अनुमतिहरू जाँच गर्नुहोस् र फेरि प्रयास गर्नुहोस्।',
    responseTimeoutError: 'प्रतिक्रिया धेरै लामो समय लिइरहेको छ। कृपया फेरि प्रयास गर्नुहोस्।',
    noResponseError: 'कुनै प्रतिक्रिया पत्ता लागेन। कृपया फेरि प्रयास गर्नुहोस्।',
    connectionInterrupted: 'जडान अवरोधित भयो। तपाईं आफ्नो सन्देश पुन: पठाउन सक्नुहुन्छ।',
    videoStartError: 'भिडियो रेकर्डिङ सुरु गर्न असफल। कृपया फेरि प्रयास गर्नुहोस्।',
    videoCameraNotSupported: 'क्यामेरा समर्थित छैन। कृपया फरक ब्राउजरसँग प्रयास गर्नुहोस्।',
    videoPauseError: 'भिडियो रेकर्डिङ रोक्न असफल।',
    videoResumeError: 'भिडियो रेकर्डिङ पुनः सुरु गर्न असफल।',
    videoStopError: 'भिडियो रेकर्डिङ रोक्न असफल। कृपया फेरि प्रयास गर्नुहोस्।',
    videoSwitchCameraError: 'क्यामेरा स्विच गर्न असफल। कृपया फेरि प्रयास गर्नुहोस्।',
    videoSendError: 'भिडियो सन्देश पठाउन असफल।',
    welcomeBack: 'फेरि स्वागत छ!',
    unsupportedFileType: 'असमर्थित फाइल प्रकार। कृपया JPG, PNG, GIF, वा MP4 फाइल अपलोड गर्नुहोस्।',
    fileTooLarge: 'यो फाइल धेरै ठूलो छ। अधिकतम आकार २०० MB हो।',
    audioCloseError: 'अडियो बन्द गर्दा त्रुटि:',
  },
  no: {
    telletDoesNotExistTitle: 'Denne lenken er ikke tilgjengelig',
    projectDoesNotExistTitle: 'Denne lenken er ikke tilgjengelig',
    projectDoesNotExistMessage:
      'Beklager, vi kunne ikke finne denne studien. Vennligst sjekk lenken du har fått, eller kontakt personen som inviterte deg.',
    maximumParticipantsTitle: 'Takk',
    maximumParticipantsMessage:
      'Takk for interessen for å delta i denne forskningen.\nVi har nå mottatt det antallet svar vi trenger, og kan dessverre ikke ta imot flere deltakere på nåværende tidspunkt.\nVi setter pris på at du tok deg tid til å besøke denne studien og ønsket å bidra til dette viktige prosjektet.\nTakk for forståelsen.',
    somethingMissingTitle: 'Denne lenken er ikke tilgjengelig',
    somethingMissingMessage:
      'Beklager, vi kunne ikke finne denne studien. Vennligst sjekk lenken du har fått, eller kontakt personen som inviterte deg.',
    projectCompletedTitle: 'Takk',
    projectCompletedMessage:
      'Takk for interessen for å delta i denne forskningen.\nDenne studien er nå avsluttet, og vi kan dessverre ikke ta imot flere deltakere.\nVi setter pris på din vilje til å bidra, og takker for forståelsen.',
    projectInDraftOrReadyStatusTitle: 'Ikke åpen ennå',
    projectInDraftOrReadyStatusMessage:
      'Takk for interessen for å delta i denne forskningen.\nDenne studien er ikke åpen for deltakelse ennå. Vennligst prøv igjen senere, eller kontakt personen som inviterte deg.',
    redirectingIn: 'Omdirigerer om',
    seconds: 'sekunder...',
    cancel: 'Avbryt',
    returnToPanel: 'Tilbake til panelet',
    errorBoundaryTitle: 'Noe gikk galt',
    errorBoundaryMessage: 'En uventet feil oppstod. Last inn siden på nytt for å fortsette.',
    errorBoundaryReloadButton: 'Last inn siden på nytt',
    messageProcessingError:
      'Oi! Det ser ut til at vi har problemer med å behandle meldingen din. Prøv å sende den på nytt.',
    microphoneProblemTextChat:
      'Det ser ut til at det er et problem med mikrofonen din. Skriv inn svarene dine ved å bruke tastaturknappen.',
    microphoneProblemVoiceChat:
      'Det ser ut til at det er et problem med mikrofonen din. Sjekk nettlesertillatelsene dine og prøv igjen.',
    responseTimeoutError: 'Svaret tar for lang tid. Vennligst prøv igjen.',
    noResponseError: 'Ingen respons oppdaget. Vennligst prøv igjen.',
    connectionInterrupted: 'Tilkoblingen ble avbrutt. Du kan sende meldingen din på nytt.',
    videoStartError: 'Kunne ikke starte videoopptak. Vennligst prøv igjen.',
    videoCameraNotSupported: 'Kameraet støttes ikke. Prøv med en annen nettleser.',
    videoPauseError: 'Kunne ikke pause videoopptak.',
    videoResumeError: 'Kunne ikke gjenoppta videoopptak.',
    videoStopError: 'Kunne ikke stoppe videoopptak. Vennligst prøv igjen.',
    videoSwitchCameraError: 'Kunne ikke bytte kamera. Vennligst prøv igjen.',
    videoSendError: 'Kunne ikke sende videomelding.',
    welcomeBack: 'Velkommen tilbake!',
    unsupportedFileType: 'Ikke-støttet filtype. Last opp en JPG, PNG, GIF eller MP4 fil.',
    fileTooLarge: 'Denne filen er for stor. Maksimal størrelse er 200 MB.',
    audioCloseError: 'Feil ved lukking av lyd:',
  },
  pa: {
    telletDoesNotExistTitle: 'ਇਹ ਲਿੰਕ ਉਪਲਬਧ ਨਹੀਂ ਹੈ',
    projectDoesNotExistTitle: 'ਇਹ ਲਿੰਕ ਉਪਲਬਧ ਨਹੀਂ ਹੈ',
    projectDoesNotExistMessage:
      'ਅਫ਼ਸੋਸ ਹੈ, ਅਸੀਂ ਇਹ ਅਧਿਐਨ ਨਹੀਂ ਲੱਭ ਸਕੇ। ਕਿਰਪਾ ਕਰਕੇ ਤੁਹਾਨੂੰ ਦਿੱਤਾ ਗਿਆ ਲਿੰਕ ਜਾਂਚੋ ਜਾਂ ਤੁਹਾਨੂੰ ਨਿਯੋਤਾ ਦੇਣ ਵਾਲੇ ਵਿਅਕਤੀ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।',
    maximumParticipantsTitle: 'ਧੰਨਵਾਦ',
    maximumParticipantsMessage:
      'ਇਸ ਖੋਜ ਵਿੱਚ ਭਾਗ ਲੈਣ ਵਿੱਚ ਰੁਚੀ ਦਿਖਾਉਣ ਲਈ ਤੁਹਾਡਾ ਧੰਨਵਾਦ।\nਸਾਨੂੰ ਹੁਣ ਲੋੜੀਂਦੇ ਜਵਾਬ ਮਿਲ ਚੁੱਕੇ ਹਨ, ਇਸ ਲਈ ਅਫ਼ਸੋਸ ਹੈ ਕਿ ਅਸੀਂ ਇਸ ਵੇਲੇ ਹੋਰ ਭਾਗੀਦਾਰ ਨਹੀਂ ਲੈ ਸਕਦੇ।\nਇਸ ਅਧਿਐਨ ਨੂੰ ਵੇਖਣ ਅਤੇ ਇਸ ਮਹੱਤਵਪੂਰਨ ਪ੍ਰੋਜੈਕਟ ਵਿੱਚ ਯੋਗਦਾਨ ਪਾਉਣ ਦੀ ਤੁਹਾਡੀ ਇੱਛਾ ਦੀ ਕਦਰ ਕਰਦੇ ਹਾਂ।\nਤੁਹਾਡੀ ਸਮਝ ਲਈ ਧੰਨਵਾਦ।',
    somethingMissingTitle: 'ਇਹ ਲਿੰਕ ਉਪਲਬਧ ਨਹੀਂ ਹੈ',
    somethingMissingMessage:
      'ਅਫ਼ਸੋਸ ਹੈ, ਅਸੀਂ ਇਹ ਅਧਿਐਨ ਨਹੀਂ ਲੱਭ ਸਕੇ। ਕਿਰਪਾ ਕਰਕੇ ਤੁਹਾਨੂੰ ਦਿੱਤਾ ਗਿਆ ਲਿੰਕ ਜਾਂਚੋ ਜਾਂ ਤੁਹਾਨੂੰ ਨਿਯੋਤਾ ਦੇਣ ਵਾਲੇ ਵਿਅਕਤੀ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।',
    projectCompletedTitle: 'ਧੰਨਵਾਦ',
    projectCompletedMessage:
      'ਇਸ ਖੋਜ ਵਿੱਚ ਭਾਗ ਲੈਣ ਵਿੱਚ ਰੁਚੀ ਦਿਖਾਉਣ ਲਈ ਤੁਹਾਡਾ ਧੰਨਵਾਦ।\nਇਹ ਅਧਿਐਨ ਹੁਣ ਬੰਦ ਹੋ ਚੁੱਕਾ ਹੈ, ਇਸ ਲਈ ਅਫ਼ਸੋਸ ਹੈ ਕਿ ਅਸੀਂ ਹੋਰ ਭਾਗੀਦਾਰ ਨਹੀਂ ਲੈ ਸਕਦੇ।\nਤੁਹਾਡੀ ਭਾਗੀਦਾਰੀ ਦੀ ਇੱਛਾ ਦੀ ਕਦਰ ਕਰਦੇ ਹਾਂ ਅਤੇ ਤੁਹਾਡੀ ਸਮਝ ਲਈ ਧੰਨਵਾਦ।',
    projectInDraftOrReadyStatusTitle: 'ਹਾਲੇ ਖੁੱਲ੍ਹਿਆ ਨਹੀਂ',
    projectInDraftOrReadyStatusMessage:
      'ਇਸ ਖੋਜ ਵਿੱਚ ਭਾਗ ਲੈਣ ਵਿੱਚ ਰੁਚੀ ਦਿਖਾਉਣ ਲਈ ਤੁਹਾਡਾ ਧੰਨਵਾਦ।\nਇਹ ਅਧਿਐਨ ਹਾਲੇ ਭਾਗੀਦਾਰੀ ਲਈ ਖੁੱਲ੍ਹਿਆ ਨਹੀਂ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਕੁਝ ਸਮੇਂ ਬਾਅਦ ਕੋਸ਼ਿਸ਼ ਕਰੋ ਜਾਂ ਤੁਹਾਨੂੰ ਨਿਯੋਤਾ ਦੇਣ ਵਾਲੇ ਵਿਅਕਤੀ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।',
    redirectingIn: 'ਇਸ ਵਿੱਚ ਰੀਡਾਇਰੈਕਟ ਹੋ ਰਿਹਾ ਹੈ',
    seconds: 'ਸਕਿੰਟ...',
    cancel: 'ਰੱਦ ਕਰੋ',
    returnToPanel: 'ਪੈਨਲ ਤੇ ਵਾਪਸ ਜਾਓ',
    errorBoundaryTitle: 'ਕੁਝ ਗਲਤ ਹੋ ਗਿਆ',
    errorBoundaryMessage: 'ਇੱਕ ਅਣਕਿਆਸੀ ਗਲਤੀ ਹੋਈ। ਜਾਰੀ ਰੱਖਣ ਲਈ ਕਿਰਪਾ ਕਰਕੇ ਪੰਨਾ ਮੁੜ ਲੋਡ ਕਰੋ।',
    errorBoundaryReloadButton: 'ਪੰਨਾ ਮੁੜ ਲੋਡ ਕਰੋ',
    messageProcessingError:
      'ਓਹੋ! ਲੱਗਦਾ ਹੈ ਕਿ ਸਾਨੂੰ ਤੁਹਾਡੇ ਸੁਨੇਹੇ ਤੇ ਕਾਰਵਾਈ ਕਰਨ ਵਿੱਚ ਮੁਸ਼ਕਲ ਆ ਰਹੀ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਇਸਨੂੰ ਦੁਬਾਰਾ ਭੇਜਣ ਦੀ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    microphoneProblemTextChat:
      'ਲੱਗਦਾ ਹੈ ਕਿ ਤੁਹਾਡੇ ਮਾਈਕ੍ਰੋਫੋਨ ਨਾਲ ਕੋਈ ਸਮੱਸਿਆ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਕੀਬੋਰਡ ਬਟਨ ਦੀ ਵਰਤੋਂ ਕਰਕੇ ਆਪਣੇ ਜਵਾਬ ਟਾਈਪ ਕਰੋ।',
    microphoneProblemVoiceChat:
      'ਲੱਗਦਾ ਹੈ ਕਿ ਤੁਹਾਡੇ ਮਾਈਕ੍ਰੋਫੋਨ ਨਾਲ ਕੋਈ ਸਮੱਸਿਆ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਆਪਣੀਆਂ ਬ੍ਰਾਊਜ਼ਰ ਇਜਾਜ਼ਤਾਂ ਦੀ ਜਾਂਚ ਕਰੋ ਅਤੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    responseTimeoutError: 'ਜਵਾਬ ਬਹੁਤ ਸਮਾਂ ਲੈ ਰਿਹਾ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    noResponseError: 'ਕੋਈ ਜਵਾਬ ਨਹੀਂ ਮਿਲਿਆ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    connectionInterrupted: 'ਕਨੈਕਸ਼ਨ ਵਿੱਚ ਵਿਘਨ ਪਿਆ। ਤੁਸੀਂ ਆਪਣਾ ਸੁਨੇਹਾ ਦੁਬਾਰਾ ਭੇਜ ਸਕਦੇ ਹੋ।',
    videoStartError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਸ਼ੁਰੂ ਕਰਨ ਵਿੱਚ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoCameraNotSupported: 'ਕੈਮਰਾ ਸਮਰਥਿਤ ਨਹੀਂ ਹੈ। ਕਿਰਪਾ ਕਰਕੇ ਕਿਸੇ ਹੋਰ ਬ੍ਰਾਊਜ਼ਰ ਨਾਲ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoPauseError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਰੋਕਣ ਵਿੱਚ ਅਸਫਲ।',
    videoResumeError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਮੁੜ ਸ਼ੁਰੂ ਕਰਨ ਵਿੱਚ ਅਸਫਲ।',
    videoStopError: 'ਵੀਡੀਓ ਰਿਕਾਰਡਿੰਗ ਬੰਦ ਕਰਨ ਵਿੱਚ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoSwitchCameraError: 'ਕੈਮਰਾ ਬਦਲਣ ਵਿੱਚ ਅਸਫਲ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।',
    videoSendError: 'ਵੀਡੀਓ ਸੁਨੇਹਾ ਭੇਜਣ ਵਿੱਚ ਅਸਫਲ।',
    welcomeBack: 'ਵਾਪਸੀ ਤੇ ਸੁਆਗਤ ਹੈ!',
    unsupportedFileType: 'ਅਸਮਰਥਿਤ ਫਾਈਲ ਕਿਸਮ। ਕਿਰਪਾ ਕਰਕੇ JPG, PNG, GIF, ਜਾਂ MP4 ਫਾਈਲ ਅਪਲੋਡ ਕਰੋ।',
    fileTooLarge: 'ਇਹ ਫ਼ਾਈਲ ਬਹੁਤ ਵੱਡੀ ਹੈ। ਵੱਧ ਤੋਂ ਵੱਧ ਆਕਾਰ 200 MB ਹੈ।',
    audioCloseError: 'ਆਡੀਓ ਬੰਦ ਕਰਨ ਵਿੱਚ ਗਲਤੀ:',
  },
  fa: {
    telletDoesNotExistTitle: 'این لینک در دسترس نیست',
    projectDoesNotExistTitle: 'این لینک در دسترس نیست',
    projectDoesNotExistMessage:
      'متأسفیم، اما موفق به یافتن این مطالعه نشدیم. لطفاً لینک دریافتی خود را بررسی فرمایید یا با فرد دعوت‌کننده تماس بگیرید.',
    maximumParticipantsTitle: 'سپاسگزاریم',
    maximumParticipantsMessage:
      'از علاقه شما برای شرکت در این تحقیق سپاسگزاریم.\nتعداد پاسخ‌های مورد نیاز برای این مطالعه دریافت شده است و متأسفانه در حال حاضر امکان پذیرش شرکت‌کنندگان جدید وجود ندارد.\nاز زمانی که برای بازدید از این مطالعه اختصاص دادید و تمایل شما به همکاری در این پروژه مهم، قدردانی می‌کنیم.\nاز درک شما سپاسگزاریم.',
    somethingMissingTitle: 'این لینک در دسترس نیست',
    somethingMissingMessage:
      'متأسفیم، اما موفق به یافتن این مطالعه نشدیم. لطفاً لینک دریافتی خود را بررسی فرمایید یا با فرد دعوت‌کننده تماس بگیرید.',
    projectCompletedTitle: 'سپاسگزاریم',
    projectCompletedMessage:
      'از علاقه شما برای شرکت در این تحقیق سپاسگزاریم.\nاین مطالعه اکنون به پایان رسیده است و متأسفانه امکان پذیرش شرکت‌کنندگان جدید وجود ندارد.\nاز تمایل شما به همکاری قدردانی می‌کنیم و از درک شما سپاسگزاریم.',
    projectInDraftOrReadyStatusTitle: 'هنوز باز نشده است',
    projectInDraftOrReadyStatusMessage:
      'از علاقه شما برای شرکت در این تحقیق سپاسگزاریم.\nاین مطالعه هنوز برای شرکت باز نشده است. لطفاً بعداً دوباره تلاش فرمایید یا با فرد دعوت‌کننده تماس بگیرید.',
    redirectingIn: 'هدایت مجدد در',
    seconds: 'ثانیه...',
    cancel: 'لغو',
    returnToPanel: 'بازگشت به پنل',
    errorBoundaryTitle: 'مشکلی پیش آمد',
    errorBoundaryMessage:
      'یک خطای غیرمنتظره رخ داد. لطفاً برای ادامه صفحه را دوباره بارگذاری کنید.',
    errorBoundaryReloadButton: 'بارگذاری مجدد صفحه',
    messageProcessingError:
      'اوه! به نظر می‌رسد در پردازش پیام شما مشکل داریم. لطفاً دوباره ارسال کنید.',
    microphoneProblemTextChat:
      'به نظر می‌رسد مشکلی با میکروفون شما وجود دارد. لطفاً پاسخ‌های خود را با استفاده از دکمه صفحه‌کلید تایپ کنید.',
    microphoneProblemVoiceChat:
      'به نظر می‌رسد مشکلی با میکروفون شما وجود دارد. لطفاً مجوزهای مرورگر خود را بررسی کرده و دوباره امتحان کنید.',
    responseTimeoutError: 'پاسخ بیش از حد طول می‌کشد. لطفاً دوباره امتحان کنید.',
    noResponseError: 'هیچ پاسخی شناسایی نشد. لطفاً دوباره امتحان کنید.',
    connectionInterrupted: 'اتصال قطع شد. می‌توانید پیام خود را دوباره ارسال کنید.',
    videoStartError: 'شروع ضبط ویدیو ناموفق بود. لطفاً دوباره امتحان کنید.',
    videoCameraNotSupported: 'دوربین پشتیبانی نمی‌شود. لطفاً با مرورگر دیگری امتحان کنید.',
    videoPauseError: 'توقف ضبط ویدیو ناموفق بود.',
    videoResumeError: 'ادامه ضبط ویدیو ناموفق بود.',
    videoStopError: 'متوقف کردن ضبط ویدیو ناموفق بود. لطفاً دوباره امتحان کنید.',
    videoSwitchCameraError: 'تعویض دوربین ناموفق بود. لطفاً دوباره امتحان کنید.',
    videoSendError: 'ارسال پیام ویدیویی ناموفق بود.',
    welcomeBack: 'خوش آمدید!',
    unsupportedFileType:
      'نوع فایل پشتیبانی نمی‌شود. لطفاً یک فایل JPG، PNG، GIF یا MP4 بارگذاری کنید.',
    fileTooLarge: 'این فایل بسیار بزرگ است. حداکثر اندازه ۲۰۰ مگابایت است.',
    audioCloseError: 'خطا در بستن صدا:',
  },
  pl: {
    telletDoesNotExistTitle: 'Ten link jest niedostępny',
    projectDoesNotExistTitle: 'Ten link jest niedostępny',
    projectDoesNotExistMessage:
      'Przepraszamy, ale nie udało nam się znaleźć tego badania. Prosimy sprawdzić otrzymany link lub skontaktować się z osobą, która Państwa zaprosiła.',
    maximumParticipantsTitle: 'Dziękujemy',
    maximumParticipantsMessage:
      'Dziękujemy za zainteresowanie udziałem w tym badaniu.\nOtrzymaliśmy już wymaganą liczbę odpowiedzi, dlatego niestety nie możemy przyjąć kolejnych uczestników w tym momencie.\nDoceniamy, że poświęcili Państwo czas na odwiedzenie tego badania i chęć wniesienia wkładu w ten ważny projekt.\nDziękujemy za zrozumienie.',
    somethingMissingTitle: 'Ten link jest niedostępny',
    somethingMissingMessage:
      'Przepraszamy, ale nie udało nam się znaleźć tego badania. Prosimy sprawdzić otrzymany link lub skontaktować się z osobą, która Państwa zaprosiła.',
    projectCompletedTitle: 'Dziękujemy',
    projectCompletedMessage:
      'Dziękujemy za zainteresowanie udziałem w tym badaniu.\nTo badanie zostało już zakończone, dlatego niestety nie możemy przyjąć kolejnych uczestników.\nDoceniamy Państwa gotowość do udziału i dziękujemy za zrozumienie.',
    projectInDraftOrReadyStatusTitle: 'Jeszcze nieotwarte',
    projectInDraftOrReadyStatusMessage:
      'Dziękujemy za zainteresowanie udziałem w tym badaniu.\nTo badanie nie jest jeszcze otwarte do udziału. Prosimy spróbować ponownie później lub skontaktować się z osobą, która Państwa zaprosiła.',
    redirectingIn: 'Przekierowanie za',
    seconds: 'sekund...',
    cancel: 'Anuluj',
    returnToPanel: 'Powrót do panelu',
    errorBoundaryTitle: 'Coś poszło nie tak',
    errorBoundaryMessage: 'Wystąpił nieoczekiwany błąd. Aby kontynuować, odśwież stronę.',
    errorBoundaryReloadButton: 'Odśwież stronę',
    messageProcessingError:
      'Ups! Wygląda na to, że mamy problem z przetworzeniem Twojej wiadomości. Spróbuj wysłać ją ponownie.',
    microphoneProblemTextChat:
      'Wygląda na to, że jest problem z Twoim mikrofonem. Wpisz odpowiedzi za pomocą przycisku klawiatury.',
    microphoneProblemVoiceChat:
      'Wygląda na to, że jest problem z Twoim mikrofonem. Sprawdź uprawnienia przeglądarki i spróbuj ponownie.',
    responseTimeoutError: 'Odpowiedź trwa zbyt długo. Spróbuj ponownie.',
    noResponseError: 'Nie wykryto odpowiedzi. Spróbuj ponownie.',
    connectionInterrupted: 'Połączenie zostało przerwane. Możesz ponownie wysłać swoją wiadomość.',
    videoStartError: 'Nie udało się rozpocząć nagrywania wideo. Spróbuj ponownie.',
    videoCameraNotSupported: 'Kamera nie jest obsługiwana. Spróbuj z inną przeglądarką.',
    videoPauseError: 'Nie udało się wstrzymać nagrywania wideo.',
    videoResumeError: 'Nie udało się wznowić nagrywania wideo.',
    videoStopError: 'Nie udało się zatrzymać nagrywania wideo. Spróbuj ponownie.',
    videoSwitchCameraError: 'Nie udało się przełączyć kamery. Spróbuj ponownie.',
    videoSendError: 'Nie udało się wysłać wiadomości wideo.',
    welcomeBack: 'Witaj ponownie!',
    unsupportedFileType: 'Nieobsługiwany typ pliku. Prześlij plik JPG, PNG, GIF lub MP4.',
    fileTooLarge: 'Ten plik jest za duży. Maksymalny rozmiar to 200 MB.',
    audioCloseError: 'Błąd podczas zamykania dźwięku:',
  },
  pt: {
    telletDoesNotExistTitle: 'Este link não está disponível',
    projectDoesNotExistTitle: 'Este link não está disponível',
    projectDoesNotExistMessage:
      'Lamentamos, mas não foi possível encontrar este estudo. Por favor, verifique o link que recebeu ou contacte a pessoa que o convidou.',
    maximumParticipantsTitle: 'Obrigado',
    maximumParticipantsMessage:
      'Agradecemos o seu interesse em participar neste estudo.\nJá recebemos o número necessário de respostas, pelo que infelizmente não podemos aceitar mais participantes neste momento.\nAgradecemos o tempo que dedicou a visitar este estudo e a sua disponibilidade para contribuir para este importante projeto.\nObrigado pela sua compreensão.',
    somethingMissingTitle: 'Este link não está disponível',
    somethingMissingMessage:
      'Lamentamos, mas não foi possível encontrar este estudo. Por favor, verifique o link que recebeu ou contacte a pessoa que o convidou.',
    projectCompletedTitle: 'Obrigado',
    projectCompletedMessage:
      'Agradecemos o seu interesse em participar neste estudo.\nEste estudo já foi encerrado, pelo que infelizmente não podemos aceitar mais participantes.\nAgradecemos a sua disponibilidade para contribuir e obrigado pela sua compreensão.',
    projectInDraftOrReadyStatusTitle: 'Ainda não está disponível',
    projectInDraftOrReadyStatusMessage:
      'Agradecemos o seu interesse em participar neste estudo.\nEste estudo ainda não está disponível para participação. Por favor, tente novamente mais tarde ou contacte a pessoa que o convidou.',
    redirectingIn: 'A redirecionar em',
    seconds: 'segundos...',
    cancel: 'Cancelar',
    returnToPanel: 'Voltar ao Painel',
    errorBoundaryTitle: 'Algo correu mal',
    errorBoundaryMessage:
      'Ocorreu um erro inesperado. Por favor, recarregue a página para continuar.',
    errorBoundaryReloadButton: 'Recarregar página',
    messageProcessingError:
      'Ups! Parece que estamos a ter problemas em processar a sua mensagem. Por favor, tente reenviá-la.',
    microphoneProblemTextChat:
      'Parece haver um problema com o seu microfone. Por favor, escreva as suas respostas usando o botão do teclado.',
    microphoneProblemVoiceChat:
      'Parece haver um problema com o seu microfone. Por favor, verifique as permissões do seu navegador e tente novamente.',
    responseTimeoutError: 'A resposta está a demorar demasiado. Por favor, tente novamente.',
    noResponseError: 'Nenhuma resposta detetada. Por favor, tente novamente.',
    connectionInterrupted: 'Ligação interrompida. Pode reenviar a sua mensagem.',
    videoStartError: 'Falha ao iniciar a gravação de vídeo. Por favor, tente novamente.',
    videoCameraNotSupported:
      'A câmara não é suportada. Por favor, tente com um navegador diferente.',
    videoPauseError: 'Falha ao pausar a gravação de vídeo.',
    videoResumeError: 'Falha ao retomar a gravação de vídeo.',
    videoStopError: 'Falha ao parar a gravação de vídeo. Por favor, tente novamente.',
    videoSwitchCameraError: 'Falha ao mudar de câmara. Por favor, tente novamente.',
    videoSendError: 'Falha ao enviar a mensagem de vídeo.',
    welcomeBack: 'Bem-vindo de volta!',
    unsupportedFileType:
      'Tipo de ficheiro não suportado. Por favor, carregue um ficheiro JPG, PNG, GIF ou MP4.',
    fileTooLarge: 'Este ficheiro é demasiado grande. O tamanho máximo é de 200 MB.',
    audioCloseError: 'Erro ao fechar o áudio:',
  },
  tl: {
    telletDoesNotExistTitle: 'Hindi magagamit ang link na ito',
    projectDoesNotExistTitle: 'Hindi magagamit ang link na ito',
    projectDoesNotExistMessage:
      'Paumanhin, hindi namin mahanap ang pag-aaral na ito. Pakisuri ang link na ibinigay sa inyo, o makipag-ugnayan sa taong nag-imbita sa inyo.',
    maximumParticipantsTitle: 'Maraming salamat po',
    maximumParticipantsMessage:
      'Salamat sa inyong interes na lumahok sa pananaliksik na ito.\nNaabot na namin ang kinakailangang bilang ng mga tugon, kaya sa ngayon ay hindi na kami tumatanggap ng mga bagong kalahok.\nPinahahalagahan namin ang inyong oras at ang kagustuhang tumulong sa mahalagang proyektong ito.\nMaraming salamat po sa inyong pag-unawa.',
    somethingMissingTitle: 'Hindi magagamit ang link na ito',
    somethingMissingMessage:
      'Paumanhin, hindi namin mahanap ang pag-aaral na ito. Pakisuri ang link na ibinigay sa inyo, o makipag-ugnayan sa taong nag-imbita sa inyo.',
    projectCompletedTitle: 'Maraming salamat po',
    projectCompletedMessage:
      'Salamat sa inyong interes na lumahok sa pananaliksik na ito.\nSarado na ang pag-aaral na ito, kaya sa ngayon ay hindi na kami tumatanggap ng mga bagong kalahok.\nPinahahalagahan namin ang inyong kagustuhang tumulong, at maraming salamat po sa inyong pag-unawa.',
    projectInDraftOrReadyStatusTitle: 'Hindi pa bukas',
    projectInDraftOrReadyStatusMessage:
      'Salamat sa inyong interes na lumahok sa pananaliksik na ito.\nHindi pa bukas ang pag-aaral na ito para sa mga kalahok. Mangyaring subukan muli sa ibang pagkakataon, o makipag-ugnayan sa taong nag-imbita sa inyo.',
    redirectingIn: 'Ida-redirect sa loob ng',
    seconds: 'segundo...',
    cancel: 'Kanselahin',
    returnToPanel: 'Bumalik sa Panel',
    errorBoundaryTitle: 'May nangyaring mali',
    errorBoundaryMessage:
      'May naganap na hindi inaasahang error. Mangyaring i-reload ang pahina upang magpatuloy.',
    errorBoundaryReloadButton: 'I-reload ang pahina',
    messageProcessingError:
      'Naku! Mukhang may problema kami sa pagproseso ng iyong mensahe. Mangyaring subukang ipadala muli.',
    microphoneProblemTextChat:
      'Mukhang may problema sa iyong mikropono. Mangyaring i-type ang iyong mga sagot gamit ang pindutan ng keyboard.',
    microphoneProblemVoiceChat:
      'Mukhang may problema sa iyong mikropono. Mangyaring suriin ang mga pahintulot ng iyong browser at subukang muli.',
    responseTimeoutError: 'Masyadong mahaba ang pagtugon. Mangyaring subukang muli.',
    noResponseError: 'Walang natukoy na tugon. Mangyaring subukang muli.',
    connectionInterrupted: 'Naantala ang koneksyon. Maaari mong ipadala muli ang iyong mensahe.',
    videoStartError: 'Nabigong simulan ang pag-record ng video. Mangyaring subukang muli.',
    videoCameraNotSupported: 'Hindi suportado ang kamera. Mangyaring subukan sa ibang browser.',
    videoPauseError: 'Nabigong i-pause ang pag-record ng video.',
    videoResumeError: 'Nabigong ipagpatuloy ang pag-record ng video.',
    videoStopError: 'Nabigong ihinto ang pag-record ng video. Mangyaring subukang muli.',
    videoSwitchCameraError: 'Nabigong magpalit ng kamera. Mangyaring subukang muli.',
    videoSendError: 'Nabigong ipadala ang video message.',
    welcomeBack: 'Maligayang pagbabalik!',
    unsupportedFileType:
      'Hindi suportadong uri ng file. Mangyaring mag-upload ng JPG, PNG, GIF, o MP4 file.',
    fileTooLarge: 'Masyadong malaki ang file na ito. Ang maximum na laki ay 200 MB.',
    audioCloseError: 'Error sa pagsasara ng audio:',
  },
  ro: {
    telletDoesNotExistTitle: 'Acest link nu este disponibil',
    projectDoesNotExistTitle: 'Acest link nu este disponibil',
    projectDoesNotExistMessage:
      'Ne pare rău, nu am putut găsi acest studiu. Vă rugăm să verificați linkul primit sau să contactați persoana care v-a invitat.',
    maximumParticipantsTitle: 'Vă mulțumim',
    maximumParticipantsMessage:
      'Vă mulțumim pentru interesul acordat participării la această cercetare.\nAm primit deja numărul necesar de răspunsuri, astfel că, din păcate, nu mai putem accepta alți participanți în acest moment.\nApreciem timpul acordat pentru a vizita acest studiu și dorința dumneavoastră de a contribui la acest proiect important.\nVă mulțumim pentru înțelegere.',
    somethingMissingTitle: 'Acest link nu este disponibil',
    somethingMissingMessage:
      'Ne pare rău, nu am putut găsi acest studiu. Vă rugăm să verificați linkul primit sau să contactați persoana care v-a invitat.',
    projectCompletedTitle: 'Vă mulțumim',
    projectCompletedMessage:
      'Vă mulțumim pentru interesul acordat participării la această cercetare.\nAcest studiu a fost închis, astfel că, din păcate, nu mai putem accepta alți participanți.\nApreciem dorința dumneavoastră de a contribui și vă mulțumim pentru înțelegere.',
    projectInDraftOrReadyStatusTitle: 'Nu este încă deschis',
    projectInDraftOrReadyStatusMessage:
      'Vă mulțumim pentru interesul acordat participării la această cercetare.\nAcest studiu nu este încă deschis pentru participare. Vă rugăm să reveniți mai târziu sau să contactați persoana care v-a invitat.',
    redirectingIn: 'Redirecționare în',
    seconds: 'secunde...',
    cancel: 'Anulează',
    returnToPanel: 'Înapoi la Panou',
    errorBoundaryTitle: 'Ceva nu a mers bine',
    errorBoundaryMessage:
      'A apărut o eroare neașteptată. Vă rugăm să reîncărcați pagina pentru a continua.',
    errorBoundaryReloadButton: 'Reîncarcă pagina',
    messageProcessingError:
      'Ups! Se pare că avem probleme în procesarea mesajului tău. Te rugăm să încerci să-l retrimiți.',
    microphoneProblemTextChat:
      'Se pare că există o problemă cu microfonul tău. Te rugăm să scrii răspunsurile folosind butonul tastaturii.',
    microphoneProblemVoiceChat:
      'Se pare că există o problemă cu microfonul tău. Te rugăm să verifici permisiunile browserului și să încerci din nou.',
    responseTimeoutError: 'Răspunsul durează prea mult. Te rugăm să încerci din nou.',
    noResponseError: 'Niciun răspuns detectat. Te rugăm să încerci din nou.',
    connectionInterrupted: 'Conexiunea a fost întreruptă. Poți retrimite mesajul tău.',
    videoStartError: 'Nu s-a putut porni înregistrarea video. Te rugăm să încerci din nou.',
    videoCameraNotSupported: 'Camera nu este acceptată. Te rugăm să încerci cu un alt browser.',
    videoPauseError: 'Nu s-a putut întrerupe înregistrarea video.',
    videoResumeError: 'Nu s-a putut relua înregistrarea video.',
    videoStopError: 'Nu s-a putut opri înregistrarea video. Te rugăm să încerci din nou.',
    videoSwitchCameraError: 'Nu s-a putut schimba camera. Te rugăm să încerci din nou.',
    videoSendError: 'Nu s-a putut trimite mesajul video.',
    welcomeBack: 'Bine ai revenit!',
    unsupportedFileType:
      'Tip de fișier neacceptat. Te rugăm să încarci un fișier JPG, PNG, GIF sau MP4.',
    fileTooLarge: 'Acest fișier este prea mare. Dimensiunea maximă este de 200 MB.',
    audioCloseError: 'Eroare la închiderea audio:',
  },
  ru: {
    telletDoesNotExistTitle: 'Ссылка недоступна',
    projectDoesNotExistTitle: 'Ссылка недоступна',
    projectDoesNotExistMessage:
      'К сожалению, нам не удалось найти это исследование. Пожалуйста, проверьте полученную ссылку или свяжитесь с человеком, который пригласил Вас.',
    maximumParticipantsTitle: 'Благодарим Вас',
    maximumParticipantsMessage:
      'Благодарим Вас за интерес к участию в данном исследовании.\nВ настоящее время мы уже получили необходимое количество ответов, поэтому, к сожалению, не можем принять новых участников.\nМы ценим, что Вы нашли время ознакомиться с этим проектом и были готовы внести свой вклад в важное исследование.\nБлагодарим за понимание.',
    somethingMissingTitle: 'Ссылка недоступна',
    somethingMissingMessage:
      'К сожалению, нам не удалось найти это исследование. Пожалуйста, проверьте полученную ссылку или свяжитесь с человеком, который пригласил Вас.',
    projectCompletedTitle: 'Благодарим Вас',
    projectCompletedMessage:
      'Благодарим Вас за интерес к участию в данном исследовании.\nВ настоящее время исследование завершено, и мы, к сожалению, не можем принять новых участников.\nМы ценим Вашу готовность помочь и благодарим за понимание.',
    projectInDraftOrReadyStatusTitle: 'Пока не открыто',
    projectInDraftOrReadyStatusMessage:
      'Благодарим Вас за интерес к участию в данном исследовании.\nВ настоящее время участие в этом исследовании ещё не открыто. Пожалуйста, попробуйте позже или свяжитесь с человеком, который пригласил Вас.',
    redirectingIn: 'Перенаправление через',
    seconds: 'секунд...',
    cancel: 'Отмена',
    returnToPanel: 'Вернуться на панель',
    errorBoundaryTitle: 'Что-то пошло не так',
    errorBoundaryMessage:
      'Произошла непредвиденная ошибка. Пожалуйста, перезагрузите страницу, чтобы продолжить.',
    errorBoundaryReloadButton: 'Перезагрузить страницу',
    messageProcessingError:
      'Упс! Похоже, возникли проблемы с обработкой вашего сообщения. Пожалуйста, попробуйте отправить его снова.',
    microphoneProblemTextChat:
      'Похоже, возникла проблема с вашим микрофоном. Пожалуйста, введите свои ответы с помощью клавиатуры.',
    microphoneProblemVoiceChat:
      'Похоже, возникла проблема с вашим микрофоном. Пожалуйста, проверьте разрешения браузера и попробуйте снова.',
    responseTimeoutError: 'Ответ занимает слишком много времени. Пожалуйста, попробуйте снова.',
    noResponseError: 'Ответ не обнаружен. Пожалуйста, попробуйте снова.',
    connectionInterrupted: 'Соединение прервано. Вы можете повторно отправить своё сообщение.',
    videoStartError: 'Не удалось начать запись видео. Пожалуйста, попробуйте снова.',
    videoCameraNotSupported: 'Камера не поддерживается. Пожалуйста, попробуйте в другом браузере.',
    videoPauseError: 'Не удалось приостановить запись видео.',
    videoResumeError: 'Не удалось возобновить запись видео.',
    videoStopError: 'Не удалось остановить запись видео. Пожалуйста, попробуйте снова.',
    videoSwitchCameraError: 'Не удалось переключить камеру. Пожалуйста, попробуйте снова.',
    videoSendError: 'Не удалось отправить видеосообщение.',
    welcomeBack: 'С возвращением!',
    unsupportedFileType:
      'Неподдерживаемый тип файла. Пожалуйста, загрузите файл JPG, PNG, GIF или MP4.',
    fileTooLarge: 'Этот файл слишком большой. Максимальный размер — 200 МБ.',
    audioCloseError: 'Ошибка закрытия аудио:',
  },
  sr: {
    telletDoesNotExistTitle: 'Овај линк није доступан',
    projectDoesNotExistTitle: 'Овај линк није доступан',
    projectDoesNotExistMessage:
      'Жао нам је, али не можемо да пронађемо ову студију. Молимо вас да проверите линк који сте добили или контактирајте особу која вас је позвала.',
    maximumParticipantsTitle: 'Хвала вам',
    maximumParticipantsMessage:
      'Хвала вам на интересовању за учешће у овом истраживању.\nДостигли смо потребан број одговора, тако да тренутно, нажалост, не можемо прихватити нове учеснике.\nВеома ценимо што сте одвојили време да посетите ову студију и што сте били спремни да дате свој допринос овом важном пројекту.\nХвала вам на разумевању.',
    somethingMissingTitle: 'Овај линк није доступан',
    somethingMissingMessage:
      'Жао нам је, али не можемо да пронађемо ову студију. Молимо вас да проверите линк који сте добили или контактирајте особу која вас је позвала.',
    projectCompletedTitle: 'Хвала вам',
    projectCompletedMessage:
      'Хвала вам на интересовању за учешће у овом истраживању.\nОва студија је сада затворена, тако да, нажалост, не можемо прихватити нове учеснике.\nВеома ценимо вашу спремност да учествујете и захваљујемо вам на разумевању.',
    projectInDraftOrReadyStatusTitle: 'Још није отворено',
    projectInDraftOrReadyStatusMessage:
      'Хвала вам на интересовању за учешће у овом истраживању.\nОва студија још увек није отворена за учешће. Молимо вас да покушате поново касније или контактирајте особу која вас је позвала.',
    redirectingIn: 'Preusmeravanje za',
    seconds: 'sekundi...',
    cancel: 'Otkaži',
    returnToPanel: 'Povratak na panel',
    errorBoundaryTitle: 'Nešto je pošlo po zlu',
    errorBoundaryMessage:
      'Došlo je do neočekivane greške. Molimo vas da ponovo učitate stranicu da biste nastavili.',
    errorBoundaryReloadButton: 'Ponovo učitaj stranicu',
    messageProcessingError:
      'Ups! Izgleda da imamo problem sa obradom vaše poruke. Molimo vas da je ponovo pošaljete.',
    microphoneProblemTextChat:
      'Izgleda da postoji problem sa vašim mikrofonom. Molimo vas da otkucate odgovore koristeći tastaturu.',
    microphoneProblemVoiceChat:
      'Izgleda da postoji problem sa vašim mikrofonom. Molimo vas da proverite dozvole pregledača i pokušate ponovo.',
    responseTimeoutError: 'Odgovor predugo traje. Molimo vas da pokušate ponovo.',
    noResponseError: 'Odgovor nije otkriven. Molimo vas da pokušate ponovo.',
    connectionInterrupted: 'Veza je prekinuta. Možete ponovo poslati svoju poruku.',
    videoStartError: 'Nije uspelo pokretanje video snimanja. Molimo vas da pokušate ponovo.',
    videoCameraNotSupported: 'Kamera nije podržana. Molimo vas da pokušate sa drugim pregledačem.',
    videoPauseError: 'Nije uspelo pauziranje video snimanja.',
    videoResumeError: 'Nije uspelo nastavljanje video snimanja.',
    videoStopError: 'Nije uspelo zaustavljanje video snimanja. Molimo vas da pokušate ponovo.',
    videoSwitchCameraError: 'Nije uspela promena kamere. Molimo vas da pokušate ponovo.',
    videoSendError: 'Nije uspelo slanje video poruke.',
    welcomeBack: 'Dobrodošli nazad!',
    unsupportedFileType: 'Nepodržan tip fajla. Molimo vas da otpremite JPG, PNG, GIF ili MP4 fajl.',
    fileTooLarge: 'Ova datoteka je prevelika. Maksimalna veličina je 200 MB.',
    audioCloseError: 'Greška pri zatvaranju audio zapisa:',
  },
  sk: {
    telletDoesNotExistTitle: 'Tento odkaz nie je dostupný',
    projectDoesNotExistTitle: 'Tento odkaz nie je dostupný',
    projectDoesNotExistMessage:
      'Ospravedlňujeme sa, ale túto štúdiu sa nám nepodarilo nájsť. Skontrolujte, prosím, odkaz, ktorý ste dostali, alebo kontaktujte osobu, ktorá vás pozvala.',
    maximumParticipantsTitle: 'Ďakujeme',
    maximumParticipantsMessage:
      'Ďakujeme za váš záujem zúčastniť sa tohto výskumu.\nPožadovaný počet odpovedí sme už získali, preto v tejto chvíli, žiaľ, nemôžeme prijať ďalších účastníkov.\nVážime si, že ste si našli čas navštíviť túto štúdiu a ochotu prispieť k tomuto dôležitému projektu.\nĎakujeme za pochopenie.',
    somethingMissingTitle: 'Tento odkaz nie je dostupný',
    somethingMissingMessage:
      'Ospravedlňujeme sa, ale túto štúdiu sa nám nepodarilo nájsť. Skontrolujte, prosím, odkaz, ktorý ste dostali, alebo kontaktujte osobu, ktorá vás pozvala.',
    projectCompletedTitle: 'Ďakujeme',
    projectCompletedMessage:
      'Ďakujeme za váš záujem zúčastniť sa tohto výskumu.\nTáto štúdia je už ukončená, preto v tejto chvíli, žiaľ, nemôžeme prijať ďalších účastníkov.\nVážime si vašu ochotu prispieť a ďakujeme za pochopenie.',
    projectInDraftOrReadyStatusTitle: 'Ešte nie je otvorené',
    projectInDraftOrReadyStatusMessage:
      'Ďakujeme za váš záujem zúčastniť sa tohto výskumu.\nTáto štúdia ešte nie je otvorená na účasť. Skúste to, prosím, neskôr alebo kontaktujte osobu, ktorá vás pozvala.',
    redirectingIn: 'Presmerovanie o',
    seconds: 'sekúnd...',
    cancel: 'Zrušiť',
    returnToPanel: 'Späť na panel',
    errorBoundaryTitle: 'Niečo sa pokazilo',
    errorBoundaryMessage:
      'Vyskytla sa neočakávaná chyba. Pre pokračovanie prosím znovu načítajte stránku.',
    errorBoundaryReloadButton: 'Znovu načítať stránku',
    messageProcessingError:
      'Ups! Vyzerá to tak, že máme problém so spracovaním vašej správy. Skúste ju prosím poslať znova.',
    microphoneProblemTextChat:
      'Zdá sa, že je problém s vaším mikrofónom. Zadajte svoje odpovede pomocou klávesnice.',
    microphoneProblemVoiceChat:
      'Zdá sa, že je problém s vaším mikrofónom. Skontrolujte prosím povolenia prehliadača a skúste to znova.',
    responseTimeoutError: 'Odpoveď trvá príliš dlho. Skúste to prosím znova.',
    noResponseError: 'Nezistila sa žiadna odpoveď. Skúste to prosím znova.',
    connectionInterrupted: 'Spojenie bolo prerušené. Svoju správu môžete znova odoslať.',
    videoStartError: 'Nepodarilo sa spustiť nahrávanie videa. Skúste to prosím znova.',
    videoCameraNotSupported: 'Kamera nie je podporovaná. Skúste to prosím v inom prehliadači.',
    videoPauseError: 'Nepodarilo sa pozastaviť nahrávanie videa.',
    videoResumeError: 'Nepodarilo sa obnoviť nahrávanie videa.',
    videoStopError: 'Nepodarilo sa zastaviť nahrávanie videa. Skúste to prosím znova.',
    videoSwitchCameraError: 'Nepodarilo sa prepnúť kameru. Skúste to prosím znova.',
    videoSendError: 'Nepodarilo sa odoslať video správu.',
    welcomeBack: 'Vitajte späť!',
    unsupportedFileType: 'Nepodporovaný typ súboru. Nahrajte prosím súbor JPG, PNG, GIF alebo MP4.',
    fileTooLarge: 'Tento súbor je príliš veľký. Maximálna veľkosť je 200 MB.',
    audioCloseError: 'Chyba pri zatváraní zvuku:',
  },
  sl: {
    telletDoesNotExistTitle: 'Ta povezava ni na voljo',
    projectDoesNotExistTitle: 'Ta povezava ni na voljo',
    projectDoesNotExistMessage:
      'Opravičujemo se, vendar te študije nismo našli. Prosimo, preverite prejeto povezavo ali se obrnite na osebo, ki vas je povabila.',
    maximumParticipantsTitle: 'Hvala',
    maximumParticipantsMessage:
      'Hvala za vaše zanimanje za sodelovanje v tej raziskavi.\nPrejeli smo že dovolj odgovorov, zato žal trenutno ne sprejemamo več novih udeležencev.\nCenimo, da ste si vzeli čas za obisk te študije in ste bili pripravljeni prispevati k temu pomembnemu projektu.\nHvala za vaše razumevanje.',
    somethingMissingTitle: 'Ta povezava ni na voljo',
    somethingMissingMessage:
      'Opravičujemo se, vendar te študije nismo našli. Prosimo, preverite prejeto povezavo ali se obrnite na osebo, ki vas je povabila.',
    projectCompletedTitle: 'Hvala',
    projectCompletedMessage:
      'Hvala za vaše zanimanje za sodelovanje v tej raziskavi.\nTa študija je zaključena, zato žal ne sprejemamo več novih udeležencev.\nCenimo vašo pripravljenost za sodelovanje in se vam zahvaljujemo za razumevanje.',
    projectInDraftOrReadyStatusTitle: 'Še ni odprto',
    projectInDraftOrReadyStatusMessage:
      'Hvala za vaše zanimanje za sodelovanje v tej raziskavi.\nTa študija še ni odprta za sodelovanje. Prosimo, poskusite znova kasneje ali se obrnite na osebo, ki vas je povabila.',
    redirectingIn: 'Preusmeritev čez',
    seconds: 'sekund...',
    cancel: 'Prekliči',
    returnToPanel: 'Nazaj na panel',
    errorBoundaryTitle: 'Nekaj je šlo narobe',
    errorBoundaryMessage:
      'Prišlo je do nepričakovane napake. Za nadaljevanje znova naložite stran.',
    errorBoundaryReloadButton: 'Znova naloži stran',
    messageProcessingError:
      'Ups! Videti je, da imamo težave z obdelavo vašega sporočila. Poskusite ga znova poslati.',
    microphoneProblemTextChat:
      'Videti je, da je težava z vašim mikrofonom. Prosimo, vtipkajte svoje odgovore s tipkovnico.',
    microphoneProblemVoiceChat:
      'Videti je, da je težava z vašim mikrofonom. Preverite dovoljenja brskalnika in poskusite znova.',
    responseTimeoutError: 'Odziv traja predolgo. Poskusite znova.',
    noResponseError: 'Odziv ni bil zaznan. Poskusite znova.',
    connectionInterrupted: 'Povezava je bila prekinjena. Svoje sporočilo lahko znova pošljete.',
    videoStartError: 'Snemanja videa ni bilo mogoče začeti. Poskusite znova.',
    videoCameraNotSupported: 'Kamera ni podprta. Poskusite z drugim brskalnikom.',
    videoPauseError: 'Snemanja videa ni bilo mogoče začasno ustaviti.',
    videoResumeError: 'Snemanja videa ni bilo mogoče nadaljevati.',
    videoStopError: 'Snemanja videa ni bilo mogoče zaustaviti. Poskusite znova.',
    videoSwitchCameraError: 'Kamere ni bilo mogoče preklopiti. Poskusite znova.',
    videoSendError: 'Video sporočila ni bilo mogoče poslati.',
    welcomeBack: 'Dobrodošli nazaj!',
    unsupportedFileType: 'Nepodprta vrsta datoteke. Naložite datoteko JPG, PNG, GIF ali MP4.',
    fileTooLarge: 'Ta datoteka je prevelika. Največja velikost je 200 MB.',
    audioCloseError: 'Napaka pri zapiranju zvoka:',
  },
  es: {
    telletDoesNotExistTitle: 'Este enlace no está disponible',
    projectDoesNotExistTitle: 'Este enlace no está disponible',
    projectDoesNotExistMessage:
      'Lo sentimos, pero no hemos podido encontrar este estudio. Por favor, compruebe el enlace que le proporcionaron o póngase en contacto con la persona que le invitó.',
    maximumParticipantsTitle: 'Gracias',
    maximumParticipantsMessage:
      'Gracias por su interés en participar en esta investigación.\nYa hemos recibido el número necesario de respuestas, por lo que lamentablemente no podemos aceptar más participantes en este momento.\nAgradecemos que se haya tomado el tiempo de visitar este estudio y su disposición para contribuir a este importante proyecto.\nGracias por su comprensión.',
    somethingMissingTitle: 'Este enlace no está disponible',
    somethingMissingMessage:
      'Lo sentimos, pero no hemos podido encontrar este estudio. Por favor, compruebe el enlace que le proporcionaron o póngase en contacto con la persona que le invitó.',
    projectCompletedTitle: 'Gracias',
    projectCompletedMessage:
      'Gracias por su interés en participar en esta investigación.\nEste estudio ya se ha cerrado, por lo que lamentablemente no podemos aceptar más participantes.\nAgradecemos su disposición para contribuir y le agradecemos su comprensión.',
    projectInDraftOrReadyStatusTitle: 'Aún no está abierto',
    projectInDraftOrReadyStatusMessage:
      'Gracias por su interés en participar en esta investigación.\nEste estudio aún no está abierto para la participación. Por favor, inténtelo de nuevo más tarde o póngase en contacto con la persona que le invitó.',
    redirectingIn: 'Redirigiendo en',
    seconds: 'segundos...',
    cancel: 'Cancelar',
    returnToPanel: 'Volver al panel',
    errorBoundaryTitle: 'Algo salió mal',
    errorBoundaryMessage:
      'Se produjo un error inesperado. Por favor, recarga la página para continuar.',
    errorBoundaryReloadButton: 'Recargar página',
    messageProcessingError:
      '¡Vaya! Parece que tenemos problemas para procesar tu mensaje. Por favor, intenta enviarlo de nuevo.',
    microphoneProblemTextChat:
      'Parece que hay un problema con tu micrófono. Por favor, escribe tus respuestas con el teclado.',
    microphoneProblemVoiceChat:
      'Parece que hay un problema con tu micrófono. Por favor, comprueba los permisos de tu navegador e inténtalo de nuevo.',
    responseTimeoutError: 'La respuesta está tardando demasiado. Por favor, inténtalo de nuevo.',
    noResponseError: 'No se detectó ninguna respuesta. Por favor, inténtalo de nuevo.',
    connectionInterrupted: 'Conexión interrumpida. Puedes volver a enviar tu mensaje.',
    videoStartError: 'No se pudo iniciar la grabación de vídeo. Por favor, inténtalo de nuevo.',
    videoCameraNotSupported:
      'La cámara no es compatible. Por favor, inténtalo con un navegador diferente.',
    videoPauseError: 'No se pudo pausar la grabación de vídeo.',
    videoResumeError: 'No se pudo reanudar la grabación de vídeo.',
    videoStopError: 'No se pudo detener la grabación de vídeo. Por favor, inténtalo de nuevo.',
    videoSwitchCameraError: 'No se pudo cambiar de cámara. Por favor, inténtalo de nuevo.',
    videoSendError: 'No se pudo enviar el mensaje de vídeo.',
    welcomeBack: '¡Bienvenido de nuevo!',
    unsupportedFileType:
      'Tipo de archivo no compatible. Por favor, sube un archivo JPG, PNG, GIF o MP4.',
    fileTooLarge: 'Este archivo es demasiado grande. El tamaño máximo es de 200 MB.',
    audioCloseError: 'Error al cerrar el audio:',
  },
  sv: {
    telletDoesNotExistTitle: 'Denna länk är inte tillgänglig',
    projectDoesNotExistTitle: 'Denna länk är inte tillgänglig',
    projectDoesNotExistMessage:
      'Vi beklagar, men vi kunde inte hitta denna studie. Kontrollera länken ni fått eller kontakta den person som bjöd in er.',
    maximumParticipantsTitle: 'Tack',
    maximumParticipantsMessage:
      'Tack för ert intresse för att delta i denna forskning.\nVi har nu tagit emot det antal svar som behövs och kan tyvärr inte ta emot fler deltagare för tillfället.\nVi uppskattar att ni tog er tid att besöka denna studie och er vilja att bidra till detta viktiga projekt.\nTack för er förståelse.',
    somethingMissingTitle: 'Denna länk är inte tillgänglig',
    somethingMissingMessage:
      'Vi beklagar, men vi kunde inte hitta denna studie. Kontrollera länken ni fått eller kontakta den person som bjöd in er.',
    projectCompletedTitle: 'Tack',
    projectCompletedMessage:
      'Tack för ert intresse för att delta i denna forskning.\nDenna studie är nu avslutad och vi kan tyvärr inte ta emot fler deltagare.\nVi uppskattar er vilja att bidra och tackar för er förståelse.',
    projectInDraftOrReadyStatusTitle: 'Inte öppen ännu',
    projectInDraftOrReadyStatusMessage:
      'Tack för ert intresse för att delta i denna forskning.\nDenna studie är ännu inte öppen för deltagande. Vänligen försök igen senare eller kontakta den person som bjöd in er.',
    redirectingIn: 'Omdirigerar om',
    seconds: 'sekunder...',
    cancel: 'Avbryt',
    returnToPanel: 'Tillbaka till panelen',
    errorBoundaryTitle: 'Något gick fel',
    errorBoundaryMessage: 'Ett oväntat fel uppstod. Vänligen ladda om sidan för att fortsätta.',
    errorBoundaryReloadButton: 'Ladda om sidan',
    messageProcessingError:
      'Hoppsan! Det verkar som att vi har problem med att bearbeta ditt meddelande. Försök att skicka det igen.',
    microphoneProblemTextChat:
      'Det verkar finnas ett problem med din mikrofon. Vänligen skriv dina svar med tangentbordet.',
    microphoneProblemVoiceChat:
      'Det verkar finnas ett problem med din mikrofon. Vänligen kontrollera dina webbläsarbehörigheter och försök igen.',
    responseTimeoutError: 'Svaret tar för lång tid. Försök igen.',
    noResponseError: 'Inget svar upptäcktes. Försök igen.',
    connectionInterrupted: 'Anslutningen avbröts. Du kan skicka ditt meddelande igen.',
    videoStartError: 'Det gick inte att starta videoinspelningen. Försök igen.',
    videoCameraNotSupported: 'Kameran stöds inte. Försök med en annan webbläsare.',
    videoPauseError: 'Det gick inte att pausa videoinspelningen.',
    videoResumeError: 'Det gick inte att återuppta videoinspelningen.',
    videoStopError: 'Det gick inte att stoppa videoinspelningen. Försök igen.',
    videoSwitchCameraError: 'Det gick inte att byta kamera. Försök igen.',
    videoSendError: 'Det gick inte att skicka videomeddelandet.',
    welcomeBack: 'Välkommen tillbaka!',
    unsupportedFileType:
      'Filtypen stöds inte. Vänligen ladda upp en JPG-, PNG-, GIF- eller MP4-fil.',
    fileTooLarge: 'Den här filen är för stor. Maximal storlek är 200 MB.',
    audioCloseError: 'Fel vid stängning av ljud:',
  },
  sw: {
    telletDoesNotExistTitle: 'Kiungo hiki hakipatikani',
    projectDoesNotExistTitle: 'Kiungo hiki hakipatikani',
    projectDoesNotExistMessage:
      'Samahani, hatukuweza kupata utafiti huu. Tafadhali hakikisha kiungo ulichopokea ni sahihi, au wasiliana na mtu aliyekualika.',
    maximumParticipantsTitle: 'Asante',
    maximumParticipantsMessage:
      'Asante kwa kuonesha nia ya kushiriki katika utafiti huu.\nTayari tumepokea idadi ya majibu tunayohitaji, hivyo kwa sasa hatuwezi kupokea washiriki wapya.\nTunathamini muda wako na utayari wako wa kuchangia mradi huu muhimu.\nAsante kwa uelewa wako.',
    somethingMissingTitle: 'Kiungo hiki hakipatikani',
    somethingMissingMessage:
      'Samahani, hatukuweza kupata utafiti huu. Tafadhali hakikisha kiungo ulichopokea ni sahihi, au wasiliana na mtu aliyekualika.',
    projectCompletedTitle: 'Asante',
    projectCompletedMessage:
      'Asante kwa kuonesha nia ya kushiriki katika utafiti huu.\nUtafiti huu sasa umefungwa, hivyo hatuwezi kupokea washiriki wapya.\nTunashukuru kwa utayari wako wa kuchangia, na tunakushukuru kwa uelewa wako.',
    projectInDraftOrReadyStatusTitle: 'Bado haijafunguliwa',
    projectInDraftOrReadyStatusMessage:
      'Asante kwa kuonesha nia ya kushiriki katika utafiti huu.\nUtafiti huu bado haujafunguliwa kwa ushiriki. Tafadhali jaribu tena baadaye, au wasiliana na mtu aliyekualika.',
    redirectingIn: 'Inaelekeza upya baada ya',
    seconds: 'sekunde...',
    cancel: 'Ghairi',
    returnToPanel: 'Rudi kwenye Paneli',
    errorBoundaryTitle: 'Hitilafu imetokea',
    errorBoundaryMessage:
      'Hitilafu isiyotarajiwa imetokea. Tafadhali pakia tena ukurasa ili kuendelea.',
    errorBoundaryReloadButton: 'Pakia ukurasa upya',
    messageProcessingError:
      'Lo! Inaonekana tuna tatizo la kuchakata ujumbe wako. Tafadhali jaribu kuutuma tena.',
    microphoneProblemTextChat:
      'Inaonekana kuna tatizo na maikrofoni yako. Tafadhali andika majibu yako kwa kutumia kibodi.',
    microphoneProblemVoiceChat:
      'Inaonekana kuna tatizo na maikrofoni yako. Tafadhali angalia ruhusa za kivinjari chako na ujaribu tena.',
    responseTimeoutError: 'Jibu linachukua muda mrefu sana. Tafadhali jaribu tena.',
    noResponseError: 'Hakuna jibu lililogunduliwa. Tafadhali jaribu tena.',
    connectionInterrupted: 'Muunganisho umekatizwa. Unaweza kutuma tena ujumbe wako.',
    videoStartError: 'Imeshindwa kuanza kurekodi video. Tafadhali jaribu tena.',
    videoCameraNotSupported: 'Kamera haitumiki. Tafadhali jaribu kwa kivinjari kingine.',
    videoPauseError: 'Imeshindwa kusitisha kurekodi video.',
    videoResumeError: 'Imeshindwa kuendelea kurekodi video.',
    videoStopError: 'Imeshindwa kusimamisha kurekodi video. Tafadhali jaribu tena.',
    videoSwitchCameraError: 'Imeshindwa kubadilisha kamera. Tafadhali jaribu tena.',
    videoSendError: 'Imeshindwa kutuma ujumbe wa video.',
    welcomeBack: 'Karibu tena!',
    unsupportedFileType: 'Aina ya faili haitumiki. Tafadhali pakia faili ya JPG, PNG, GIF au MP4.',
    fileTooLarge: 'Faili hili ni kubwa mno. Ukubwa wa juu zaidi ni MB 200.',
    audioCloseError: 'Hitilafu ya kufunga sauti:',
  },
  ta: {
    telletDoesNotExistTitle: 'இந்த இணைப்பு கிடைக்கவில்லை',
    projectDoesNotExistTitle: 'இந்த இணைப்பு கிடைக்கவில்லை',
    projectDoesNotExistMessage:
      'மன்னிக்கவும், இந்த ஆய்வை கண்டுபிடிக்க முடியவில்லை. உங்களுக்கு வழங்கப்பட்ட இணைப்பை சரிபார்க்கவும், அல்லது உங்களை அழைத்தவரை தொடர்புகொள்ளவும்.',
    maximumParticipantsTitle: 'நன்றி',
    maximumParticipantsMessage:
      'இந்த ஆய்வில் பங்கேற்க நீங்கள் காட்டிய ஆர்வத்திற்கு நன்றி.\nதேவையான பதில்களின் எண்ணிக்கை தற்போது பெறப்பட்டுள்ளதால், இந்த நேரத்தில் மேலும் பங்கேற்பாளர்களை ஏற்க முடியவில்லை.\nஇந்த முக்கியமான திட்டத்தில் பங்களிக்க நீங்கள் எடுத்த நேரத்திற்கும், முனைப்பிற்கும் நன்றி.\nஉங்கள் புரிதலுக்கு நன்றி.',
    somethingMissingTitle: 'இந்த இணைப்பு கிடைக்கவில்லை',
    somethingMissingMessage:
      'மன்னிக்கவும், இந்த ஆய்வை கண்டுபிடிக்க முடியவில்லை. உங்களுக்கு வழங்கப்பட்ட இணைப்பை சரிபார்க்கவும், அல்லது உங்களை அழைத்தவரை தொடர்புகொள்ளவும்.',
    projectCompletedTitle: 'நன்றி',
    projectCompletedMessage:
      'இந்த ஆய்வில் பங்கேற்க நீங்கள் காட்டிய ஆர்வத்திற்கு நன்றி.\nஇந்த ஆய்வு தற்போது முடிவடைந்துள்ளதால், இனி பங்கேற்பாளர்களை ஏற்க முடியவில்லை.\nஉங்கள் பங்களிப்பிற்கு முனைப்பிற்கும், புரிதலுக்கும் நன்றி.',
    projectInDraftOrReadyStatusTitle: 'இன்னும் திறக்கப்படவில்லை',
    projectInDraftOrReadyStatusMessage:
      'இந்த ஆய்வில் பங்கேற்க நீங்கள் காட்டிய ஆர்வத்திற்கு நன்றி.\nஇந்த ஆய்வு இன்னும் பங்கேற்புக்காக திறக்கப்படவில்லை. தயவுசெய்து பிறகு முயற்சிக்கவும், அல்லது உங்களை அழைத்தவரை தொடர்புகொள்ளவும்.',
    redirectingIn: 'திசைதிருப்பப்படுகிறது',
    seconds: 'வினாடிகளில்...',
    cancel: 'ரத்துசெய்',
    returnToPanel: 'பேனலுக்குத் திரும்பு',
    errorBoundaryTitle: 'ஏதோ தவறு நடந்துவிட்டது',
    errorBoundaryMessage: 'எதிர்பாராத பிழை ஏற்பட்டது. தொடர பக்கத்தை மீண்டும் ஏற்றவும்.',
    errorBoundaryReloadButton: 'பக்கத்தை மீண்டும் ஏற்று',
    messageProcessingError:
      'அச்சச்சோ! உங்கள் செய்தியைச் செயலாக்குவதில் சிக்கல் உள்ளது போல் தெரிகிறது. தயவுசெய்து அதை மீண்டும் அனுப்ப முயற்சிக்கவும்.',
    microphoneProblemTextChat:
      'உங்கள் மைக்ரோஃபோனில் சிக்கல் இருப்பதாகத் தெரிகிறது. விசைப்பலகையைப் பயன்படுத்தி உங்கள் பதில்களைத் தட்டச்சு செய்யவும்.',
    microphoneProblemVoiceChat:
      'உங்கள் மைக்ரோஃபோனில் சிக்கல் இருப்பதாகத் தெரிகிறது. உங்கள் உலாவி அனுமதிகளைச் சரிபார்த்து மீண்டும் முயற்சிக்கவும்.',
    responseTimeoutError: 'பதிலளிக்க அதிக நேரம் எடுக்கிறது. மீண்டும் முயற்சிக்கவும்.',
    noResponseError: 'எந்தப் பதிலும் கண்டறியப்படவில்லை. மீண்டும் முயற்சிக்கவும்.',
    connectionInterrupted: 'இணைப்பு தடைபட்டது. உங்கள் செய்தியை மீண்டும் அனுப்பலாம்.',
    videoStartError: 'வீடியோ பதிவைத் தொடங்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    videoCameraNotSupported: 'கேமரா ஆதரிக்கப்படவில்லை. வேறு உலாவியில் முயற்சிக்கவும்.',
    videoPauseError: 'வீடியோ பதிவை இடைநிறுத்த முடியவில்லை.',
    videoResumeError: 'வீடியோ பதிவை மீண்டும் தொடங்க முடியவில்லை.',
    videoStopError: 'வீடியோ பதிவை நிறுத்த முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    videoSwitchCameraError: 'கேமராவை மாற்ற முடியவில்லை. மீண்டும் முயற்சிக்கவும்.',
    videoSendError: 'வீடியோ செய்தியை அனுப்ப முடியவில்லை.',
    welcomeBack: 'மீண்டும் வரவேற்கிறோம்!',
    unsupportedFileType:
      'ஆதரிக்கப்படாத கோப்பு வகை. JPG, PNG, GIF அல்லது MP4 கோப்பைப் பதிவேற்றவும்.',
    fileTooLarge: 'இந்தக் கோப்பு மிகப் பெரியது. அதிகபட்ச அளவு 200 MB.',
    audioCloseError: 'ஆடியோவை மூடுவதில் பிழை:',
  },
  te: {
    telletDoesNotExistTitle: 'ఈ లింక్ అందుబాటులో లేదు',
    projectDoesNotExistTitle: 'ఈ లింక్ అందుబాటులో లేదు',
    projectDoesNotExistMessage:
      'క్షమించండి, మేము ఈ అధ్యయనాన్ని కనుగొనలేకపోయాము. దయచేసి మీకు అందిన లింక్‌ను పరిశీలించండి లేదా మీకు ఆహ్వానం పంపిన వ్యక్తిని సంప్రదించండి.',
    maximumParticipantsTitle: 'ధన్యవాదాలు',
    maximumParticipantsMessage:
      'ఈ పరిశోధనలో పాల్గొనడానికి మీరు చూపిన ఆసక్తికి ధన్యవాదాలు.\nమాకు అవసరమైన సమాధానాల సంఖ్య ఇప్పటికే అందింది కనుక, ఈ సమయంలో మేము ఇకపై కొత్త పాల్గొనేవారిని స్వీకరించలేము.\nఈ ప్రాజెక్టుకు మీరు సమయం కేటాయించి, సహకరించడానికి సిద్ధంగా ఉన్నందుకు మేము కృతజ్ఞతలు తెలుపుతున్నాము.\nమీ అవగాహనకు ధన్యవాదాలు.',
    somethingMissingTitle: 'ఈ లింక్ అందుబాటులో లేదు',
    somethingMissingMessage:
      'క్షమించండి, మేము ఈ అధ్యయనాన్ని కనుగొనలేకపోయాము. దయచేసి మీకు అందిన లింక్‌ను పరిశీలించండి లేదా మీకు ఆహ్వానం పంపిన వ్యక్తిని సంప్రదించండి.',
    projectCompletedTitle: 'ధన్యవాదాలు',
    projectCompletedMessage:
      'ఈ పరిశోధనలో పాల్గొనడానికి మీరు చూపిన ఆసక్తికి ధన్యవాదాలు.\nఈ అధ్యయనం ఇప్పుడు ముగిసింది, కాబట్టి మేము ఇకపై కొత్త పాల్గొనేవారిని స్వీకరించలేము.\nమీ సహకారానికి మేము కృతజ్ఞతలు తెలుపుతున్నాము, మీ అవగాహనకు ధన్యవాదాలు.',
    projectInDraftOrReadyStatusTitle: 'ఇంకా ప్రారంభించలేదు',
    projectInDraftOrReadyStatusMessage:
      'ఈ పరిశోధనలో పాల్గొనడానికి మీరు చూపిన ఆసక్తికి ధన్యవాదాలు.\nఈ అధ్యయనం ఇంకా ప్రారంభించలేదు. దయచేసి కొంతసేపటి తర్వాత మళ్లీ ప్రయత్నించండి లేదా మీకు ఆహ్వానం పంపిన వ్యక్తిని సంప్రదించండి.',
    redirectingIn: 'దారి మళ్లించబడుతోంది',
    seconds: 'సెకన్లలో...',
    cancel: 'రద్దు చేయి',
    returnToPanel: 'ప్యానెల్‌కు తిరిగి వెళ్లండి',
    errorBoundaryTitle: 'ఏదో తప్పు జరిగింది',
    errorBoundaryMessage:
      'ఊహించని లోపం సంభవించింది. కొనసాగించడానికి దయచేసి పేజీని మళ్లీ లోడ్ చేయండి.',
    errorBoundaryReloadButton: 'పేజీని మళ్లీ లోడ్ చేయండి',
    messageProcessingError:
      'అయ్యో! మీ సందేశాన్ని ప్రాసెస్ చేయడంలో మాకు సమస్య ఉన్నట్లు కనిపిస్తోంది. దయచేసి దాన్ని మళ్లీ పంపడానికి ప్రయత్నించండి.',
    microphoneProblemTextChat:
      'మీ మైక్రోఫోన్‌లో సమస్య ఉన్నట్లు కనిపిస్తోంది. దయచేసి కీబోర్డ్ బటన్ ఉపయోగించి మీ సమాధానాలను టైప్ చేయండి.',
    microphoneProblemVoiceChat:
      'మీ మైక్రోఫోన్‌లో సమస్య ఉన్నట్లు కనిపిస్తోంది. దయచేసి మీ బ్రౌజర్ అనుమతులను తనిఖీ చేసి మళ్లీ ప్రయత్నించండి.',
    responseTimeoutError: 'ప్రతిస్పందించడానికి ఎక్కువ సమయం పడుతోంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    noResponseError: 'ఎలాంటి ప్రతిస్పందన కనుగొనబడలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.',
    connectionInterrupted: 'కనెక్షన్ అంతరాయం కలిగింది. మీరు మీ సందేశాన్ని మళ్లీ పంపవచ్చు.',
    videoStartError: 'వీడియో రికార్డింగ్ ప్రారంభించడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    videoCameraNotSupported: 'కెమెరా సపోర్ట్ చేయబడదు. దయచేసి వేరే బ్రౌజర్‌తో ప్రయత్నించండి.',
    videoPauseError: 'వీడియో రికార్డింగ్‌ను విరామం చేయడంలో విఫలమైంది.',
    videoResumeError: 'వీడియో రికార్డింగ్‌ను మళ్లీ ప్రారంభించడంలో విఫలమైంది.',
    videoStopError: 'వీడియో రికార్డింగ్‌ను ఆపడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    videoSwitchCameraError: 'కెమెరాను మార్చడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.',
    videoSendError: 'వీడియో సందేశం పంపడంలో విఫలమైంది.',
    welcomeBack: 'తిరిగి స్వాగతం!',
    unsupportedFileType:
      'సపోర్ట్ చేయని ఫైల్ రకం. దయచేసి JPG, PNG, GIF లేదా MP4 ఫైల్‌ను అప్‌లోడ్ చేయండి.',
    fileTooLarge: 'ఈ ఫైల్ చాలా పెద్దది. గరిష్ఠ పరిమాణం 200 MB.',
    audioCloseError: 'ఆడియో మూసివేయడంలో లోపం:',
  },
  th: {
    telletDoesNotExistTitle: 'ไม่พบลิงก์นี้',
    projectDoesNotExistTitle: 'ไม่พบลิงก์นี้',
    projectDoesNotExistMessage:
      'ขออภัย ไม่พบการศึกษานี้ กรุณาตรวจสอบลิงก์ที่ท่านได้รับ หรือสอบถามผู้ที่เชิญท่านเข้าร่วม',
    maximumParticipantsTitle: 'ขอขอบคุณ',
    maximumParticipantsMessage:
      'ขอขอบคุณที่ท่านให้ความสนใจเข้าร่วมการวิจัยนี้\nขณะนี้เราได้รับจำนวนคำตอบที่ต้องการครบถ้วนแล้ว จึงไม่สามารถรับผู้เข้าร่วมเพิ่มเติมได้ในขณะนี้\nเราขอขอบคุณที่ท่านสละเวลาเยี่ยมชมการศึกษาและมีความตั้งใจที่จะมีส่วนร่วมในโครงการสำคัญนี้\nขอขอบคุณในความเข้าใจของท่าน',
    somethingMissingTitle: 'ไม่พบลิงก์นี้',
    somethingMissingMessage:
      'ขออภัย ไม่พบการศึกษานี้ กรุณาตรวจสอบลิงก์ที่ท่านได้รับ หรือสอบถามผู้ที่เชิญท่านเข้าร่วม',
    projectCompletedTitle: 'ขอขอบคุณ',
    projectCompletedMessage:
      'ขอขอบคุณที่ท่านให้ความสนใจเข้าร่วมการวิจัยนี้\nขณะนี้การศึกษานี้ได้ปิดรับผู้เข้าร่วมแล้ว จึงไม่สามารถรับผู้เข้าร่วมเพิ่มเติมได้\nเราขอขอบคุณในความตั้งใจของท่าน และขอขอบคุณในความเข้าใจ',
    projectInDraftOrReadyStatusTitle: 'ยังไม่เปิดรับ',
    projectInDraftOrReadyStatusMessage:
      'ขอขอบคุณที่ท่านให้ความสนใจเข้าร่วมการวิจัยนี้\nขณะนี้การศึกษานี้ยังไม่เปิดรับผู้เข้าร่วม กรุณาลองใหม่อีกครั้งในภายหลัง หรือสอบถามผู้ที่เชิญท่านเข้าร่วม',
    redirectingIn: 'กำลังเปลี่ยนเส้นทางใน',
    seconds: 'วินาที...',
    cancel: 'ยกเลิก',
    returnToPanel: 'กลับไปยังแผงควบคุม',
    errorBoundaryTitle: 'เกิดข้อผิดพลาด',
    errorBoundaryMessage: 'เกิดข้อผิดพลาดที่ไม่คาดคิด โปรดรีโหลดหน้าเว็บเพื่อดำเนินการต่อ',
    errorBoundaryReloadButton: 'รีโหลดหน้า',
    messageProcessingError:
      'อุ๊ปส์! ดูเหมือนเรากำลังมีปัญหาในการประมวลผลข้อความของคุณ โปรดลองส่งอีกครั้ง',
    microphoneProblemTextChat:
      'ดูเหมือนจะมีปัญหากับไมโครโฟนของคุณ โปรดพิมพ์คำตอบของคุณโดยใช้แป้นพิมพ์',
    microphoneProblemVoiceChat:
      'ดูเหมือนจะมีปัญหากับไมโครโฟนของคุณ โปรดตรวจสอบการอนุญาตของเบราว์เซอร์และลองอีกครั้ง',
    responseTimeoutError: 'การตอบกลับใช้เวลานานเกินไป โปรดลองอีกครั้ง',
    noResponseError: 'ไม่พบการตอบกลับ โปรดลองอีกครั้ง',
    connectionInterrupted: 'การเชื่อมต่อขาดหาย คุณสามารถส่งข้อความของคุณอีกครั้งได้',
    videoStartError: 'ไม่สามารถเริ่มบันทึกวิดีโอได้ โปรดลองอีกครั้ง',
    videoCameraNotSupported: 'ไม่รองรับกล้อง โปรดลองใช้เบราว์เซอร์อื่น',
    videoPauseError: 'ไม่สามารถหยุดบันทึกวิดีโอชั่วคราวได้',
    videoResumeError: 'ไม่สามารถบันทึกวิดีโอต่อได้',
    videoStopError: 'ไม่สามารถหยุดบันทึกวิดีโอได้ โปรดลองอีกครั้ง',
    videoSwitchCameraError: 'ไม่สามารถสลับกล้องได้ โปรดลองอีกครั้ง',
    videoSendError: 'ไม่สามารถส่งข้อความวิดีโอได้',
    welcomeBack: 'ยินดีต้อนรับกลับ!',
    unsupportedFileType: 'ประเภทไฟล์ไม่รองรับ โปรดอัปโหลดไฟล์ JPG, PNG, GIF หรือ MP4',
    fileTooLarge: 'ไฟล์นี้มีขนาดใหญ่เกินไป ขนาดสูงสุดคือ 200 MB',
    audioCloseError: 'ข้อผิดพลาดในการปิดเสียง:',
  },
  tr: {
    telletDoesNotExistTitle: 'Bu bağlantı kullanılamıyor',
    projectDoesNotExistTitle: 'Bu bağlantı kullanılamıyor',
    projectDoesNotExistMessage:
      'Üzgünüz, bu çalışmayı bulamadık. Lütfen size verilen bağlantıyı kontrol edin ya da sizi davet eden kişiyle iletişime geçin.',
    maximumParticipantsTitle: 'Teşekkür ederiz',
    maximumParticipantsMessage:
      'Bu araştırmaya katılmak istediğiniz için teşekkür ederiz.\nGerekli yanıt sayısına ulaşıldığı için şu anda yeni katılımcı kabul edemiyoruz.\nÇalışmamızı ziyaret ettiğiniz ve bu önemli projeye katkıda bulunmak istediğiniz için teşekkür ederiz.\nAnlayışınız için teşekkür ederiz.',
    somethingMissingTitle: 'Bu bağlantı kullanılamıyor',
    somethingMissingMessage:
      'Üzgünüz, bu çalışmayı bulamadık. Lütfen size verilen bağlantıyı kontrol edin ya da sizi davet eden kişiyle iletişime geçin.',
    projectCompletedTitle: 'Teşekkür ederiz',
    projectCompletedMessage:
      'Bu araştırmaya katılmak istediğiniz için teşekkür ederiz.\nBu çalışma sona erdiği için artık yeni katılımcı kabul edemiyoruz.\nKatkıda bulunma isteğiniz için teşekkür eder, anlayışınız için minnettarız.',
    projectInDraftOrReadyStatusTitle: 'Henüz açık değil',
    projectInDraftOrReadyStatusMessage:
      'Bu araştırmaya katılmak istediğiniz için teşekkür ederiz.\nBu çalışma henüz katılıma açık değildir. Lütfen daha sonra tekrar deneyin ya da sizi davet eden kişiyle iletişime geçin.',
    redirectingIn: 'Yönlendiriliyor:',
    seconds: 'saniye...',
    cancel: 'İptal',
    returnToPanel: 'Panele Dön',
    errorBoundaryTitle: 'Bir şeyler ters gitti',
    errorBoundaryMessage:
      'Beklenmeyen bir hata oluştu. Devam etmek için lütfen sayfayı yeniden yükleyin.',
    errorBoundaryReloadButton: 'Sayfayı yeniden yükle',
    messageProcessingError:
      'Hoppala! Mesajınızı işlemede sorun yaşıyoruz gibi görünüyor. Lütfen tekrar göndermeyi deneyin.',
    microphoneProblemTextChat:
      'Mikrofonunuzla ilgili bir sorun var gibi görünüyor. Lütfen cevaplarınızı klavye ile yazın.',
    microphoneProblemVoiceChat:
      'Mikrofonunuzla ilgili bir sorun var gibi görünüyor. Lütfen tarayıcı izinlerinizi kontrol edip tekrar deneyin.',
    responseTimeoutError: 'Yanıt çok uzun sürüyor. Lütfen tekrar deneyin.',
    noResponseError: 'Yanıt algılanmadı. Lütfen tekrar deneyin.',
    connectionInterrupted: 'Bağlantı kesildi. Mesajınızı tekrar gönderebilirsiniz.',
    videoStartError: 'Video kaydı başlatılamadı. Lütfen tekrar deneyin.',
    videoCameraNotSupported: 'Kamera desteklenmiyor. Lütfen farklı bir tarayıcı ile deneyin.',
    videoPauseError: 'Video kaydı duraklatılamadı.',
    videoResumeError: 'Video kaydı devam ettirilemedi.',
    videoStopError: 'Video kaydı durdurulamadı. Lütfen tekrar deneyin.',
    videoSwitchCameraError: 'Kamera değiştirilemedi. Lütfen tekrar deneyin.',
    videoSendError: 'Video mesajı gönderilemedi.',
    welcomeBack: 'Tekrar hoş geldiniz!',
    unsupportedFileType:
      'Desteklenmeyen dosya türü. Lütfen JPG, PNG, GIF veya MP4 dosyası yükleyin.',
    fileTooLarge: 'Bu dosya çok büyük. Maksimum boyut 200 MB’tır.',
    audioCloseError: 'Ses kapatılırken hata:',
  },
  uk: {
    telletDoesNotExistTitle: 'Це посилання недоступне',
    projectDoesNotExistTitle: 'Це посилання недоступне',
    projectDoesNotExistMessage:
      'Вибачте, ми не змогли знайти це дослідження. Будь ласка, перевірте отримане посилання або зверніться до особи, яка Вас запросила.',
    maximumParticipantsTitle: 'Дякуємо',
    maximumParticipantsMessage:
      'Дякуємо за Вашу зацікавленість у цьому дослідженні.\nМи вже отримали необхідну кількість відповідей, тому, на жаль, наразі не можемо приймати нових учасників.\nМи цінуємо Ваш час і бажання долучитися до цього важливого проєкту.\nДякуємо за розуміння.',
    somethingMissingTitle: 'Це посилання недоступне',
    somethingMissingMessage:
      'Вибачте, ми не змогли знайти це дослідження. Будь ласка, перевірте отримане посилання або зверніться до особи, яка Вас запросила.',
    projectCompletedTitle: 'Дякуємо',
    projectCompletedMessage:
      'Дякуємо за Вашу зацікавленість у цьому дослідженні.\nЦе дослідження вже завершено, тому, на жаль, ми не можемо приймати нових учасників.\nМи вдячні за Вашу готовність долучитися та дякуємо за розуміння.',
    projectInDraftOrReadyStatusTitle: 'Ще не відкрито',
    projectInDraftOrReadyStatusMessage:
      'Дякуємо за Вашу зацікавленість у цьому дослідженні.\nЦе дослідження ще не відкрите для участі. Будь ласка, спробуйте пізніше або зверніться до особи, яка Вас запросила.',
    redirectingIn: 'Перенаправлення через',
    seconds: 'секунд...',
    cancel: 'Скасувати',
    returnToPanel: 'Повернутися на панель',
    errorBoundaryTitle: 'Щось пішло не так',
    errorBoundaryMessage:
      'Сталася непередбачена помилка. Будь ласка, перезавантажте сторінку, щоб продовжити.',
    errorBoundaryReloadButton: 'Перезавантажити сторінку',
    messageProcessingError:
      'Ой! Схоже, у нас проблеми з обробкою вашого повідомлення. Будь ласка, спробуйте надіслати його ще раз.',
    microphoneProblemTextChat:
      'Схоже, виникла проблема з вашим мікрофоном. Будь ласка, введіть свої відповіді за допомогою клавіатури.',
    microphoneProblemVoiceChat:
      'Схоже, виникла проблема з вашим мікрофоном. Будь ласка, перевірте дозволи браузера та спробуйте знову.',
    responseTimeoutError: 'Відповідь займає забагато часу. Будь ласка, спробуйте ще раз.',
    noResponseError: 'Відповідь не виявлено. Будь ласка, спробуйте ще раз.',
    connectionInterrupted: "З'єднання перервано. Ви можете повторно надіслати своє повідомлення.",
    videoStartError: 'Не вдалося розпочати запис відео. Будь ласка, спробуйте ще раз.',
    videoCameraNotSupported: 'Камера не підтримується. Будь ласка, спробуйте інший браузер.',
    videoPauseError: 'Не вдалося призупинити запис відео.',
    videoResumeError: 'Не вдалося відновити запис відео.',
    videoStopError: 'Не вдалося зупинити запис відео. Будь ласка, спробуйте ще раз.',
    videoSwitchCameraError: 'Не вдалося переключити камеру. Будь ласка, спробуйте ще раз.',
    videoSendError: 'Не вдалося надіслати відеоповідомлення.',
    welcomeBack: 'З поверненням!',
    unsupportedFileType:
      'Непідтримуваний тип файлу. Будь ласка, завантажте файл JPG, PNG, GIF або MP4.',
    fileTooLarge: 'Цей файл завеликий. Максимальний розмір — 200 МБ.',
    audioCloseError: 'Помилка закриття аудіо:',
  },
  ur: {
    telletDoesNotExistTitle: 'یہ لنک دستیاب نہیں ہے',
    projectDoesNotExistTitle: 'یہ لنک دستیاب نہیں ہے',
    projectDoesNotExistMessage:
      'معذرت چاہتے ہیں، ہم یہ مطالعہ تلاش نہیں کر سکے۔ براہ کرم آپ کو دیا گیا لنک چیک کریں یا جس شخص نے آپ کو مدعو کیا ہے اس سے رابطہ کریں۔',
    maximumParticipantsTitle: 'شکریہ',
    maximumParticipantsMessage:
      'اس تحقیق میں حصہ لینے میں دلچسپی ظاہر کرنے کا شکریہ۔\nہمیں اب مطلوبہ تعداد میں جوابات موصول ہو چکے ہیں، اس لیے بدقسمتی سے اس وقت مزید شرکاء کو شامل نہیں کیا جا سکتا۔\nہم آپ کے وقت اور اس اہم منصوبے میں تعاون کی خواہش کی قدر کرتے ہیں۔\nآپ کے تعاون اور سمجھ بوجھ کا شکریہ۔',
    somethingMissingTitle: 'یہ لنک دستیاب نہیں ہے',
    somethingMissingMessage:
      'معذرت چاہتے ہیں، ہم یہ مطالعہ تلاش نہیں کر سکے۔ براہ کرم آپ کو دیا گیا لنک چیک کریں یا جس شخص نے آپ کو مدعو کیا ہے اس سے رابطہ کریں۔',
    projectCompletedTitle: 'شکریہ',
    projectCompletedMessage:
      'اس تحقیق میں حصہ لینے میں دلچسپی ظاہر کرنے کا شکریہ۔\nیہ مطالعہ اب بند ہو چکا ہے، اس لیے بدقسمتی سے مزید شرکاء کو شامل نہیں کیا جا سکتا۔\nہم آپ کے تعاون کی خواہش کی قدر کرتے ہیں اور آپ کے تعاون اور سمجھ بوجھ کا شکریہ ادا کرتے ہیں۔',
    projectInDraftOrReadyStatusTitle: 'ابھی کھلا نہیں ہے',
    projectInDraftOrReadyStatusMessage:
      'اس تحقیق میں حصہ لینے میں دلچسپی ظاہر کرنے کا شکریہ۔\nیہ مطالعہ ابھی شرکاء کے لیے کھلا نہیں ہے۔ براہ کرم بعد میں دوبارہ کوشش کریں یا جس شخص نے آپ کو مدعو کیا ہے اس سے رابطہ کریں۔',
    redirectingIn: 'ری ڈائریکٹ ہو رہا ہے',
    seconds: 'سیکنڈ میں...',
    cancel: 'منسوخ کریں',
    returnToPanel: 'پینل پر واپس جائیں',
    errorBoundaryTitle: 'کچھ غلط ہو گیا',
    errorBoundaryMessage:
      'ایک غیر متوقع خرابی پیش آ گئی۔ براہ کرم جاری رکھنے کے لیے صفحہ دوبارہ لوڈ کریں۔',
    errorBoundaryReloadButton: 'صفحہ دوبارہ لوڈ کریں',
    messageProcessingError:
      'اوہ! لگتا ہے ہمیں آپ کے پیغام پر کارروائی کرنے میں دشواری ہو رہی ہے۔ براہ کرم اسے دوبارہ بھیجنے کی کوشش کریں۔',
    microphoneProblemTextChat:
      'لگتا ہے آپ کے مائیکروفون میں کوئی مسئلہ ہے۔ براہ کرم کی بورڈ کا استعمال کرتے ہوئے اپنے جوابات ٹائپ کریں۔',
    microphoneProblemVoiceChat:
      'لگتا ہے آپ کے مائیکروفون میں کوئی مسئلہ ہے۔ براہ کرم اپنے براؤزر کی اجازتیں چیک کریں اور دوبارہ کوشش کریں۔',
    responseTimeoutError: 'جواب میں بہت زیادہ وقت لگ رہا ہے۔ براہ کرم دوبارہ کوشش کریں۔',
    noResponseError: 'کوئی جواب موصول نہیں ہوا۔ براہ کرم دوبارہ کوشش کریں۔',
    connectionInterrupted: 'کنکشن منقطع ہو گیا۔ آپ اپنا پیغام دوبارہ بھیج سکتے ہیں۔',
    videoStartError: 'ویڈیو ریکارڈنگ شروع کرنے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔',
    videoCameraNotSupported:
      'کیمرہ تعاون یافتہ نہیں ہے۔ براہ کرم کسی مختلف براؤزر کے ساتھ کوشش کریں۔',
    videoPauseError: 'ویڈیو ریکارڈنگ روکنے میں ناکامی۔',
    videoResumeError: 'ویڈیو ریکارڈنگ دوبارہ شروع کرنے میں ناکامی۔',
    videoStopError: 'ویڈیو ریکارڈنگ بند کرنے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔',
    videoSwitchCameraError: 'کیمرہ تبدیل کرنے میں ناکامی۔ براہ کرم دوبارہ کوشش کریں۔',
    videoSendError: 'ویڈیو پیغام بھیجنے میں ناکامی۔',
    welcomeBack: 'واپسی پر خوش آمدید!',
    unsupportedFileType:
      'غیر تعاون یافتہ فائل کی قسم۔ براہ کرم JPG، PNG، GIF یا MP4 فائل اپ لوڈ کریں۔',
    fileTooLarge: 'یہ فائل بہت بڑی ہے۔ زیادہ سے زیادہ سائز 200 MB ہے۔',
    audioCloseError: 'آڈیو بند کرنے میں خرابی:',
  },
  uz: {
    telletDoesNotExistTitle: 'Ushbu havola mavjud emas',
    projectDoesNotExistTitle: 'Ushbu havola mavjud emas',
    projectDoesNotExistMessage:
      'Kechirasiz, bu tadqiqotni topa olmadik. Iltimos, Sizga berilgan havolani tekshiring yoki Sizni taklif qilgan shaxs bilan bog‘laning.',
    maximumParticipantsTitle: 'Rahmat',
    maximumParticipantsMessage:
      'Ushbu tadqiqotda ishtirok etish istagingiz uchun rahmat.\nHozirda zarur javoblar soniga yetdik, shuning uchun afsuski, hozir yangi ishtirokchilarni qabul qila olmaymiz.\nTadqiqotimizga tashrif buyurganingiz va ushbu muhim loyihaga hissa qo‘shishga tayyor bo‘lganingiz uchun minnatdormiz.\nTushunganingiz uchun rahmat.',
    somethingMissingTitle: 'Ushbu havola mavjud emas',
    somethingMissingMessage:
      'Kechirasiz, bu tadqiqotni topa olmadik. Iltimos, Sizga berilgan havolani tekshiring yoki Sizni taklif qilgan shaxs bilan bog‘laning.',
    projectCompletedTitle: 'Rahmat',
    projectCompletedMessage:
      'Ushbu tadqiqotda ishtirok etish istagingiz uchun rahmat.\nTadqiqot yakunlangan, shuning uchun afsuski, hozir yangi ishtirokchilarni qabul qila olmaymiz.\nHissa qo‘shishga tayyor bo‘lganingiz uchun minnatdormiz va tushunganingiz uchun rahmat.',
    projectInDraftOrReadyStatusTitle: 'Hali ochilmagan',
    projectInDraftOrReadyStatusMessage:
      'Ushbu tadqiqotda ishtirok etish istagingiz uchun rahmat.\nTadqiqot hali ishtirok uchun ochilmagan. Iltimos, keyinroq yana urinib ko‘ring yoki Sizni taklif qilgan shaxs bilan bog‘laning.',
    redirectingIn: "Qayta yo'naltirish",
    seconds: "soniyadan so'ng...",
    cancel: 'Bekor qilish',
    returnToPanel: 'Panelga qaytish',
    errorBoundaryTitle: "Nimadir noto'g'ri ketdi",
    errorBoundaryMessage: 'Kutilmagan xatolik yuz berdi. Davom etish uchun sahifani qayta yuklang.',
    errorBoundaryReloadButton: 'Sahifani qayta yuklash',
    messageProcessingError:
      "Voy! Xabaringizni qayta ishlashda muammo bor shekilli. Iltimos, uni qayta yuborib ko'ring.",
    microphoneProblemTextChat:
      'Mikrofoningizda muammo bor shekilli. Iltimos, javoblaringizni klaviatura yordamida yozing.',
    microphoneProblemVoiceChat:
      "Mikrofoningizda muammo bor shekilli. Iltimos, brauzer ruxsatlarini tekshiring va qayta urinib ko'ring.",
    responseTimeoutError: "Javob juda uzoq vaqt olmoqda. Iltimos, qayta urinib ko'ring.",
    noResponseError: "Hech qanday javob aniqlanmadi. Iltimos, qayta urinib ko'ring.",
    connectionInterrupted: 'Aloqa uzildi. Xabaringizni qayta yuborishingiz mumkin.',
    videoStartError: "Video yozishni boshlab bo'lmadi. Iltimos, qayta urinib ko'ring.",
    videoCameraNotSupported:
      "Kamera qo'llab-quvvatlanmaydi. Iltimos, boshqa brauzer bilan urinib ko'ring.",
    videoPauseError: "Video yozishni to'xtatib bo'lmadi.",
    videoResumeError: "Video yozishni davom ettirib bo'lmadi.",
    videoStopError: "Video yozishni to'xtatib bo'lmadi. Iltimos, qayta urinib ko'ring.",
    videoSwitchCameraError: "Kamerani almashtirib bo'lmadi. Iltimos, qayta urinib ko'ring.",
    videoSendError: "Video xabarni yuborib bo'lmadi.",
    welcomeBack: 'Xush kelibsiz!',
    unsupportedFileType:
      "Qo'llab-quvvatlanmaydigan fayl turi. Iltimos, JPG, PNG, GIF yoki MP4 faylini yuklang.",
    fileTooLarge: 'Bu fayl juda katta. Maksimal hajm — 200 MB.',
    audioCloseError: 'Audioni yopishda xatolik:',
  },
  vi: {
    telletDoesNotExistTitle: 'Liên kết này không khả dụng',
    projectDoesNotExistTitle: 'Liên kết này không khả dụng',
    projectDoesNotExistMessage:
      'Chúng tôi xin lỗi, nhưng không thể tìm thấy nghiên cứu này. Vui lòng kiểm tra lại liên kết bạn đã nhận hoặc liên hệ với người đã mời bạn.',
    maximumParticipantsTitle: 'Cảm ơn bạn',
    maximumParticipantsMessage:
      'Cảm ơn bạn đã quan tâm đến việc tham gia nghiên cứu này.\nChúng tôi đã nhận đủ số lượng phản hồi cần thiết, vì vậy hiện tại rất tiếc chúng tôi không thể tiếp nhận thêm người tham gia.\nChúng tôi trân trọng thời gian bạn đã dành để ghé thăm nghiên cứu này cũng như sự sẵn lòng đóng góp của bạn cho dự án quan trọng này.\nCảm ơn bạn đã thông cảm.',
    somethingMissingTitle: 'Liên kết này không khả dụng',
    somethingMissingMessage:
      'Chúng tôi xin lỗi, nhưng không thể tìm thấy nghiên cứu này. Vui lòng kiểm tra lại liên kết bạn đã nhận hoặc liên hệ với người đã mời bạn.',
    projectCompletedTitle: 'Cảm ơn bạn',
    projectCompletedMessage:
      'Cảm ơn bạn đã quan tâm đến việc tham gia nghiên cứu này.\nNghiên cứu này hiện đã kết thúc, vì vậy rất tiếc chúng tôi không thể tiếp nhận thêm người tham gia.\nChúng tôi trân trọng sự đóng góp của bạn và cảm ơn bạn đã thông cảm.',
    projectInDraftOrReadyStatusTitle: 'Chưa mở',
    projectInDraftOrReadyStatusMessage:
      'Cảm ơn bạn đã quan tâm đến việc tham gia nghiên cứu này.\nNghiên cứu này hiện chưa mở để tham gia. Xin vui lòng quay lại sau hoặc liên hệ với người đã mời bạn.',
    redirectingIn: 'Đang chuyển hướng sau',
    seconds: 'giây...',
    cancel: 'Hủy',
    returnToPanel: 'Quay lại Bảng điều khiển',
    errorBoundaryTitle: 'Đã xảy ra lỗi',
    errorBoundaryMessage: 'Đã xảy ra lỗi không mong muốn. Vui lòng tải lại trang để tiếp tục.',
    errorBoundaryReloadButton: 'Tải lại trang',
    messageProcessingError:
      'Ối! Có vẻ như chúng tôi đang gặp sự cố khi xử lý tin nhắn của bạn. Vui lòng thử gửi lại.',
    microphoneProblemTextChat:
      'Có vẻ như có vấn đề với micrô của bạn. Vui lòng nhập câu trả lời của bạn bằng bàn phím.',
    microphoneProblemVoiceChat:
      'Có vẻ như có vấn đề với micrô của bạn. Vui lòng kiểm tra quyền trình duyệt và thử lại.',
    responseTimeoutError: 'Phản hồi mất quá nhiều thời gian. Vui lòng thử lại.',
    noResponseError: 'Không phát hiện phản hồi. Vui lòng thử lại.',
    connectionInterrupted: 'Kết nối bị gián đoạn. Bạn có thể gửi lại tin nhắn của mình.',
    videoStartError: 'Không thể bắt đầu ghi video. Vui lòng thử lại.',
    videoCameraNotSupported: 'Máy ảnh không được hỗ trợ. Vui lòng thử với trình duyệt khác.',
    videoPauseError: 'Không thể tạm dừng ghi video.',
    videoResumeError: 'Không thể tiếp tục ghi video.',
    videoStopError: 'Không thể dừng ghi video. Vui lòng thử lại.',
    videoSwitchCameraError: 'Không thể chuyển máy ảnh. Vui lòng thử lại.',
    videoSendError: 'Không thể gửi tin nhắn video.',
    welcomeBack: 'Chào mừng bạn trở lại!',
    unsupportedFileType: 'Loại tệp không được hỗ trợ. Vui lòng tải lên tệp JPG, PNG, GIF hoặc MP4.',
    fileTooLarge: 'Tệp này quá lớn. Kích thước tối đa là 200 MB.',
    audioCloseError: 'Lỗi khi đóng âm thanh:',
  },
  cy: {
    telletDoesNotExistTitle: "Nid yw'r ddolen hon ar gael",
    projectDoesNotExistTitle: "Nid yw'r ddolen hon ar gael",
    projectDoesNotExistMessage:
      "Ymddiheurwn, ni allwn ddod o hyd i'r astudiaeth hon. Gwiriwch y ddolen a gawsoch, neu cysylltwch â'r sawl a'ch gwahoddodd.",
    maximumParticipantsTitle: 'Diolch i chi',
    maximumParticipantsMessage:
      "Diolch i chi am eich diddordeb mewn cymryd rhan yn yr ymchwil hon.\nRydym bellach wedi derbyn y nifer angenrheidiol o ymatebion, felly yn anffodus ni allwn dderbyn rhagor o gyfranogwyr ar hyn o bryd.\nRydym yn gwerthfawrogi eich amser i ymweld â'r astudiaeth hon a'ch parodrwydd i gyfrannu at y prosiect pwysig hwn.\nDiolch i chi am eich dealltwriaeth.",
    somethingMissingTitle: "Nid yw'r ddolen hon ar gael",
    somethingMissingMessage:
      "Ymddiheurwn, ni allwn ddod o hyd i'r astudiaeth hon. Gwiriwch y ddolen a gawsoch, neu cysylltwch â'r sawl a'ch gwahoddodd.",
    projectCompletedTitle: 'Diolch i chi',
    projectCompletedMessage:
      "Diolch i chi am eich diddordeb mewn cymryd rhan yn yr ymchwil hon.\nMae'r astudiaeth hon bellach wedi cau, felly yn anffodus ni allwn dderbyn rhagor o gyfranogwyr.\nRydym yn gwerthfawrogi eich parodrwydd i gyfrannu, a diolch i chi am eich dealltwriaeth.",
    projectInDraftOrReadyStatusTitle: 'Nid yw ar agor eto',
    projectInDraftOrReadyStatusMessage:
      "Diolch i chi am eich diddordeb mewn cymryd rhan yn yr ymchwil hon.\nNid yw'r astudiaeth hon ar agor i gyfranogwyr eto. Ceisiwch eto yn nes ymlaen, neu cysylltwch â'r sawl a'ch gwahoddodd.",
    redirectingIn: 'Ailgyfeirio ymhen',
    seconds: 'eiliad...',
    cancel: 'Canslo',
    returnToPanel: "Dychwelyd i'r Panel",
    errorBoundaryTitle: "Aeth rhywbeth o'i le",
    errorBoundaryMessage: 'Digwyddodd gwall annisgwyl. Ailymlwythwch y dudalen i barhau.',
    errorBoundaryReloadButton: "Ailymlwytho'r dudalen",
    messageProcessingError:
      "Wps! Mae'n ymddangos ein bod yn cael trafferth prosesu eich neges. Ceisiwch ei hailanfon.",
    microphoneProblemTextChat:
      "Mae'n ymddangos bod problem gyda'ch meicroffon. Teipiwch eich atebion gan ddefnyddio'r bysellfwrdd.",
    microphoneProblemVoiceChat:
      "Mae'n ymddangos bod problem gyda'ch meicroffon. Gwiriwch ganiatâd eich porwr a cheisiwch eto.",
    responseTimeoutError: "Mae'r ymateb yn cymryd gormod o amser. Ceisiwch eto.",
    noResponseError: 'Ni chanfuwyd unrhyw ymateb. Ceisiwch eto.',
    connectionInterrupted: 'Torrwyd y cysylltiad. Gallwch ailanfon eich neges.',
    videoStartError: 'Methwyd dechrau recordio fideo. Ceisiwch eto.',
    videoCameraNotSupported: "Nid yw'r camera yn cael ei gefnogi. Ceisiwch gyda phorwr gwahanol.",
    videoPauseError: 'Methwyd oedi recordiad fideo.',
    videoResumeError: 'Methwyd ailddechrau recordiad fideo.',
    videoStopError: 'Methwyd stopio recordiad fideo. Ceisiwch eto.',
    videoSwitchCameraError: 'Methwyd newid camera. Ceisiwch eto.',
    videoSendError: 'Methwyd anfon neges fideo.',
    welcomeBack: "Croeso'n ôl!",
    unsupportedFileType: 'Math o ffeil heb ei gefnogi. Llwythwch ffeil JPG, PNG, GIF neu MP4.',
    fileTooLarge: 'Mae’r ffeil hon yn rhy fawr. Y maint mwyaf yw 200 MB.',
    audioCloseError: 'Gwall wrth gau sain:',
  },
  id: {
    telletDoesNotExistTitle: 'Tautan ini tidak tersedia',
    projectDoesNotExistTitle: 'Tautan ini tidak tersedia',
    projectDoesNotExistMessage:
      'Maaf, kami tidak dapat menemukan studi ini. Silakan periksa kembali tautan yang Anda terima, atau hubungi pihak yang mengundang Anda.',
    maximumParticipantsTitle: 'Terima kasih',
    maximumParticipantsMessage:
      'Terima kasih atas minat Anda untuk berpartisipasi dalam penelitian ini.\nKami telah menerima jumlah tanggapan yang diperlukan, sehingga saat ini kami tidak dapat menerima peserta tambahan.\nKami menghargai waktu Anda untuk mengunjungi studi ini dan kesediaan Anda untuk berkontribusi dalam proyek penting ini.\nTerima kasih atas pengertian Anda.',
    somethingMissingTitle: 'Tautan ini tidak tersedia',
    somethingMissingMessage:
      'Maaf, kami tidak dapat menemukan studi ini. Silakan periksa kembali tautan yang Anda terima, atau hubungi pihak yang mengundang Anda.',
    projectCompletedTitle: 'Terima kasih',
    projectCompletedMessage:
      'Terima kasih atas minat Anda untuk berpartisipasi dalam penelitian ini.\nStudi ini telah ditutup, sehingga kami tidak dapat menerima peserta tambahan.\nKami menghargai kesediaan Anda untuk berkontribusi, dan terima kasih atas pengertian Anda.',
    projectInDraftOrReadyStatusTitle: 'Belum dibuka',
    projectInDraftOrReadyStatusMessage:
      'Terima kasih atas minat Anda untuk berpartisipasi dalam penelitian ini.\nStudi ini belum dibuka untuk partisipasi. Silakan coba lagi nanti, atau hubungi pihak yang mengundang Anda.',
    redirectingIn: 'Mengalihkan dalam',
    seconds: 'detik...',
    cancel: 'Batal',
    returnToPanel: 'Kembali ke Panel',
    errorBoundaryTitle: 'Terjadi kesalahan',
    errorBoundaryMessage:
      'Terjadi kesalahan tak terduga. Silakan muat ulang halaman untuk melanjutkan.',
    errorBoundaryReloadButton: 'Muat ulang halaman',
    messageProcessingError:
      'Ups! Sepertinya kami mengalami masalah dalam memproses pesan Anda. Silakan coba kirim ulang.',
    microphoneProblemTextChat:
      'Sepertinya ada masalah dengan mikrofon Anda. Silakan ketik jawaban Anda menggunakan keyboard.',
    microphoneProblemVoiceChat:
      'Sepertinya ada masalah dengan mikrofon Anda. Silakan periksa izin browser Anda dan coba lagi.',
    responseTimeoutError: 'Respons terlalu lama. Silakan coba lagi.',
    noResponseError: 'Tidak ada respons terdeteksi. Silakan coba lagi.',
    connectionInterrupted: 'Koneksi terputus. Anda dapat mengirim ulang pesan Anda.',
    videoStartError: 'Gagal memulai perekaman video. Silakan coba lagi.',
    videoCameraNotSupported: 'Kamera tidak didukung. Silakan coba dengan browser lain.',
    videoPauseError: 'Gagal menjeda perekaman video.',
    videoResumeError: 'Gagal melanjutkan perekaman video.',
    videoStopError: 'Gagal menghentikan perekaman video. Silakan coba lagi.',
    videoSwitchCameraError: 'Gagal mengganti kamera. Silakan coba lagi.',
    videoSendError: 'Gagal mengirim pesan video.',
    welcomeBack: 'Selamat datang kembali!',
    unsupportedFileType: 'Jenis file tidak didukung. Silakan unggah file JPG, PNG, GIF, atau MP4.',
    fileTooLarge: 'File ini terlalu besar. Ukuran maksimum adalah 200 MB.',
    audioCloseError: 'Kesalahan saat menutup audio:',
  },
} satisfies Record<TranslationLanguage, RespondentUiTranslation>;

/**
 * Look up a localised `RespondentUiTranslation` for the given
 * languageCode. Falls back to English if the code is missing or a
 * non-canonical value (e.g. arriving from legacy data or a URL param).
 */
export function getRespondentUiTranslation(
  languageCode: string | undefined | null,
): RespondentUiTranslation {
  if (!languageCode) return RESPONDENT_UI_TRANSLATION.en;
  return (
    RESPONDENT_UI_TRANSLATION[languageCode as TranslationLanguage] ?? RESPONDENT_UI_TRANSLATION.en
  );
}
